// The requirements register — THE CONTRACT-BEARING HEART ('#register').
//
// This page answers one question before any other: is the scope on record something the
// engagement can be held to, and if not, what exactly is missing. The five zones — masthead,
// ledger, obligation, contested, rows — are the study's order, and the ledger is made entirely
// of data the page already fetched and used to throw away.
//
// ATTEST voices on this page (docs/ATTEST.md): the STATEMENT column and every requirement,
// acceptance-criterion and definition-of-done sentence are THE RECORD — serif (.voice-record)
// with a claim mark (◇ proposed / ▪ held). Verbatim evidence spans take full evidence dress
// (.ev closed by .ev-src, which names the document). Refs, keys and locators are mono
// (.src-ref); counts, tallies and dates are quantities and take tabular sans (.voice-figure)
// — a count is arithmetic, so it never carries a claim mark. Weight is WEIGHT, never a hue.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tx, TxTools } from '../components/Tx.js';
import {
  AlertTriangle,
  Check,
  CheckCheck,
  Download,
  FileText,
  FlaskConical,
  GitMerge,
  Lock,
  Package,
  Plus,
  Scissors,
  Tags,
  Trash2,
  X,
} from 'lucide-react';
import { RowMenu } from '../components/PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import './RegisterPage.de.js';
import './RegisterPage.fr.js';
import RoadmapView from './register/RoadmapView.js';
import PlannerChat from './register/PlannerChat.js';
import { useEngagement } from '../context/EngagementContext.js';
import { useToast } from '../components/Toast.js';
import ConfirmModal from '../components/ConfirmModal.js';
import FieldChat from '../components/FieldChat.js';
import RefSuggest, { type RefOption } from '../components/RefSuggest.js';
import EmptyState from '../components/EmptyState.js';
import { usePoll } from '../hooks/usePoll.js';
import { ROUTES, useDeepLink, useOpenRoute, useRouteParams } from '../routes.js';
import type { RailCell } from '../../shared/rail.js';
import { REQUIREMENT_STATUS_LABEL } from '../../shared/requirementStatus.js';
import { request } from '../api/client.js';
import { agentApi } from '../api/agent.js';
import { contractingApi, type Milestone } from '../api/contracting.js';
import { ALL_RUNGS, RUNG_NOUN, bindingRung, buildLadder, tally, type Rung, type RungKey } from './register/ladder.js';
import { anchorRow, hinge, parentRef, positions, refKind, refsIn } from './register/refLink.js';
import {
  acsApi,
  cascadeSentence,
  deliverablesApi,
  exportsApi,
  registerApi,
  testCasesApi,
  EvidenceInvalidError,
  type AcceptanceCriterion,
  type BulkDeleteResult,
  type CategorySchemeResponse,
  type OpenQuestion,
  type CoverageReport,
  type DeletableEntity,
  type DeletionPlan,
  type DeletionResult,
  type Deliverable,
  type EvidenceFailure,
  type EvidenceInput,
  type EvidenceRow,
  type LintResult,
  type Priority,
  type ProseReference,
  type RegisterRow,
  type RequirementDetail,
  type RequirementStatus,
  type RequirementType,
  type SpanContext,
  type TestCase,
  type WorkingOverride,
} from '../api/register.js';

const POLL_MS = 15000;

/** The translator's shape, for the plain helpers below that take it as an argument. */
type T = ReturnType<typeof useT>;

// ───────────────────────── vocabularies + presentation ─────────────────────────

const TYPES: { value: RequirementType; label: string }[] = [
  { value: 'functional', label: 'Functional' },
  { value: 'non_functional', label: 'Non-functional' },
  { value: 'constraint', label: 'Constraint' },
  { value: 'assumption', label: 'Assumption' },
  { value: 'out_of_scope', label: 'Exclusion' },
];

const PRIORITIES: { value: Priority; label: string }[] = [
  { value: 'must', label: 'Must' },
  { value: 'should', label: 'Should' },
  { value: 'could', label: 'Could' },
  { value: 'wont', label: "Won't" },
];

/**
 * Lifecycle position on the stage spectrum (COMPONENTS.md — the approach vector). The label
 * text itself comes from the ONE shared requirement-status map (src/shared/requirementStatus.ts)
 * — this local table adds only what is specific to this badge: whether the status sits on the
 * spectrum at all.
 */
const STATUS_STAGE: Record<string, number | null> = {
  draft: 1,
  confirmed: 3,
  baselined: 4,
  changed: 6,
  delivered: 5,
  accepted: 7,
  descoped: null,
};

/** Test-case lifecycle. 'ready' is the only status the client portal lists for UAT. */
const TC_STATUSES: { value: string; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'ready', label: 'Ready' },
  { value: 'dropped', label: 'Dropped' },
];

const DELIVERABLE_STATUSES: { value: string; label: string }[] = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'dropped', label: 'Dropped' },
];

/** Ready is a release, not a colour: the lamp says "the client can see this now". */
function TcStatusBadge({ status }: { status: string }) {
  const t = useT();
  if (status === 'ready') return <span className="badge badge-ok">{t('Ready')}</span>;
  if (status === 'dropped') return <span className="badge text-ink-3">{t('Dropped')}</span>;
  return <span className="badge badge-warn">{t('Draft')}</span>;
}

const typeLabel = (t: string): string => TYPES.find((x) => x.value === t)?.label ?? t;

function StatusBadge({ status, mergedInto }: { status: string; mergedInto?: string | null }) {
  const t = useT();
  // A merged-away requirement is NOT an exclusion — "Out of scope" here misled the operator
  // into thinking a live feature had been dropped. The pointer tells the true story.
  if (mergedInto) {
    return (
      <span className="badge text-ink-3" title={t('Merged — its content lives on in {ref}, where the evidence was carried over.', { ref: mergedInto })}>
        {t('merged → {ref}', { ref: mergedInto })}
      </span>
    );
  }
  const label = REQUIREMENT_STATUS_LABEL[status]?.label ?? status;
  const stage = STATUS_STAGE[status] ?? null;
  if (stage === null) return <span className="badge text-ink-3">{t(label)}</span>;
  // LAW 2 — a lifecycle status is a CATEGORY and takes no hue. It used to paint one of
  // seven stage colours; what actually matters (proposed vs held) is now said by the
  // claim mark beside the statement, and "locked" by the padlock.
  return (
    <span className="badge">
      {status === 'baselined' ? <Lock className="icon-sm" style={{ width: 11, height: 11 }} /> : null}
      {t(label)}
    </span>
  );
}

/**
 * Weight is WEIGHT (ATTEST LAW 2). The old `meta-tag--cat-*` classes this used are the category
 * palette, which attest.css has already neutralised to one identical grey — so the colour said
 * nothing and the reader had to read the word anyway. Ink and case say it instead.
 */
function PriorityTag({ priority }: { priority: string }) {
  const t = useT();
  return <span className={`reg-w reg-w--${priority}`}>{t(priority === 'wont' ? "won't" : priority)}</span>;
}

/**
 * Which flow owns each machinery status — shown as the tooltip on the read-only status chip.
 * These are never offered as select targets: the register edits classification, not lifecycle.
 */
const STATUS_FLOW_TITLE: Record<string, string> = {
  baselined: 'Set by the baseline machinery when the SOW is signed — altered only through a change request.',
  changed: 'Set when a change request is approved.',
  descoped: 'Set by descope (pre-baseline) or an approved change request.',
  delivered: 'Set by the delivery flow on the Acceptance page.',
  accepted: 'Set by the acceptance flow when the client signs off.',
};

/** The one explanation every disabled control on a locked row carries (mirrors BaselinedEditError). */
const lockTitle = (t: T, ref: string, status: string): string =>
  t('{ref} is {status} and belongs to a signed baseline — raise a change request to alter it.', {
    ref,
    status: t(REQUIREMENT_STATUS_LABEL[status]?.word ?? status),
  });

/**
 * A classification chip as a quiet in-place select — every axis (type, priority, status,
 * category) is editable until the baseline locks it. This generalises the priority select of
 * the previous build; locked rows now render it DISABLED with the change-request explanation
 * as the tooltip (operator decision), and a forced API call still gets the server's refusal.
 */
function QuietSelect({
  value,
  options,
  disabled,
  label,
  title,
  minWidth,
  onChange,
}: {
  value: string;
  options: { value: string; label: string; title?: string }[];
  disabled?: boolean;
  /** Accessible name, e.g. "Priority of REQ-012". */
  label: string;
  /** Tooltip — on locked rows, the change-request explanation. */
  title: string;
  minWidth?: number;
  onChange: (next: string) => void;
}) {
  return (
    <select
      className="form-select"
      style={{ width: 'auto', height: 24, fontSize: 12, padding: '0 22px 0 8px', ...(minWidth ? { minWidth } : {}) }}
      value={value}
      disabled={disabled}
      aria-label={label}
      title={title}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value)}
    >
      {/* A value outside the offered list (an ad-hoc category) must still render as itself. */}
      {options.some((o) => o.value === value) ? null : <option value={value}>{value || '—'}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value} title={o.title}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * LAW 6 — the claim mark. A draft requirement is what the machine (or a hurried
 * operator) *proposed*; it is open (◇). Anything a human has stood behind —
 * confirmed, baselined, changed by an approved CR — is *held*, and closes (▪).
 * A dropped or merged-away row carries NO mark: nobody is claiming it any more.
 * Computed values (counts, coverage tallies) never get one at all.
 */
function ClaimMark({ status, mergedInto, subject }: { status: string; mergedInto?: string | null; subject?: string }) {
  const t = useT();
  if (mergedInto || status === 'dropped' || status === 'out_of_scope') return null;
  const held = status !== 'draft';
  const who = subject ?? t('This statement');
  return (
    <span
      className={`att ${held ? 'att--held' : 'att--proposed'}`}
      aria-hidden="true"
      title={
        held
          ? t('{subject} is held — an operator stands behind it as scope.', { subject: who })
          : t('{subject} is proposed — nobody has confirmed it yet.', { subject: who })
      }
    />
  );
}

const clamp2: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  // Without this the second line stops mid-word and looks like a rendering fault rather
  // than a deliberate preview. The full text is one click away in the detail panel.
  textOverflow: 'ellipsis',
  overflowWrap: 'anywhere',
};

// ───────────────────────── shared small components ─────────────────────────

function LintFlags({ lint }: { lint: LintResult | null | undefined }) {
  const t = useT();
  if (!lint || !lint.flags?.length) {
    return (
      <span className="badge badge-ok">
        <Check className="icon-sm" style={{ width: 11, height: 11 }} /> {t('Lint clean')}
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-4 mt-4">
      {lint.flags.map((flag, i) => (
        <div
          key={`${flag.code}-${i}`}
          className="flex items-start gap-8 text-sm"
          style={{ color: flag.severity === 'error' ? 'var(--warn)' : 'var(--ink-3)' }}
        >
          <AlertTriangle className="icon-sm shrink-0" style={{ marginTop: 1 }} />
          <span>{flag.message}</span>
        </div>
      ))}
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
  className,
}: {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
  className?: string;
}) {
  const t = useT();
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${wide ? 'modal-wide' : ''}${className ? ` ${className}` : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="close-btn" onClick={onClose} aria-label={t('Close')}>
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

/**
 * A requirement ref that shows its FACE on hover (and on keyboard focus): the statement and,
 * when the register carries one, the rationale — in a floating card. Position fixed, measured
 * off the badge at open, flipping above when the viewport below runs out: the deliverables
 * table lives in a `.sheet--clip` band whose overflow would cut an absolutely-positioned card.
 */
function ReqBadge({ reqRef, meta, statement, rationale }: {
  reqRef: string;
  meta: string;
  statement: string | null;
  rationale: string | null;
}) {
  const [pos, setPos] = useState<{ x: number; top: number | null; bottom: number | null } | null>(null);
  const show = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 260;
    setPos({
      x: Math.max(8, Math.min(r.left, window.innerWidth - 372)),
      top: up ? null : r.bottom + 6,
      bottom: up ? window.innerHeight - r.top + 6 : null,
    });
  };
  return (
    <span
      className="badge mono req-pop"
      tabIndex={0}
      onMouseEnter={(e) => show(e.currentTarget)}
      onMouseLeave={() => setPos(null)}
      onFocus={(e) => show(e.currentTarget)}
      onBlur={() => setPos(null)}
    >
      {reqRef}
      {pos ? (
        <span
          className="req-popcard"
          role="tooltip"
          style={{ left: pos.x, top: pos.top ?? undefined, bottom: pos.bottom ?? undefined }}
        >
          <span className="rp-head">
            <span className="src-ref">{reqRef}</span>
            <span className="rp-meta">{meta}</span>
          </span>
          <span className="rp-statement">{statement ?? '—'}</span>
          {rationale ? <span className="rp-why">{rationale}</span> : null}
        </span>
      ) : null}
    </span>
  );
}

// ───────────────────────── hard delete ─────────────────────────
//
// Retire and delete coexist on purpose. Descope / Dropped keep a row that was real and whose
// history matters. Delete is for the MISTAKE — the typo, the duplicate, the row created twice —
// and it is immediate. The server owns the decision (src/server/utils/deletion.ts); everything
// here asks it for the plan, says in plain words what goes with the row, and when the answer is
// no, prints the reason, which always names the alternative.

const ENTITY_NOUN: Record<DeletableEntity, string> = {
  requirement: 'requirement',
  deliverable: 'deliverable',
  acceptance_criterion: 'acceptance criterion',
  test_case: 'test case',
};

/** The retire that coexists with delete — shown alongside a refusal, never instead of it. */
const RETIRE_HINT: Record<DeletableEntity, string> = {
  requirement: 'Mark out of scope keeps the requirement with every revision and every piece of evidence.',
  deliverable: 'Setting the status to Dropped keeps the deliverable and its mappings.',
  acceptance_criterion: 'Setting the criterion to dropped keeps it and its test-case links.',
  test_case: 'Setting the test case to Dropped keeps it and any runs the client recorded.',
};

const PLAN_FOR: Record<DeletableEntity, (id: number) => Promise<DeletionPlan>> = {
  requirement: (id) => registerApi.deletionPlan(id),
  deliverable: (id) => deliverablesApi.deletionPlan(id),
  acceptance_criterion: (id) => acsApi.deletionPlan(id),
  test_case: (id) => testCasesApi.deletionPlan(id),
};

const DELETE_FOR: Record<DeletableEntity, (id: number) => Promise<DeletionResult>> = {
  requirement: (id) => registerApi.remove(id),
  deliverable: (id) => deliverablesApi.remove(id),
  acceptance_criterion: (id) => acsApi.remove(id),
  test_case: (id) => testCasesApi.remove(id),
};

interface DeleteTarget {
  entity: DeletableEntity;
  id: number;
  ref: string;
}

/** The policy cuts its label's statement at 60 characters; say so rather than stopping mid-word. */
const planLabel = (label: string): string => {
  const statement = label.replace(/^\S+\s+—\s+/, '');
  return statement.length >= 60 ? `${label}…` : label;
};

/** The trash affordance every row wears. Small, quiet, and never the primary action. */
function TrashButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      className="btn btn-icon"
      title={label}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Trash2 className="icon-sm" />
    </button>
  );
}

/**
 * Ask for the plan, then either confirm the cascade in words or explain the refusal.
 * A blocked plan is never a disabled button with no explanation: the blocker message is the
 * whole point, because it is what tells the operator what to do instead.
 */
function DeleteModal({
  target,
  onClose,
  onDeleted,
}: {
  target: DeleteTarget;
  onClose: () => void;
  onDeleted: (result: DeletionResult) => void;
}) {
  const { t, tp } = useLocale();
  const [plan, setPlan] = useState<DeletionPlan | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setPlan(null);
    setRefusal(null);
    setError(null);
    PLAN_FOR[target.entity](target.id)
      .then((p) => alive && setPlan(p))
      .catch((e: Error) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [target.entity, target.id]);

  const noun = t(ENTITY_NOUN[target.entity]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      onDeleted(await DELETE_FOR[target.entity](target.id));
    } catch (e) {
      // The server recomputes the plan on the way in, so it can still refuse here — a baseline
      // may have been signed since the plan was fetched. Show the reason where the operator is
      // looking rather than in a toast that scrolls away.
      setRefusal((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const closeBtn = (
    <button className="btn btn-ghost" onClick={onClose}>
      {t('Close')}
    </button>
  );

  if (error) {
    return (
      <Modal title={t('Delete {ref}', { ref: target.ref })} onClose={onClose} footer={closeBtn}>
        <div className="flex items-start gap-8 text-sm" style={{ color: 'var(--err)' }}>
          <AlertTriangle className="icon-sm shrink-0" style={{ marginTop: 2 }} />
          <span>{error}</span>
        </div>
      </Modal>
    );
  }

  const blockers = refusal ? [{ code: 'refused', message: refusal }] : (plan?.blockers ?? []);

  if (blockers.length) {
    return (
      <Modal title={t('{ref} cannot be deleted', { ref: target.ref })} onClose={onClose} footer={closeBtn}>
        {blockers.map((b, i) => (
          <div key={`${b.code}-${i}`} className="flex items-start gap-8 mb-12">
            <Lock className="icon-sm shrink-0" style={{ marginTop: 3, color: 'var(--warn)' }} />
            <p className="text-sm text-ink-1" style={{ lineHeight: 1.6 }}>
              {b.message}
            </p>
          </div>
        ))}
        <p className="text-sm text-ink-3">{t(RETIRE_HINT[target.entity])}</p>
      </Modal>
    );
  }

  if (!plan) {
    return (
      <Modal title={t('Delete {ref}', { ref: target.ref })} onClose={onClose} footer={closeBtn}>
        <div className="flex justify-center p-24">
          <span className="spinner spinner-lg" />
        </div>
      </Modal>
    );
  }

  const cascade = cascadeSentence(plan.cascade);
  return (
    <ConfirmModal
      open
      title={t('Delete {ref}?', { ref: target.ref })}
      confirmLabel={busy ? t('Deleting…') : t('Delete {noun}', { noun })}
      danger
      onCancel={onClose}
      onConfirm={() => void confirm()}
    >
      <p className="text-sm text-ink-1 mb-8">{planLabel(plan.label)}</p>
      <p className="text-sm text-ink-2" style={{ lineHeight: 1.6 }}>
        {t('The {noun} is removed from the register for good.', { noun })} {cascade ? t(cascade) : t('Nothing else points at it.')}
      </p>
      {/* Not part of the cascade sentence on purpose: these texts do NOT go with the row —
          they stay behind, still naming a ref that no longer exists. */}
      {plan.prose_references ? (
        <p className="text-sm mt-8" style={{ lineHeight: 1.6, color: 'var(--attend)' }}>
          {tp(
            plan.prose_references,
            '{n} text elsewhere names {ref} — it stays, and the name will dangle. Update it afterwards.',
            '{n} texts elsewhere name {ref} — they stay, and the name will dangle. Update them afterwards.',
            { ref: target.ref ?? '' },
          )}
        </p>
      ) : null}
      <p className="text-sm text-ink-3 mt-12" style={{ lineHeight: 1.6 }}>
        {t('Delete is for a mistake — a duplicate, a typo, a row that should never have existed.')}{' '}
        {t(RETIRE_HINT[target.entity])}
      </p>
    </ConfirmModal>
  );
}

/**
 * Bulk delete. The plan is recomputed per row on the server; the dry run builds this dialog and
 * the live call reports the same shape back. Refusals are listed with their reason — a row is
 * never dropped from the batch without the operator being told which one and why.
 */
function BulkDeleteModal({
  engagementId,
  ids,
  onClose,
  onDone,
}: {
  engagementId: number;
  ids: number[];
  onClose: () => void;
  onDone: (result: BulkDeleteResult) => void;
}) {
  const t = useT();
  const { tp } = useLocale();
  const [preview, setPreview] = useState<BulkDeleteResult | null>(null);
  const [outcome, setOutcome] = useState<BulkDeleteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    registerApi
      .bulkDelete(engagementId, ids, true)
      .then((r) => alive && setPreview(r))
      .catch((e: Error) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [engagementId, ids]);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await registerApi.bulkDelete(engagementId, ids);
      setOutcome(res);
      onDone(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const shown = outcome ?? preview;
  const refusedList = (rows: BulkDeleteResult['refused']) => (
    <>
      <div className="section-label mt-16 mb-8">
        {t('Refused')} <span className="voice-figure text-ink-3">{rows.length}</span>
      </div>
      {rows.map((r) => (
        <div key={r.id} className="border rounded-1 p-12 mb-8">
          <div className="flex items-center gap-8 mb-4">
            <Lock className="icon-sm" style={{ color: 'var(--warn)' }} />
            <span className="mono text-sm">{r.ref ?? `#${r.id}`}</span>
          </div>
          <p className="text-sm text-ink-2" style={{ lineHeight: 1.6 }}>
            {r.reason}
          </p>
        </div>
      ))}
    </>
  );

  return (
    <Modal
      title={outcome ? t('Delete finished') : tp(ids.length, 'Delete {n} requirement?', 'Delete {n} requirements?')}
      onClose={onClose}
      wide
      footer={
        outcome ? (
          <button className="btn btn-accent" onClick={onClose}>
            {t('Close')}
          </button>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={onClose}>
              {t('Cancel')}
            </button>
            <button
              className="btn btn-danger"
              disabled={busy || !preview || !preview.deleted.length}
              onClick={() => void run()}
            >
              {busy ? <span className="spinner spinner-sm" /> : <Trash2 className="icon-sm" />}{' '}
              {t('Delete {n} permanently', { n: preview ? preview.deleted.length : ids.length })}
            </button>
          </>
        )
      }
    >
      {error ? (
        <div className="flex items-start gap-8 text-sm mb-12" style={{ color: 'var(--err)' }}>
          <AlertTriangle className="icon-sm shrink-0" style={{ marginTop: 2 }} />
          <span>{error}</span>
        </div>
      ) : null}
      {!shown && !error ? (
        <div className="flex justify-center p-24">
          <span className="spinner spinner-lg" />
        </div>
      ) : null}
      {shown ? (
        <>
          <div className="section-label mb-8">
            {outcome ? t('Deleted') : t('Will be deleted')} <span className="voice-figure text-ink-3">{shown.deleted.length}</span>
          </div>
          {shown.deleted.map((d) => {
            const cascade = cascadeSentence(d.cascade);
            // The dangle warning the single-delete dialog carries — the bulk rows pay for the
            // same scan, so its answer is shown here too, not thrown away.
            const dangling = (d as { prose_references?: number }).prose_references ?? 0;
            return (
              <div key={d.id} className="flex items-start gap-8 mb-8">
                <span className="mono text-sm" style={{ minWidth: 66 }}>
                  {d.ref ?? `#${d.id}`}
                </span>
                <span className="text-sm text-ink-2 flex-1" style={{ lineHeight: 1.5 }}>
                  {planLabel(d.label).replace(/^\S+\s+—\s+/, '')}
                  {cascade ? <span className="text-ink-3"> — {cascade}</span> : null}
                  {dangling ? (
                    <span style={{ color: 'var(--attend)' }}>
                      {' — '}
                      {tp(dangling, '{n} text keeps naming it — update after', '{n} texts keep naming it — update after')}
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
          {!shown.deleted.length ? (
            <p className="text-sm text-ink-3">{t('Nothing in this selection can be deleted.')}</p>
          ) : null}
          {shown.refused.length ? refusedList(shown.refused) : null}
          {shown.missing.length ? (
            <p className="text-sm text-ink-3 mt-12">
              <span className="voice-figure">{shown.missing.length}</span>{' '}
              {tp(shown.missing.length, 'selected row is no longer in this engagement and was left alone.', 'selected rows are no longer in this engagement and were left alone.')}
            </p>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

// ───────────────────────── evidence-in-context modal ─────────────────────────

function SpanModal({ evidence, onClose }: { evidence: EvidenceRow; onClose: () => void }) {
  const t = useT();
  const [context, setContext] = useState<SpanContext | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    registerApi
      .evidenceContext(evidence)
      .then((c) => alive && setContext(c))
      .catch((e: Error) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [evidence]);

  return (
    <Modal title={t('{ref} — span in context', { ref: evidence.document_ref })} onClose={onClose} wide>
      {!context && !error ? <div className="spinner" /> : null}
      {error ? <div className="text-warn text-sm">{error}</div> : null}
      {context ? (
        <>
          <div className="flex gap-8 items-center mb-12">
            <span className="badge badge-accent src-ref">{context.document_ref}</span>
            <span className="text-sm text-ink-3">{context.original_filename}</span>
            {/* a character offset is a locator into the document, not a quantity — mono */}
            <span className="src-ref text-ink-3 ml-auto">
              {t('chars {start}–{end}', { start: context.char_start, end: context.char_end })}
            </span>
          </div>
          {/* the document speaking, at length: the record's voice, not the console's */}
          <div
            className="bg-inset border rounded-1 p-16 voice-record"
            style={{ maxHeight: '46vh', overflowY: 'auto', whiteSpace: 'pre-wrap', maxWidth: 'none' }}
          >
            {context.truncated_before ? <span className="text-ink-4">…</span> : null}
            <span className="text-ink-3">{context.before}</span>
            <mark
              style={{
                background: 'var(--accent-dim)',
                color: 'var(--accent)',
                padding: '1px 2px',
                borderRadius: 'var(--r1)',
                boxShadow: 'inset 0 -1px 0 var(--accent-mid)',
              }}
            >
              {context.span}
            </mark>
            <span className="text-ink-3">{context.after}</span>
            {context.truncated_after ? <span className="text-ink-4">…</span> : null}
          </div>
        </>
      ) : null}
    </Modal>
  );
}

// ───────────────────────── evidence editor (create / revise) ─────────────────────────

interface DocOption {
  id: number;
  ref: string;
  original_filename: string;
  status: string;
}

function EvidenceEditor({
  documents,
  documentsError,
  value,
  failures,
  onRowEdited,
  onChange,
}: {
  documents: DocOption[];
  documentsError: string | null;
  value: EvidenceInput[];
  /** Per-row server verdicts (P2). Keyed by the index the server rejected. */
  failures: EvidenceFailure[];
  /** A row the operator has started fixing stops wearing its old verdict. */
  onRowEdited: (index: number) => void;
  onChange: (next: EvidenceInput[]) => void;
}) {
  const t = useT();
  const add = () =>
    onChange([...value, { document_id: documents[0]?.id ?? 0, quoted_span: '', locator: {} }]);
  const reasonFor = (i: number) => failures.find((f) => f.index === i)?.reason ?? null;
  const update = (i: number, patch: Partial<EvidenceInput>) => {
    if (reasonFor(i)) onRowEdited(i);
    onChange(value.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  };

  return (
    <div className="form-group">
      <label className="form-label">{t('Evidence — verbatim spans (P2)')}</label>
      {documentsError ? (
        <div className="text-sm text-ink-3 mb-8">
          {t('Document list unavailable ({error}). The requirement can be created without evidence and cited later.', { error: documentsError })}
        </div>
      ) : null}
      {value.map((row, i) => {
        const reason = reasonFor(i);
        return (
          <div
            key={i}
            className="border rounded-1 p-12 mb-8 bg-inset"
            style={reason ? { borderColor: 'var(--err)' } : undefined}
          >
            <div className="flex gap-8 items-center mb-8">
              <select
                className="form-select"
                style={{ width: 'auto', flex: 1 }}
                value={row.document_id || ''}
                onChange={(e) => update(i, { document_id: Number(e.target.value) })}
              >
                <option value="">{t('Select document…')}</option>
                {documents.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.ref} — {d.original_filename}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-icon"
                title={t('Remove evidence row')}
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="icon-sm" />
              </button>
            </div>
            <textarea
              className="form-textarea"
              rows={3}
              placeholder={t('Paste the exact sentence from the document — it is validated as a literal substring.')}
              value={row.quoted_span}
              onChange={(e) => update(i, { quoted_span: e.target.value })}
              style={reason ? { borderColor: 'var(--err)' } : undefined}
            />
            {reason ? (
              <div className="flex items-start gap-8 mt-8 text-sm" style={{ color: 'var(--err)' }}>
                <AlertTriangle className="icon-sm shrink-0" style={{ marginTop: 1 }} />
                <span>{reason}</span>
              </div>
            ) : null}
          </div>
        );
      })}
      <button className="btn btn-sm" onClick={add} disabled={!documents.length}>
        <Plus className="icon-sm" /> {t('Add evidence')}
      </button>
    </div>
  );
}

// ───────────────────────── requirement form modal (create / revise) ─────────────────────────

interface RequirementFormValues {
  statement: string;
  rationale: string;
  type: RequirementType;
  priority: Priority;
  category: string;
  evidence: EvidenceInput[];
}

function RequirementFormModal({
  title,
  submitLabel,
  initial,
  documents,
  documentsError,
  busy,
  evidenceFailures,
  categoryOptions,
  onEvidenceEdited,
  onSubmit,
  onClose,
  engagementId,
  refOptions,
}: {
  title: string;
  submitLabel: string;
  initial: RequirementFormValues;
  /** The field proposer's scope — suggestions draft in this engagement's language and terms. */
  engagementId: number;
  /** What # can reference from this form — the register's deliverables and requirements. */
  refOptions?: RefOption[];
  documents: DocOption[];
  documentsError: string | null;
  busy: boolean;
  evidenceFailures: EvidenceFailure[];
  /** Scheme names first — offered as suggestions; free text stays possible. */
  categoryOptions: string[];
  onEvidenceEdited: (index: number) => void;
  onSubmit: (values: RequirementFormValues) => void;
  onClose: () => void;
}) {
  const t = useT();
  const { tp } = useLocale();
  const [values, setValues] = useState<RequirementFormValues>(initial);
  const set = (patch: Partial<RequirementFormValues>) => setValues((v) => ({ ...v, ...patch }));
  const statementRef = useRef<HTMLTextAreaElement | null>(null);
  const rationaleRef = useRef<HTMLTextAreaElement | null>(null);

  return (
    <Modal
      title={title}
      onClose={onClose}
      wide
      className="modal-compose"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            {t('Cancel')}
          </button>
          <button className="btn btn-accent" disabled={busy || values.statement.trim().length < 3} onClick={() => onSubmit(values)}>
            {busy ? <span className="spinner spinner-sm" /> : null} {submitLabel}
          </button>
        </>
      }
    >
      {/* Draft with the agent on the left, the form on the right — the workbench's own shape.
          The agent proposes per-field text blocks; a take fills the form, never the record. */}
      <div className="del-compose">
        <FieldChat
          engagementId={engagementId}
          title={title}
          contextLines={[`Requirement classification: ${values.type} · ${values.priority}${values.category ? ` · ${values.category}` : ''}`]}
          fields={[
            { key: 'statement', label: t('Statement'), get: () => values.statement, set: (txt) => set({ statement: txt }) },
            { key: 'rationale', label: t('Rationale'), get: () => values.rationale, set: (txt) => set({ rationale: txt }) },
          ]}
        />
        <div className="del-compose-form">
      <div className="form-group">
        <label className="form-label">{t('Statement')}</label>
        <div className="refwrap">
          <textarea
            ref={statementRef}
            className="form-textarea"
            rows={4}
            autoFocus
            placeholder={t('The system shall …')}
            value={values.statement}
            onChange={(e) => set({ statement: e.target.value })}
          />
          {refOptions ? <RefSuggest taRef={statementRef} options={refOptions} /> : null}
        </div>
        <span className="rw-hintline">{t('# inserts a reference — DEL-xx, REQ-xxx')}</span>
      </div>
      <div className="form-group">
        <label className="form-label">{t('Rationale')}</label>
        <div className="refwrap">
          <textarea
            ref={rationaleRef}
            className="form-textarea"
            rows={2}
            placeholder={t('Why this exists — the business reason, not the implementation.')}
            value={values.rationale}
            onChange={(e) => set({ rationale: e.target.value })}
          />
          {refOptions ? <RefSuggest taRef={rationaleRef} options={refOptions} /> : null}
        </div>
      </div>
      <div className="flex gap-12">
        <div className="form-group flex-1">
          <label className="form-label">{t('Type')}</label>
          <select className="form-select" value={values.type} onChange={(e) => set({ type: e.target.value as RequirementType })}>
            {TYPES.map((ty) => (
              <option key={ty.value} value={ty.value}>
                {t(ty.label)}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group flex-1">
          <label className="form-label">{t('Priority')}</label>
          <select className="form-select" value={values.priority} onChange={(e) => set({ priority: e.target.value as Priority })}>
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {t(p.label)}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group flex-1">
          <label className="form-label">{t('Category')}</label>
          <input
            className="form-input"
            placeholder={t('Pick from the scheme…')}
            list="category-scheme-options"
            value={values.category}
            onChange={(e) => set({ category: e.target.value })}
          />
          <datalist id="category-scheme-options">
            {categoryOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
      </div>
      {evidenceFailures.length ? (
        <div className="text-sm mb-8" style={{ color: 'var(--err)' }}>
          {tp(
            evidenceFailures.length,
            '{n} evidence span rejected — a quote must be a literal substring of the cited document. Fix the marked rows and submit again; nothing was saved.',
            '{n} evidence spans rejected — a quote must be a literal substring of the cited document. Fix the marked rows and submit again; nothing was saved.',
          )}
        </div>
      ) : null}
      <EvidenceEditor
        documents={documents}
        documentsError={documentsError}
        value={values.evidence}
        failures={evidenceFailures}
        onRowEdited={onEvidenceEdited}
        onChange={(evidence) => set({ evidence })}
      />
        </div>
      </div>
    </Modal>
  );
}

// ───────────────────────── tag chips + tag pool editor ─────────────────────────
//
// Categories are universal (fixed in code, same for every engagement); engagement-specific
// classification lives in TAGS. The pool below is the vocabulary the AGENT may tag from —
// operators editing a requirement's tags are never restricted to it.

/** Quiet, metadata-weight tag chips — never decoration. */
function TagChips({ tags, small }: { tags: string[]; small?: boolean }) {
  if (!tags?.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-4" style={{ verticalAlign: 'middle' }}>
      {tags.map((t) => (
        <span
          key={t}
          className="badge badge-plain mono"
          style={{ textTransform: 'none', ...(small ? { fontSize: 12, padding: '0 6px' } : {}) }}
        >
          {t}
        </span>
      ))}
    </span>
  );
}

const TAG_SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function TagPoolModal({
  pool,
  busy,
  onSave,
  onClear,
  onClose,
}: {
  pool: string[] | null;
  busy: boolean;
  onSave: (tags: string[]) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [tags, setTags] = useState<string[]>(pool ?? []);
  const [draft, setDraft] = useState('');

  const normalized = draft.trim().toLowerCase();
  const addable = normalized.length >= 2 && normalized.length <= 30 && TAG_SHAPE.test(normalized) && !tags.includes(normalized);
  const add = () => {
    if (!addable || tags.length >= 30) return;
    setTags((xs) => [...xs, normalized]);
    setDraft('');
  };

  return (
    <Modal
      title={t('Tag pool — vocabulary editor')}
      onClose={onClose}
      footer={
        <>
          {pool ? (
            <button
              className="btn btn-ghost"
              disabled={busy}
              title={t('Remove the pool — the agent proposes no tags at all')}
              onClick={onClear}
            >
              {t('Clear pool')}
            </button>
          ) : null}
          <button className="btn btn-ghost" onClick={onClose}>
            {t('Cancel')}
          </button>
          <button className="btn btn-accent" disabled={busy} onClick={() => onSave(tags)}>
            {busy ? <span className="spinner spinner-sm" /> : null} {t('Save pool')}
          </button>
        </>
      }
    >
      <div className="text-sm text-ink-3 mb-12">
        {t('The only tags the agent may attach to its proposals — engagement-specific classification next to the (universal) category. Lowercase-kebab, 2–30 characters, at most 30 tags. Your own tag edits on requirements are not restricted to this pool.')}
      </div>
      <div className="text-xs text-ink-4 mb-12">
        {t('This edits the VOCABULARY only — nothing here tags a requirement. To tag one, open its detail panel and click the pool chips there.')}
      </div>
      <div className="flex flex-wrap gap-4 mb-12">
        {tags.map((tg) => (
          <span key={tg} className="chip active mono">
            {tg}
            <button
              className="btn-unstyled"
              style={{ marginLeft: 6, cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', padding: 0 }}
              title={t('Remove {tag} from the pool', { tag: tg })}
              onClick={() => setTags((xs) => xs.filter((x) => x !== tg))}
            >
              ×
            </button>
          </span>
        ))}
        {!tags.length ? <span className="text-sm text-ink-3">{t('No tags — the agent will propose none.')}</span> : null}
      </div>
      <div className="flex gap-8">
        <input
          className="form-input flex-1 mono"
          placeholder={t('e.g. data-residency')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button className="btn btn-sm" disabled={!addable || tags.length >= 30} onClick={add}>
          <Plus className="icon-sm" /> {t('Add tag')}
        </button>
      </div>
      {normalized && !addable ? (
        <div className="text-xs text-ink-4 mt-4">
          {tags.includes(normalized)
            ? t('Already in the pool.')
            : t('Tags are lowercase-kebab (letters/digits and single hyphens), 2–30 characters.')}
        </div>
      ) : null}
    </Modal>
  );
}

// ───────────────────────── merge / split modals ─────────────────────────

function MergeModal({
  source,
  candidates,
  busy,
  onSubmit,
  onClose,
}: {
  source: RequirementDetail;
  candidates: RegisterRow[];
  busy: boolean;
  onSubmit: (targetId: number, mergedStatement: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [targetId, setTargetId] = useState<number | null>(null);
  const [statement, setStatement] = useState('');
  const [query, setQuery] = useState('');
  const filtered = candidates
    .filter((c) => c.id !== source.id && !c.locked)
    .filter((c) => !query || `${c.ref} ${c.statement ?? ''}`.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 40);

  return (
    <Modal
      title={t('Merge {ref} into…', { ref: source.ref })}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            {t('Cancel')}
          </button>
          <button className="btn btn-accent" disabled={busy || !targetId} onClick={() => targetId && onSubmit(targetId, statement)}>
            {t('Merge — {ref} becomes out of scope', { ref: source.ref })}
          </button>
        </>
      }
    >
      <p className="text-sm text-ink-3 mb-12">
        {t('{ref}’s evidence is carried onto the target as a superseding revision, and {ref} is marked out of scope. Nothing is deleted.', { ref: source.ref })}
      </p>
      <div className="search-wrapper mb-12" style={{ width: '100%' }}>
        <input
          className="search-input"
          placeholder={t('Find the target requirement…')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query ? (
          <button className="search-clear" onClick={() => setQuery('')}>
            ×
          </button>
        ) : null}
      </div>
      <div className="border rounded-1" style={{ maxHeight: 240, overflowY: 'auto' }}>
        {filtered.map((c) => (
          <button
            key={c.id}
            className={`selectable ${targetId === c.id ? 'active' : ''}`}
            style={{ width: '100%', textAlign: 'left' }}
            onClick={() => {
              setTargetId(c.id);
              setStatement(c.statement ?? '');
            }}
          >
            <span className="mono text-sm">{c.ref}</span>
            <span className="text-sm text-ink-2 ml-8">{c.statement}</span>
          </button>
        ))}
        {!filtered.length ? <div className="p-12 text-sm text-ink-3">{t('No eligible target.')}</div> : null}
      </div>
      {targetId ? (
        <div className="form-group mt-16">
          <label className="form-label">{t('Merged statement (the target’s new revision)')}</label>
          <textarea className="form-textarea" rows={4} value={statement} onChange={(e) => setStatement(e.target.value)} />
        </div>
      ) : null}
    </Modal>
  );
}

function SplitModal({
  source,
  busy,
  onSubmit,
  onClose,
}: {
  source: RequirementDetail;
  busy: boolean;
  onSubmit: (parts: { statement: string }[]) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [parts, setParts] = useState<string[]>([source.statement ?? '', '']);
  const valid = parts.filter((p) => p.trim().length > 2);

  return (
    <Modal
      title={t('Split {ref}', { ref: source.ref })}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            {t('Cancel')}
          </button>
          <button
            className="btn btn-accent"
            disabled={busy || valid.length < 2}
            onClick={() => onSubmit(valid.map((statement) => ({ statement })))}
          >
            {t('Split into {n} — {ref} becomes out of scope', { n: valid.length, ref: source.ref })}
          </button>
        </>
      }
    >
      <p className="text-sm text-ink-3 mb-12">
        {t('Each part becomes a new requirement with its own REQ ref, inheriting {ref}’s evidence, type, priority and category. {ref} is marked out of scope with the split recorded.', { ref: source.ref })}
      </p>
      {parts.map((part, i) => (
        <div className="form-group" key={i}>
          <label className="form-label">{t('Part {n}', { n: i + 1 })}</label>
          <div className="flex gap-8">
            <textarea
              className="form-textarea flex-1"
              rows={3}
              value={part}
              onChange={(e) => setParts(parts.map((p, idx) => (idx === i ? e.target.value : p)))}
            />
            {parts.length > 2 ? (
              <button className="btn btn-icon" title={t('Remove part')} onClick={() => setParts(parts.filter((_, idx) => idx !== i))}>
                <Trash2 className="icon-sm" />
              </button>
            ) : null}
          </div>
        </div>
      ))}
      <button className="btn btn-sm" onClick={() => setParts([...parts, ''])}>
        <Plus className="icon-sm" /> {t('Add part')}
      </button>
    </Modal>
  );
}

// ───────────────────────── test-case modal (steps editor) ─────────────────────────

interface StepDraft {
  action: string;
  expected: string;
}

function TestCaseModal({
  acOptions,
  preselectedAcId,
  busy,
  onSubmit,
  onClose,
}: {
  acOptions: { id: number; ref: string; statement: string }[];
  preselectedAcId?: number;
  busy: boolean;
  onSubmit: (body: {
    title: string;
    preconditions: string;
    test_data_ref: string;
    expected_result: string;
    steps: StepDraft[];
    acceptance_criterion_ids: number[];
  }) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [title, setTitle] = useState('');
  const [preconditions, setPreconditions] = useState('');
  const [testDataRef, setTestDataRef] = useState('');
  const [expectedResult, setExpectedResult] = useState('');
  const [steps, setSteps] = useState<StepDraft[]>([{ action: '', expected: '' }]);
  const [acIds, setAcIds] = useState<number[]>(preselectedAcId ? [preselectedAcId] : []);

  const setStep = (i: number, patch: Partial<StepDraft>) =>
    setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  return (
    <Modal
      title={t('New test case')}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            {t('Cancel')}
          </button>
          <button
            className="btn btn-accent"
            disabled={busy || title.trim().length < 3 || !steps.some((s) => s.action.trim())}
            onClick={() =>
              onSubmit({
                title,
                preconditions,
                test_data_ref: testDataRef,
                expected_result: expectedResult,
                steps: steps.filter((s) => s.action.trim()),
                acceptance_criterion_ids: acIds,
              })
            }
          >
            {t('Create test case')}
          </button>
        </>
      }
    >
      <div className="form-group">
        <label className="form-label">{t('Title')}</label>
        <input className="form-input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="flex gap-12">
        <div className="form-group flex-1">
          <label className="form-label">{t('Preconditions')}</label>
          <input className="form-input" value={preconditions} onChange={(e) => setPreconditions(e.target.value)} />
        </div>
        <div className="form-group flex-1">
          <label className="form-label">{t('Test data reference')}</label>
          <input
            className="form-input"
            placeholder={t('A pointer, never the data itself')}
            value={testDataRef}
            onChange={(e) => setTestDataRef(e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('Steps')}</label>
        <table className="table table-compact">
          <thead>
            <tr>
              <th style={{ width: 32 }}>#</th>
              <th>{t('Action')}</th>
              <th>{t('Expected')}</th>
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {steps.map((step, i) => (
              <tr key={i}>
                <td className="voice-figure">{i + 1}</td>
                <td>
                  <input
                    className="form-input form-input--bare"
                    placeholder={t('Do this…')}
                    value={step.action}
                    onChange={(e) => setStep(i, { action: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className="form-input form-input--bare"
                    placeholder={t('Observe that…')}
                    value={step.expected}
                    onChange={(e) => setStep(i, { expected: e.target.value })}
                  />
                </td>
                <td>
                  {steps.length > 1 ? (
                    <button className="btn btn-icon" title={t('Remove step')} onClick={() => setSteps(steps.filter((_, x) => x !== i))}>
                      <Trash2 className="icon-sm" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-sm mt-8" onClick={() => setSteps([...steps, { action: '', expected: '' }])}>
          <Plus className="icon-sm" /> {t('Add step')}
        </button>
      </div>

      <div className="form-group">
        <label className="form-label">{t('Overall expected result')}</label>
        <textarea className="form-textarea" rows={2} value={expectedResult} onChange={(e) => setExpectedResult(e.target.value)} />
      </div>

      <div className="form-group">
        <label className="form-label">{t('Covers acceptance criteria')}</label>
        <div className="flex flex-wrap gap-4" style={{ maxHeight: 140, overflowY: 'auto' }}>
          {acOptions.map((ac) => (
            <button
              key={ac.id}
              className={`chip ${acIds.includes(ac.id) ? 'active' : ''}`}
              title={ac.statement}
              onClick={() => setAcIds(acIds.includes(ac.id) ? acIds.filter((x) => x !== ac.id) : [...acIds, ac.id])}
            >
              <span className="mono">{ac.ref}</span>
            </button>
          ))}
          {!acOptions.length ? <span className="text-sm text-ink-3">{t('No acceptance criteria yet.')}</span> : null}
        </div>
      </div>
    </Modal>
  );
}

// ───────────────────────── detail panel ─────────────────────────

function AddAcForm({ requirement, onCreated }: { requirement: RequirementDetail; onCreated: () => void }) {
  const t = useT();
  const toast = useToast();
  const [statement, setStatement] = useState('');
  const [method, setMethod] = useState('');
  const [preview, setPreview] = useState<LintResult | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (statement.trim().length < 5) {
      setPreview(null);
      return;
    }
    timer.current = setTimeout(() => {
      acsApi
        .lint(statement, method, requirement.type)
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 400);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [statement, method, requirement.type]);

  const submit = async () => {
    setBusy(true);
    try {
      const ac = await acsApi.create(requirement.id, statement, method || undefined);
      setStatement('');
      setMethod('');
      setPreview(null);
      onCreated();
      toast(
        ac.lint_flags?.pass ? 'ok' : 'warn',
        ac.lint_flags?.pass ? t('{ref} created', { ref: ac.ref }) : t('{ref} created — linter flagged it', { ref: ac.ref }),
      );
    } catch (e) {
      toast('err', (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded-1 p-12 mt-12 bg-inset">
      <div className="section-label mb-8">{t('Add acceptance criterion')}</div>
      <textarea
        className="form-textarea mb-8"
        rows={2}
        placeholder={t('Given …, when …, then … (include a measurable threshold)')}
        value={statement}
        onChange={(e) => setStatement(e.target.value)}
      />
      <input
        className="form-input mb-8"
        placeholder={t('Measurement method (required for non-functional)')}
        value={method}
        onChange={(e) => setMethod(e.target.value)}
      />
      {preview ? <LintFlags lint={preview} /> : null}
      <button className="btn btn-sm btn-accent mt-8" disabled={busy || statement.trim().length < 5} onClick={submit}>
        <Plus className="icon-sm" /> {t('Add criterion')}
      </button>
    </div>
  );
}

/**
 * THE PANEL — the requirement, in the order the operator asks about it.
 *
 * The re-cut (IMPLEMENTATION §7): EVIDENCE is promoted above the action bar, because "why does
 * it say this?" is the question the operator brought and it used to sit below Delete. The five
 * sections are Statement · Evidence · Obligations · Classification · History, and the footer
 * carries ONE primary verb with everything else behind `More ▾` — a row of six equal buttons
 * states no order of business.
 *
 * Nothing the panel could do before it can no longer do: revise, merge, split, descope, delete,
 * link and unlink deliverables, add and delete criteria, create and release test cases, edit
 * type / weight / category / tags, and read the full revision history all survive.
 */
function RequirementPanel({
  detail,
  rows,
  deliverables,
  busy,
  tagPool,
  categories,
  initialSection,
  highlightAc,
  onSaveTags,
  onSetField,
  onClose,
  onReload,
  onAction,
  onViewSpan,
  onOpenRef,
}: {
  detail: RequirementDetail;
  rows: RegisterRow[];
  deliverables: Deliverable[];
  busy: boolean;
  /** Suggestions only — the operator may type any tag; the pool binds the agent. */
  tagPool: string[] | null;
  /** The universal category scheme (GET /register/category-scheme) — descriptions ride as tooltips. */
  categories: { value: string; label: string; title?: string }[];
  /** Where an arriving deep link wants the panel to open. Applied once, on mount. */
  initialSection: 'evidence' | 'acs' | null;
  /** `?ac=AC-014.2` — the criterion the caller came for, marked so it can be found. */
  highlightAc: string | null;
  onSaveTags: (tags: string[]) => void;
  onSetField: (field: 'type' | 'priority' | 'status' | 'category', value: string | null) => void;
  onClose: () => void;
  onReload: () => void;
  onAction: (action: 'revise' | 'merge' | 'split' | 'descope' | 'restore' | 'confirm' | 'unconfirm' | 'new-tc' | 'delete' | 'change-request') => void;
  onViewSpan: (evidence: EvidenceRow) => void;
  onOpenRef: (ref: string) => void;
}) {
  const t = useT();
  const { tp, fmtDate, fmtDateTime } = useLocale();
  const toast = useToast();
  const [showAllRevisions, setShowAllRevisions] = useState(false);
  const [linkId, setLinkId] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [holding, setHolding] = useState(false);
  const tags = detail.tags ?? [];
  const addTag = () => {
    const v = tagDraft.trim();
    if (!v || tags.includes(v)) return;
    setTagDraft('');
    onSaveTags([...tags, v]);
  };
  // The panel owns the deletes of the things it lists; the requirement itself goes through
  // onAction, because deleting it has to close this panel.
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const locked = detail.locked;

  const unlinked = deliverables.filter((d) => !detail.deliverables.some((x) => x.id === d.id));

  // A deep link that named a criterion opens the panel AT the obligations section.
  useEffect(() => {
    if (!initialSection) return;
    const id = initialSection === 'acs' ? 'reg-p-obligations' : 'reg-p-evidence';
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
  }, [initialSection, detail.id]);

  const link = async () => {
    if (!linkId) return;
    try {
      await deliverablesApi.mapRequirement(Number(linkId), detail.id);
      setLinkId('');
      onReload();
      toast('ok', t('Deliverable linked'));
    } catch (e) {
      toast('err', (e as Error).message);
    }
  };

  const unlink = async (deliverableId: number) => {
    try {
      await deliverablesApi.unmapRequirement(deliverableId, detail.id);
      onReload();
      toast('ok', t('Deliverable unlinked'));
    } catch (e) {
      toast('err', (e as Error).message);
    }
  };

  /**
   * HOLD ALL — the endpoint `POST /deliverables/acs/:id/status` has existed, guarded and audited,
   * since the criteria shipped, and until now NOTHING in either SPA called it: there are zero
   * `acceptance_criterion.status` rows in the audit log. Every proposed criterion in the product
   * was therefore permanently ◇. This one button is the whole change.
   */
  const draftAcs = detail.acceptance_criteria.filter((ac) => ac.status === 'draft');
  const holdAll = async () => {
    setHolding(true);
    const done: string[] = [];
    const refused: { ref: string; reason: string }[] = [];
    for (const ac of draftAcs) {
      try {
        await acsApi.setStatus(ac.id, 'confirmed');
        done.push(ac.ref);
      } catch (e) {
        refused.push({ ref: ac.ref, reason: (e as Error).message });
      }
    }
    setHolding(false);
    onReload();
    if (refused.length) {
      toast('warn', t('{done} held, {refused} refused — {ref}: {reason}', { done: done.length, refused: refused.length, ref: refused[0].ref, reason: refused[0].reason }));
    } else {
      toast('ok', tp(done.length, '{n} criterion held — an operator now stands behind it', '{n} criteria held — an operator now stands behind them'));
    }
  };

  const revisions = showAllRevisions ? detail.revisions : detail.revisions.slice(0, 3);
  const statusWord = detail.status === 'draft' ? t('proposed') : t(REQUIREMENT_STATUS_LABEL[detail.status]?.word ?? detail.status);

  return (
    <div className="panel-overlay open">
      <div className="panel-overlay-bg" onClick={onClose} />
      <div className="panel-slide reg-panel" style={{ width: 'min(660px, 96vw)' }}>
        {/* ── head: ref, revision, where it came from, when, what it is now ── */}
        <div className="panel-slide-head">
          <div style={{ minWidth: 0 }}>
            <div className="mono text-lg font-semi">{detail.ref}</div>
            <div className="reg-p-meta">
              <span className="voice-figure">{t('rev {n}', { n: detail.revision_no })}</span>
              <span className="sep">·</span>
              <span>{t((detail.revision_source ?? 'operator').replace(/_/g, ' '))}</span>
              <span className="sep">·</span>
              <span className="voice-figure">{fmtDate(detail.revised_at)}</span>
              <span className="sep">·</span>
              <span>{statusWord}</span>
              {detail.merged_into_ref ? (
                <>
                  <span className="sep">·</span>
                  <button type="button" className="reg-link" onClick={() => onOpenRef(detail.merged_into_ref!)}>
                    {t('merged into {ref} →', { ref: detail.merged_into_ref })}
                  </button>
                </>
              ) : null}
            </div>
          </div>
          <button className="panel-slide-close" onClick={onClose} aria-label={t('Close')}>
            ×
          </button>
        </div>

        <div className="panel-slide-body p-0">
          {/* ── 1 · the statement, in the record's own voice ── */}
          <div className="panel-section">
            <div className="section-label mb-8">{t('Statement')}</div>
            <div className="flex items-baseline gap-8">
              <ClaimMark status={detail.status} mergedInto={detail.merged_into_ref} subject={detail.ref} />
              <Tx type="requirement_revision" id={detail.current_revision_id} field="statement"
                  original={detail.statement} className="voice-record" style={{ margin: 0 }} as="p" />
            </div>
            {/* The only place a translation can be corrected: a detail view, never a list row. */}
            <TxTools type="requirement_revision" id={detail.current_revision_id} field="statement"
                     original={detail.statement} />
            {detail.rationale ? (
              <>
                {/* rationale is Signet summarising WHY — the system's voice, not the record's */}
                <div className="section-label mt-16 mb-4">{t('Rationale')}</div>
                <Tx type="requirement_revision" id={detail.current_revision_id} field="rationale"
                    original={detail.rationale} className="text-sm text-ink-2"
                    style={{ lineHeight: 1.55 }} as="p" />
              </>
            ) : null}
          </div>

          {/* ── 2 · EVIDENCE, above the actions: it is the answer to the question the
                 operator brought (P2), not a footnote below Delete. ── */}
          <div className="panel-section" id="reg-p-evidence">
            <div className="flex items-center gap-8 mb-8">
              <div className="section-label">{t('Evidence')}</div>
              <span className="voice-figure text-xs text-ink-3">{detail.evidence.length}</span>
            </div>
            {!detail.evidence.length ? (
              <div className="text-sm text-ink-3">
                {t('Nothing is cited on the current revision —')} <b>{t('{ref} cannot answer “why does it say this?”', { ref: detail.ref })}</b>.{' '}
                {t('Confirming it is refused (P2).')}
              </div>
            ) : null}
            {/* A verbatim span from a client document is the record at its purest: full
                evidence dress — serif behind the 2px rule, closed by the ref line that
                says which document it came from. An .ev without an .ev-src is a defect. */}
            {detail.evidence.map((ev) => (
              <div key={ev.id} className="reg-evblock">
                <blockquote className="ev" style={{ margin: 0 }}>
                  {ev.quoted_span}
                </blockquote>
                <div className="ev-src">
                  <span className="att att--quoted" aria-hidden="true" />
                  <span className="src-ref">{ev.document_ref}</span>
                  <span className="sep">·</span>
                  <span className="truncate" style={{ maxWidth: 200 }}>
                    {ev.original_filename}
                  </span>
                  <span className="sep">·</span>
                  <span className="voice-figure">{fmtDate(ev.effective_date)}</span>
                  {/* The document this span came from has since been replaced. The span is still
                      verbatim — but what it is evidence OF may have moved (DESIGN §6). */}
                  {ev.document_status === 'superseded' ? (
                    <>
                      <span className="sep">·</span>
                      <span className="text-warn">{t('superseded document')}</span>
                    </>
                  ) : null}
                  <button className="reg-link ml-auto" onClick={() => onViewSpan(ev)}>
                    {t('View in context →')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ── 3 · the obligations this requirement carries, each with ONE act ── */}
          <div className="panel-section" id="reg-p-obligations">
            <div className="section-label mb-8">{t('Obligations')}</div>

            {/* deliverable — funded or not */}
            <div className="reg-obl">
              <span className={`reg-lamp${detail.deliverables.length ? ' on' : ''}`} aria-hidden="true" />
              <span className="reg-obl-k">{t('Deliverable')}</span>
              <span className="reg-obl-v">
                {detail.deliverables.length ? (
                  <span className="flex flex-wrap gap-8">
                    {detail.deliverables.map((d) => (
                      <span key={d.id} className="badge badge-clickable" title={d.name}>
                        <span className="src-ref">{d.ref}</span> {d.name}
                        {!locked ? (
                          <button
                            className="btn btn-icon"
                            style={{ width: 16, height: 16, marginLeft: 4 }}
                            title={t('Unlink {ref}', { ref: d.ref })}
                            onClick={() => unlink(d.id)}
                          >
                            <X className="icon-sm" style={{ width: 10, height: 10 }} />
                          </button>
                        ) : null}
                      </span>
                    ))}
                  </span>
                ) : detail.type === 'out_of_scope' || detail.status === 'descoped' ? (
                  <span className="n-none">{t('not applicable — an exclusion funds nothing')}</span>
                ) : (
                  <span className="text-warn">{t('unfunded — no deliverable carries it')}</span>
                )}
                {!locked && unlinked.length ? (
                  <span className="flex gap-8 mt-8">
                    <select className="form-select" style={{ flex: 1 }} value={linkId} onChange={(e) => setLinkId(e.target.value)}>
                      <option value="">{t('Map to a deliverable…')}</option>
                      {unlinked.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.ref} — {d.name}
                        </option>
                      ))}
                    </select>
                    <button className="btn btn-sm" disabled={!linkId} onClick={link}>
                      {t('Map')}
                    </button>
                  </span>
                ) : null}
              </span>
            </div>

            {/* criteria — testable or not, and the button that lets them be held */}
            <div className="reg-obl">
              <span className={`reg-lamp${detail.acceptance_criteria.length ? ' on' : ''}`} aria-hidden="true" />
              <span className="reg-obl-k">{t('Criteria')}</span>
              <span className="reg-obl-v">
                {!detail.acceptance_criteria.length ? (
                  detail.type === 'out_of_scope' || detail.status === 'descoped' ? (
                    <span className="n-none">{t('not applicable')}</span>
                  ) : (
                    <span className="text-warn">{t('nothing here is testable yet')}</span>
                  )
                ) : null}
                {detail.acceptance_criteria.map((ac) => (
                  <div
                    key={ac.id}
                    className={`reg-ac${highlightAc === ac.ref ? ' reg-ac--hit' : ''}`}
                    id={`reg-ac-${ac.ref}`}
                  >
                    <div className="flex items-start gap-8">
                      <span className="src-ref text-ink-1">{ac.ref}</span>
                      <ClaimMark status={ac.status} subject={ac.ref} />
                      <Tx type="acceptance_criterion" id={ac.id} field="statement" original={ac.statement}
                          className="voice-record flex-1" style={{ fontSize: 'var(--t-scan)', lineHeight: 1.5 }} as="span" />
                      <TrashButton
                        label={t('Delete {ref}', { ref: ac.ref })}
                        onClick={() => setDeleteTarget({ entity: 'acceptance_criterion', id: ac.id, ref: ac.ref })}
                      />
                    </div>
                    {ac.measurement_method ? (
                      <div className="text-xs text-ink-3 mt-4">
                        {t('Measured by:')}{' '}
                        <Tx type="acceptance_criterion" id={ac.id} field="measurement_method" original={ac.measurement_method} as="span" />
                      </div>
                    ) : null}
                    <LintFlags lint={ac.lint_flags} />
                    <div className="flex flex-wrap gap-4 mt-4 items-center">
                      {ac.test_cases.length ? (
                        ac.test_cases.map((tc) => (
                          <span key={tc.id} className="badge mono" title={tc.title}>
                            {tc.ref}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-warn">{t('no test case — the client has nothing to run at UAT')}</span>
                      )}
                    </div>
                  </div>
                ))}
                <AddAcForm requirement={detail} onCreated={onReload} />
              </span>
              {draftAcs.length ? (
                <span className="reg-obl-a">
                  <button
                    className="btn btn-sm"
                    disabled={busy || holding}
                    title={t('Hold all {n} proposed criteria — an operator stands behind them as what the client will be held to', { n: draftAcs.length })}
                    onClick={() => void holdAll()}
                  >
                    {t('Hold all {n}', { n: draftAcs.length })}
                  </button>
                </span>
              ) : null}
            </div>

            {/* test cases — the thing that turns a criterion into a pass/fail at UAT */}
            <div className="reg-obl">
              <span className={`reg-lamp${detail.test_cases.length ? ' on' : ''}`} aria-hidden="true" />
              <span className="reg-obl-k">{t('Test cases')}</span>
              <span className="reg-obl-v">
                {!detail.test_cases.length ? <span className="text-ink-3">{t('None linked.')}</span> : null}
                {detail.test_cases.map((tc) => (
                  <div key={tc.id} className="reg-tc">
                    <div className="flex items-center gap-8">
                      <span className="mono text-sm">{tc.ref}</span>
                      <Tx type="test_case" id={tc.id} field="title" original={tc.title}
                          className="text-sm text-ink-1 flex-1" as="span" />
                      <TcStatusBadge status={tc.status} />
                      {/* A draft test case is invisible to the client — this select is the release. */}
                      <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={tc.status}
                        title={t("Only 'ready' test cases appear in the client's UAT list")}
                        onChange={async (e) => {
                          try {
                            await testCasesApi.setStatus(tc.id, e.target.value);
                            onReload();
                            toast('ok', t('{ref} is {status}', { ref: tc.ref, status: t(e.target.value) }));
                          } catch (err) {
                            toast('err', (err as Error).message);
                          }
                        }}
                      >
                        {TC_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {t(s.label)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {(tc.steps ?? []).length ? (
                      <ol className="text-xs text-ink-3 mt-8" style={{ paddingLeft: 18 }}>
                        {(tc.steps ?? []).map((s) => (
                          <li key={s.n} style={{ marginBottom: 2 }}>
                            {s.action}
                            {s.expected ? <span className="text-ink-4"> → {s.expected}</span> : null}
                          </li>
                        ))}
                      </ol>
                    ) : null}
                  </div>
                ))}
              </span>
              <span className="reg-obl-a">
                <button
                  className="btn btn-sm"
                  disabled={!detail.acceptance_criteria.length}
                  title={detail.acceptance_criteria.length ? t('Write a test case against these criteria') : t('A test case attaches to a criterion — there is none yet')}
                  onClick={() => onAction('new-tc')}
                >
                  {t('New test case')}
                </button>
              </span>
            </div>
          </div>

          {/* ── 4 · classification — the axes that left the grid ── */}
          <div className="panel-section">
            <div className="section-label mb-8">{t('Classification')}</div>
            <dl className="reg-klist">
              <dt>{t('Type')}</dt>
              <dd>
                <QuietSelect
                  value={detail.type}
                  options={TYPES.map((ty) => ({ value: ty.value, label: t(ty.label) }))}
                  disabled={busy || locked}
                  label={t('Type of {ref}', { ref: detail.ref })}
                  title={locked ? lockTitle(t, detail.ref, detail.status) : t('Change the requirement type')}
                  onChange={(next) => onSetField('type', next)}
                />
              </dd>
              <dt>{t('Weight')}</dt>
              <dd>
                <QuietSelect
                  value={detail.priority}
                  options={PRIORITIES.map((p) => ({ value: p.value, label: t(p.label) }))}
                  disabled={busy || locked}
                  label={t('Weight of {ref}', { ref: detail.ref })}
                  title={locked ? lockTitle(t, detail.ref, detail.status) : t('Change the weight — MoSCoW is editable until the baseline locks it')}
                  onChange={(next) => onSetField('priority', next)}
                />
              </dd>
              <dt>{t('Status')}</dt>
              <dd>
                {detail.status === 'draft' || detail.status === 'confirmed' ? (
                  // The two statuses the operator owns, both ways (confirm AND unconfirm). The
                  // machinery statuses are never offered as targets — they render as a chip.
                  <QuietSelect
                    value={detail.status}
                    options={[
                      { value: 'draft', label: t('Draft') },
                      { value: 'confirmed', label: t(REQUIREMENT_STATUS_LABEL.confirmed.label) },
                    ]}
                    disabled={busy}
                    label={t('Status of {ref}', { ref: detail.ref })}
                    title={t('Draft ↔ held — baselined, changed, out-of-scope, delivered and accepted are set by their own flows')}
                    onChange={(next) => onSetField('status', next)}
                  />
                ) : (
                  <span style={{ display: 'inline-flex' }} title={STATUS_FLOW_TITLE[detail.status] ? t(STATUS_FLOW_TITLE[detail.status]) : ''}>
                    <StatusBadge status={detail.status} mergedInto={detail.merged_into_ref} />
                  </span>
                )}
              </dd>
              <dt>{t('Category')}</dt>
              <dd>
                <QuietSelect
                  value={detail.category ?? ''}
                  options={[{ value: '', label: t('uncategorised') }, ...categories]}
                  disabled={busy || locked}
                  label={t('Category of {ref}', { ref: detail.ref })}
                  title={locked ? lockTitle(t, detail.ref, detail.status) : t('Change the (universal) category')}
                  onChange={(next) => onSetField('category', next || null)}
                />
              </dd>
              <dt>{t('Tags')}</dt>
              <dd>
                {/* Pool tags are TOGGLE CHIPS that tag/untag THIS requirement on click, saving
                    immediately. The pool VOCABULARY is edited in the toolbar's Tag pool modal. */}
                {tagPool?.length ? (
                  <div className="flex flex-wrap gap-4 items-center mb-8">
                    {tagPool.map((tg) => {
                      const on = tags.includes(tg);
                      return (
                        <button
                          key={tg}
                          className={`chip mono ${on ? 'active' : ''}`}
                          disabled={busy || locked}
                          title={
                            locked
                              ? lockTitle(t, detail.ref, detail.status)
                              : on
                                ? t('Remove {tag} from {ref}', { tag: tg, ref: detail.ref })
                                : t('Tag {ref} with {tag}', { tag: tg, ref: detail.ref })
                          }
                          onClick={() => onSaveTags(on ? tags.filter((x) => x !== tg) : [...tags, tg])}
                        >
                          {on ? <Check className="icon-sm" style={{ width: 11, height: 11 }} /> : null} {tg}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-4 items-center">
                  {tags
                    .filter((tg) => !(tagPool ?? []).includes(tg))
                    .map((tg) => (
                      <span key={tg} className="badge badge-plain mono" style={{ textTransform: 'none' }}>
                        {tg}
                        <button
                          style={{ marginLeft: 4, cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', padding: 0 }}
                          title={locked ? lockTitle(t, detail.ref, detail.status) : t('Remove tag {tag}', { tag: tg })}
                          disabled={busy || locked}
                          onClick={() => onSaveTags(tags.filter((x) => x !== tg))}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  {!tags.length && !tagPool?.length ? <span className="text-xs text-ink-4">{t('none')}</span> : null}
                  <input
                    className="form-input mono"
                    style={{ width: 150, height: 24, fontSize: 12 }}
                    placeholder={t('add your own tag…')}
                    title={locked ? lockTitle(t, detail.ref, detail.status) : t('Free-text operator tag — not restricted to the pool. Enter saves.')}
                    value={tagDraft}
                    disabled={busy || locked}
                    onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                  />
                  <button
                    className="btn btn-sm btn-ghost"
                    disabled={busy || locked || !tagDraft.trim()}
                    title={locked ? lockTitle(t, detail.ref, detail.status) : t('Add the typed tag')}
                    onClick={addTag}
                  >
                    <Plus className="icon-sm" />
                  </button>
                </div>
              </dd>
            </dl>
          </div>

          {/* ── 5 · history ── */}
          <div className="panel-section">
            <div className="flex items-center gap-8 mb-8">
              <div className="section-label">{t('History')}</div>
              <span className="voice-figure text-xs text-ink-3">
                {tp(detail.revisions.length, '{n} revision', '{n} revisions')}
              </span>
            </div>
            {revisions.map((rev, i) => {
              const prev = detail.revisions[detail.revisions.indexOf(rev) + 1];
              const changed = prev && prev.statement !== rev.statement;
              return (
                <div key={rev.id} className={`reg-rev${rev.is_current ? ' reg-rev--cur' : ''}`}>
                  <div className="flex items-center gap-8 mb-4" style={{ flexWrap: 'wrap' }}>
                    <span className="mono text-sm">{t('rev {n}', { n: rev.revision_no })}</span>
                    <span className="badge">{t(rev.source.replace('_', ' '))}</span>
                    {rev.change_request_ref ? <span className="badge badge-accent mono">{rev.change_request_ref}</span> : null}
                    {rev.proposal_id ? <span className="badge mono">{t('proposal #{id}', { id: rev.proposal_id })}</span> : null}
                    {rev.is_current ? <span className="badge badge-ok">{t('current')}</span> : null}
                  </div>
                  {/* who and when: a name is the system reporting, a timestamp and a tally
                      are quantities — neither is a ref, so neither is mono */}
                  <div className="text-xs text-ink-3 mb-8">
                    {rev.created_by_name ?? t(rev.created_by_kind)} ·{' '}
                    <span className="voice-figure">{fmtDateTime(rev.created_at)}</span> ·{' '}
                    <span className="voice-figure">{rev.evidence.length}</span> {t('evidence')}
                  </div>
                  {/* every revision carries the statement as it stood — the record, verbatim */}
                  {changed && i < 2 ? (
                    <div className="read-quote">
                      <div className="text-ink-4" style={{ textDecoration: 'line-through' }}>
                        {prev.statement}
                      </div>
                      <div className="text-ink-2 mt-4">{rev.statement}</div>
                    </div>
                  ) : (
                    <div className="voice-record text-ink-2" style={{ fontSize: 'var(--t-scan)', lineHeight: 1.5 }}>
                      {rev.statement}
                    </div>
                  )}
                </div>
              );
            })}
            {detail.revisions.length > 3 ? (
              <button className="btn btn-sm" onClick={() => setShowAllRevisions(!showAllRevisions)}>
                {showAllRevisions ? t('Show latest 3') : t('Show all {n} revisions', { n: detail.revisions.length })}
              </button>
            ) : null}
          </div>

          {/* ── change requests, when any exist ── */}
          {detail.change_requests.length ? (
            <div className="panel-section">
              <div className="section-label mb-8">{t('Change requests')}</div>
              {detail.change_requests.map((cr) => (
                <div key={cr.id} className="flex items-center gap-8 mb-4">
                  <span className="mono text-sm">{cr.ref}</span>
                  <span className="text-sm text-ink-2 flex-1">{cr.title}</span>
                  <span className="badge">{t(cr.status)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* ── footer: ONE primary, everything else behind More ▾ ── */}
        <div className="reg-p-foot">
          {locked ? (
            <>
              <span className="text-xs text-ink-3" style={{ maxWidth: '38ch' }}>
                {t('Signed scope — every change goes through a change request.')}
              </span>
              {/* Replaces a permanently-disabled button that explained why it did not work
                  instead of working: this one opens the CR flow where it actually lives. */}
              <button className="btn btn-sm" disabled={busy} onClick={() => onAction('change-request')}>
                {t('Raise a change request →')}
              </button>
            </>
          ) : detail.status === 'descoped' ? (
            <button
              className="btn btn-accent btn-sm"
              disabled={busy}
              title={t('Bring it back as proposed ◇ — it is confirmed again like any other row')}
              onClick={() => onAction('restore')}
            >
              {t('Restore to scope — proposed ◇')}
            </button>
          ) : detail.status === 'draft' && !detail.evidence.length ? (
            <span className="text-xs text-warn" style={{ maxWidth: '44ch' }}>
              {t('Cannot be confirmed: it cites no evidence (P2). Revise it with a quoted span first.')}
            </span>
          ) : detail.status === 'draft' ? (
            <button className="btn btn-accent btn-sm" disabled={busy} onClick={() => onAction('confirm')}>
              <Check className="icon-sm" /> {t('Confirm — stand behind it')}
            </button>
          ) : (
            <button className="btn btn-accent btn-sm" disabled={busy} onClick={() => onAction('revise')}>
              <FileText className="icon-sm" /> {t('Revise → rev {n}', { n: (detail.revision_no ?? 0) + 1 })}
            </button>
          )}

          <div className="reg-more">
            <button className="btn btn-sm btn-ghost" onClick={() => setMoreOpen((v) => !v)} aria-expanded={moreOpen}>
              {t('More ▾')}
            </button>
            {moreOpen ? (
              <div className="reg-more-menu">
                {!locked && detail.status !== 'draft' ? null : (
                  <button disabled={busy} onClick={() => { setMoreOpen(false); onAction('revise'); }}>
                    {t('Revise → new revision')}
                  </button>
                )}
                {/* The other direction of Confirm, pre-baseline only: a confirmed row goes back to
                    proposed ◇ while the client is still being asked — nothing is lost, the revision
                    history stays, and G1 counts it as unconfirmed again until it is re-confirmed. */}
                {detail.status === 'confirmed' && !locked ? (
                  <button disabled={busy} onClick={() => { setMoreOpen(false); onAction('unconfirm'); }}>
                    {t('Back to proposed ◇')}
                  </button>
                ) : null}
                <button disabled={busy || locked} onClick={() => { setMoreOpen(false); onAction('merge'); }}>
                  {t('Merge into…')}
                </button>
                <button disabled={busy || locked} onClick={() => { setMoreOpen(false); onAction('split'); }}>
                  {t('Split…')}
                </button>
                {/* Retire, not destroy — so it no longer wears the danger colour that belongs
                    to Delete. The two must not read as the same act. */}
                <button disabled={busy || locked} onClick={() => { setMoreOpen(false); onAction('descope'); }}>
                  {t('Mark out of scope')}
                </button>
                {/* Out of scope keeps it. Delete is for the row that should never have existed;
                    it asks the server for the plan first and a signed row refuses with its reason. */}
                <button className="danger" disabled={busy} onClick={() => { setMoreOpen(false); onAction('delete'); }}>
                  {t('Delete permanently…')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {deleteTarget ? (
        <DeleteModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(res) => {
            setDeleteTarget(null);
            onReload();
            toast('ok', t('{ref} deleted', { ref: res.ref ?? t('It') }));
          }}
        />
      ) : null}
    </div>
  );
}

// ───────────────────────── deliverables tab ─────────────────────────

function DeliverablesTab({
  engagementId,
  deliverables,
  milestones,
  rows,
  onReload,
}: {
  engagementId: number;
  deliverables: Deliverable[];
  milestones: Milestone[];
  rows: RegisterRow[];
  onReload: () => void;
}) {
  const { t, tp } = useLocale();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dod, setDod] = useState('');
  const [newMilestone, setNewMilestone] = useState('');
  const [newReqIds, setNewReqIds] = useState<number[]>([]);
  /** The compose rail's requirement filter — 50 rows need a sieve, not scrolling. */
  const [reqQuery, setReqQuery] = useState('');
  const [selected, setSelected] = useState<Deliverable | null>(null);
  /** The register row behind a REQ badge — the hover card's statement and rationale. */
  const reqOf = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  /** What # can reference from these forms: every deliverable and every requirement. */
  const refOptions = useMemo<RefOption[]>(() => [
    ...deliverables.map((d) => ({ ref: d.ref, label: d.name })),
    ...rows.map((r) => ({ ref: r.ref, label: (r.statement ?? '').replace(/\s+/g, ' ').slice(0, 80) })),
  ], [deliverables, rows]);
  const newDescRef = useRef<HTMLTextAreaElement | null>(null);
  const newDodRef = useRef<HTMLTextAreaElement | null>(null);
  const selDescRef = useRef<HTMLTextAreaElement | null>(null);
  const selDodRef = useRef<HTMLTextAreaElement | null>(null);
  /** Where the SELECTED deliverable's ref is written elsewhere — fetched when the modal opens. */
  const [selRefs, setSelRefs] = useState<ProseReference[]>([]);
  /** Which ref that fetch is FOR — a slower answer for a previous deliverable is dropped. */
  const selRefsFor = useRef<string | null>(null);
  const [mapIds, setMapIds] = useState<number[]>([]);
  const [mapQuery, setMapQuery] = useState('');
  const [savingMap, setSavingMap] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const openDeliverable = (d: Deliverable) => {
    setSelected(d);
    setMapIds(d.requirements.map((r) => r.id));
    setMapQuery('');
    setSelRefs([]);
    selRefsFor.current = d.ref;
    deliverablesApi.references(engagementId, d.ref)
      .then((out) => { if (selRefsFor.current === d.ref) setSelRefs(out.references); })
      .catch(() => { if (selRefsFor.current === d.ref) setSelRefs([]); });
  };

  const create = async () => {
    try {
      await deliverablesApi.create(engagementId, {
        name,
        description,
        definition_of_done: dod,
        milestone_id: newMilestone ? Number(newMilestone) : null,
        requirement_ids: newReqIds,
      });
      setCreating(false);
      setName('');
      setDescription('');
      setDod('');
      setNewMilestone('');
      setNewReqIds([]);
      onReload();
      toast('ok', t('Deliverable created'));
    } catch (e) {
      toast('err', (e as Error).message);
    }
  };

  /** Inline row edits: the milestone and the status are the two fields the operator
   *  changes weekly, so they live in the row rather than behind a modal. The detail modal
   *  routes its text edits (name, description, definition of done) through here too. */
  const patchRow = async (d: Deliverable, body: Parameters<typeof deliverablesApi.update>[1], message: string) => {
    try {
      const fresh = await deliverablesApi.update(d.id, body);
      if (selected?.id === d.id) setSelected(fresh);
      onReload();
      toast('ok', message);
    } catch (e) {
      toast('err', (e as Error).message);
    }
  };

  const saveMapping = async () => {
    if (!selected) return;
    setSavingMap(true);
    try {
      const fresh = await deliverablesApi.mapRequirements(selected.id, mapIds, true);
      setSelected(fresh);
      setMapIds(fresh.requirements.map((r) => r.id));
      onReload();
      toast('ok', tp(fresh.requirements.length, '{ref} maps {n} requirement', '{ref} maps {n} requirements', { ref: fresh.ref }));
    } catch (e) {
      toast('err', (e as Error).message);
    } finally {
      setSavingMap(false);
    }
  };

  const unmilestoned = deliverables.filter((d) => !d.milestone_id && d.status !== 'dropped').length;
  const mapCandidates = rows
    .filter((r) => !mapQuery || `${r.ref} ${r.statement ?? ''}`.toLowerCase().includes(mapQuery.toLowerCase()))
    .slice(0, 60);
  const mappingDirty =
    !!selected &&
    (mapIds.length !== selected.requirements.length ||
      mapIds.some((id) => !selected.requirements.some((r) => r.id === id)));

  return (
    <>
      <div className="sheet sheet--clip reg-gridwrap">
      <div className="reg-toolbar">
        <span className="text-sm text-ink-3">
          <span className="voice-figure">{deliverables.length}</span> {t('deliverables')} ·{' '}
          <span className="voice-figure">{deliverables.filter((d) => !d.requirements.length).length}</span> {t('unjustified')} ·{' '}
          <span className="voice-figure">{unmilestoned}</span> {t('without a milestone')}
        </span>
        <button className="btn btn-accent ml-auto" onClick={() => setCreating(true)}>
          <Plus className="icon-sm" /> {t('New deliverable')}
        </button>
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 76 }}>{t('Ref')}</th>
            <th style={{ width: '24%' }}>{t('Name')}</th>
            <th>{t('Definition of done')}</th>
            <th style={{ width: '18%' }}>{t('Requirements')}</th>
            <th style={{ width: 168 }}>{t('Milestone')}</th>
            <th style={{ width: 132 }}>{t('Status')}</th>
            <th style={{ width: 56, textAlign: 'right' }}>{t('ACs')}</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {deliverables.map((d) => (
            <tr key={d.id} className="cursor-pointer" onClick={() => openDeliverable(d)}>
              <td className="src-ref">{d.ref}</td>
              <td className="font-medium text-ink-1">{d.name}</td>
              {/* the definition of done is the record: the sentence that decides acceptance */}
              <td style={{ maxWidth: 460 }} title={d.definition_of_done ?? undefined}>
                <div className="voice-record" style={clamp2}>
                  {d.definition_of_done || <span className="n-none">{t('— not defined')}</span>}
                </div>
              </td>
              <td>
                <div className="flex flex-wrap gap-4">
                  {d.requirements.slice(0, 6).map((r) => {
                    const full = reqOf.get(r.id);
                    return (
                      <ReqBadge
                        key={r.id}
                        reqRef={r.ref}
                        meta={[full ? t(full.type) : null, t(r.priority === 'wont' ? "won't" : r.priority), t(r.status)]
                          .filter(Boolean).join(' · ')}
                        statement={r.statement}
                        rationale={full?.rationale ?? null}
                      />
                    );
                  })}
                  {d.requirements.length > 6 ? <span className="badge">+{d.requirements.length - 6}</span> : null}
                  {!d.requirements.length ? (
                    <span className="badge badge-warn" title={t('No requirement — unjustified work (blocks G2)')}>
                      {t('Unjustified')}
                    </span>
                  ) : null}
                </div>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  className="form-select"
                  style={{ width: '100%' }}
                  value={d.milestone_id ?? ''}
                  title={milestones.length
                    ? t('The milestone this deliverable is invoiced and accepted against')
                    : t('No milestones yet — define them on Contracting → Payment schedule')}
                  onChange={(e) =>
                    void patchRow(
                      d,
                      { milestone_id: e.target.value ? Number(e.target.value) : null },
                      e.target.value ? t('{ref} moved to that milestone', { ref: d.ref }) : t('{ref} detached from its milestone', { ref: d.ref }),
                    )
                  }
                >
                  <option value="">
                    {milestones.length ? t('— no milestone —') : t('— none defined yet —')}
                  </option>
                  {milestones.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <select
                  className="form-select"
                  style={{ width: '100%' }}
                  value={d.status}
                  onChange={(e) => void patchRow(d, { status: e.target.value }, t('{ref} is {status}', { ref: d.ref, status: t(e.target.value.replace('_', ' ')) }))}
                >
                  {DELIVERABLE_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {t(s.label)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="voice-figure text-right">{d.ac_count}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <TrashButton
                  label={t('Delete {ref}', { ref: d.ref })}
                  onClick={() => setDeleteTarget({ entity: 'deliverable', id: d.id, ref: d.ref })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!deliverables.length ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Package className="icon-lg" />
          </div>
          <div className="empty-state-title">{t('No deliverables yet')}</div>
          <div className="empty-state-desc">
            {t('Deliverables turn requirements into funded work. Every must-have needs one before G2.')}
          </div>
        </div>
      ) : null}
      </div>

      {creating ? (
        <Modal
          title={t('New deliverable')}
          onClose={() => setCreating(false)}
          wide
          className="modal-compose"
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setCreating(false)}>
                {t('Cancel')}
              </button>
              <button className="btn btn-accent" disabled={name.trim().length < 2} onClick={create}>
                {t('Create')}
              </button>
            </>
          }
        >
          {/* Draft with the agent on the left, the form in the middle, and on the right THE
              DECISION THIS OVERLAY EXISTS FOR: which requirements the new deliverable funds —
              as readable cards (statement, priority, who already funds it), not a raster of
              faceless ref chips. The unfunded ones lead: they are why a deliverable gets
              created at all (G2). */}
          <div className="del-compose">
            <FieldChat
              engagementId={engagementId}
              title={t('New deliverable')}
              contextLines={
                newReqIds.length
                  ? ['It is meant to satisfy these requirements:',
                     ...rows.filter((r) => newReqIds.includes(r.id)).slice(0, 12)
                       .map((r) => `- ${r.ref} — ${(r.statement ?? '').replace(/\s+/g, ' ').slice(0, 300)}`)]
                  : undefined
              }
              fields={[
                { key: 'name', label: t('Name'), get: () => name, set: setName },
                { key: 'description', label: t('Description'), get: () => description, set: setDescription },
                { key: 'definition_of_done', label: t('Definition of done'), get: () => dod, set: setDod },
              ]}
            />
            <div className="del-compose-form">
              <div className="form-group">
                <label className="form-label">{t('Name')}</label>
                <input className="form-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('Description')}</label>
                <div className="refwrap">
                  <textarea ref={newDescRef} className="form-textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
                  <RefSuggest taRef={newDescRef} options={refOptions} />
                </div>
                <span className="rw-hintline">{t('# inserts a reference — DEL-xx, REQ-xxx')}</span>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Definition of done')}</label>
                <div className="refwrap">
                  <textarea
                    ref={newDodRef}
                    className="form-textarea"
                    rows={3}
                    placeholder={t('What must be true for this to count as delivered.')}
                    value={dod}
                    onChange={(e) => setDod(e.target.value)}
                  />
                  <RefSuggest taRef={newDodRef} options={refOptions} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Milestone')}</label>
                <select className="form-select" value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)}>
                  <option value="">{t('— no milestone —')}</option>
                  {milestones.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <aside className="req-pickrail" aria-label={t('Requirements this funds')}>
              <div className="req-pickhead">
                <span className="u-label">{t('Requirements this funds')}</span>
                <span className="voice-figure text-ink-3">{newReqIds.length}</span>
                <div className="search-wrapper" style={{ marginLeft: 'auto', maxWidth: 190 }}>
                  <input
                    className="search-input"
                    placeholder={t('Filter requirements…')}
                    value={reqQuery}
                    onChange={(e) => setReqQuery(e.target.value)}
                  />
                  {reqQuery ? <button className="search-clear" onClick={() => setReqQuery('')}>×</button> : null}
                </div>
              </div>
              <div className="req-pickcards">
                {rows
                  .filter((r) => !reqQuery || `${r.ref} ${r.statement ?? ''}`.toLowerCase().includes(reqQuery.toLowerCase()))
                  // Unfunded first — a requirement no deliverable covers is the reason this
                  // overlay is open; sort is stable, so register order holds within each half.
                  .sort((a, b) => Number(a.deliverables.length > 0) - Number(b.deliverables.length > 0))
                  .map((r) => {
                    const on = newReqIds.includes(r.id);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        className={`req-pickcard${on ? ' on' : ''}`}
                        onClick={() =>
                          setNewReqIds(on ? newReqIds.filter((x) => x !== r.id) : [...newReqIds, r.id])
                        }
                      >
                        <span className="rp-top">
                          <input type="checkbox" readOnly checked={on} tabIndex={-1} />
                          <span className="src-ref">{r.ref}</span>
                          <PriorityTag priority={r.priority} />
                          {!r.deliverables.length ? (
                            <span className="badge badge-warn" title={t('No deliverable covers this yet (G2)')}>
                              {t('not funded yet')}
                            </span>
                          ) : null}
                        </span>
                        <span className="rp-stmt" title={r.statement ?? ''}>{r.statement}</span>
                        {r.deliverables.length ? (
                          <span className="rp-funded">
                            {t('already funded by')}{' '}
                            {r.deliverables.map((d) => d.ref).join(', ')}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                {!rows.length ? <span className="text-sm text-ink-3">{t('The register is empty.')}</span> : null}
              </div>
            </aside>
          </div>
        </Modal>
      ) : null}

      {selected ? (
        <Modal
          title={`${selected.ref} — ${selected.name}`}
          onClose={() => setSelected(null)}
          wide
          className="modal-compose"
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setSelected(null)}>
                {t('Close')}
              </button>
              <button className="btn btn-accent" disabled={!mappingDirty || savingMap} onClick={() => void saveMapping()}>
                {savingMap ? <span className="spinner spinner-sm" /> : null} {t('Save mapping ({n})', { n: mapIds.length })}
              </button>
            </>
          }
        >
          {/* The workbench's shape here too: draft with the agent on the left, the record's
              fields on the right — takes go through the same blur-save patches the operator's
              own typing uses. */}
          <div className="del-compose">
            <FieldChat
              engagementId={engagementId}
              title={`${selected.ref} — ${selected.name}`}
              contextLines={
                selected.requirements.length
                  ? ['It satisfies these requirements:',
                     ...selected.requirements.slice(0, 12)
                       .map((r) => `- ${r.ref} — ${(r.statement ?? '').replace(/\s+/g, ' ').slice(0, 300)}`)]
                  : undefined
              }
              fields={[
                { key: 'name', label: t('Name'), get: () => selected.name,
                  set: (txt) => void patchRow(selected, { name: txt }, t('{ref} renamed', { ref: selected.ref })) },
                { key: 'description', label: t('Description'), get: () => selected.description ?? '',
                  set: (txt) => void patchRow(selected, { description: txt }, t('{ref} description saved', { ref: selected.ref })) },
                { key: 'definition_of_done', label: t('Definition of done'), get: () => selected.definition_of_done ?? '',
                  set: (txt) => void patchRow(selected, { definition_of_done: txt }, t('{ref} definition of done saved', { ref: selected.ref })) },
              ]}
            />
            <div className="del-compose-form">
          <div className="flex gap-12 mb-16">
            <div className="form-group flex-1 mb-0">
              <label className="form-label">{t('Milestone')}</label>
              <select
                className="form-select"
                value={selected.milestone_id ?? ''}
                onChange={(e) =>
                  void patchRow(
                    selected,
                    { milestone_id: e.target.value ? Number(e.target.value) : null },
                    e.target.value ? t('{ref} moved to that milestone', { ref: selected.ref }) : t('{ref} detached from its milestone', { ref: selected.ref }),
                  )
                }
              >
                <option value="">{t('— no milestone —')}</option>
                {milestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group flex-1 mb-0">
              <label className="form-label">{t('Status')}</label>
              <select
                className="form-select"
                value={selected.status}
                onChange={(e) => void patchRow(selected, { status: e.target.value }, t('{ref} is {status}', { ref: selected.ref, status: t(e.target.value.replace('_', ' ')) }))}
              >
                {DELIVERABLE_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {t(s.label)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* The texts are EDITABLE here — a deliverable is defined, rewritten and changed
              from this list, not only created. Saved on blur, like the workbench's own
              editors; the field proposer sits under each text field. */}
          <div className="form-group">
            <label className="form-label">{t('Name')}</label>
            <input
              key={`nm-${selected.id}-${selected.name}`}
              className="form-input"
              defaultValue={selected.name}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== selected.name) void patchRow(selected, { name: v }, t('{ref} renamed', { ref: selected.ref }));
              }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('Description')}</label>
            <div className="refwrap">
              <textarea
                key={`ds-${selected.id}-${selected.description ?? ''}`}
                ref={selDescRef}
                className="form-textarea"
                rows={2}
                defaultValue={selected.description ?? ''}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if ((v || null) !== (selected.description ?? null)) {
                    void patchRow(selected, { description: v }, t('{ref} description saved', { ref: selected.ref }));
                  }
                }}
              />
              <RefSuggest taRef={selDescRef} options={refOptions} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('Definition of done')}</label>
            {/* the record: the sentence acceptance is judged against */}
            <div className="refwrap">
              <textarea
                key={`dd-${selected.id}-${selected.definition_of_done ?? ''}`}
                ref={selDodRef}
                className="form-textarea voice-record"
                rows={3}
                placeholder={t('What must be true for this to count as delivered.')}
                defaultValue={selected.definition_of_done ?? ''}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if ((v || null) !== (selected.definition_of_done ?? null)) {
                    void patchRow(selected, { definition_of_done: v }, t('{ref} definition of done saved', { ref: selected.ref }));
                  }
                }}
              />
              <RefSuggest taRef={selDodRef} options={refOptions} />
            </div>
          </div>

          {/* WHO NAMES THIS REF — derived from the register's own prose, so an edit here is
              made with open eyes: these are the texts that may need to move along. */}
          {selRefs.length ? (
            <div className="form-group">
              <label className="form-label">
                {t('Referenced in')} <span className="voice-figure text-ink-3">{selRefs.length}</span>
              </label>
              <div className="flex flex-wrap gap-4">
                {selRefs.map((x, i) => (
                  <span
                    key={`${x.entity_type}-${x.entity_id}-${x.field}-${i}`}
                    className="badge mono"
                    title={`${x.label ?? ''}\n…${x.excerpt}…`}
                  >
                    {x.ref ?? (x.label?.trim() ? x.label.slice(0, 28) : x.entity_type.replace(/_/g, ' '))}
                  </span>
                ))}
              </div>
              <span className="rw-hintline">{t('texts that name {ref} — change it and they may need to move along', { ref: selected.ref })}</span>
            </div>
          ) : null}

          <div className="flex items-center gap-8 mb-8">
            <div className="section-label">{t('Mapped requirements')}</div>
            <span className="voice-figure text-xs text-ink-3">{mapIds.length}</span>
            {!mapIds.length ? (
              <span className="badge badge-warn" title={t('No requirement — unjustified work (blocks G2)')}>
                {t('Unjustified')}
              </span>
            ) : null}
            <div className="search-wrapper ml-auto" style={{ maxWidth: 260 }}>
              <input
                className="search-input"
                placeholder={t('Filter requirements…')}
                value={mapQuery}
                onChange={(e) => setMapQuery(e.target.value)}
              />
              {mapQuery ? (
                <button className="search-clear" onClick={() => setMapQuery('')}>
                  ×
                </button>
              ) : null}
            </div>
          </div>
          {/* Multi-select: mapping a deliverable is one decision over many requirements,
              not one modal round-trip per row. Save applies adds and removals together. */}
          <div className="border rounded-1 p-8 mb-8" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {mapCandidates.map((r) => {
              const on = mapIds.includes(r.id);
              return (
                <button
                  key={r.id}
                  className={`selectable ${on ? 'active' : ''}`}
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => setMapIds(on ? mapIds.filter((x) => x !== r.id) : [...mapIds, r.id])}
                >
                  <span className="flex items-center gap-8" style={{ width: '100%' }}>
                    <input type="checkbox" readOnly checked={on} tabIndex={-1} />
                    <span className="src-ref">{r.ref}</span>
                    <PriorityTag priority={r.priority} />
                    <span className="text-sm text-ink-2 flex-1 truncate">{r.statement}</span>
                  </span>
                </button>
              );
            })}
            {!mapCandidates.length ? <div className="p-12 text-sm text-ink-3">{t('No requirement matches that filter.')}</div> : null}
          </div>
          {mappingDirty ? (
            <div className="text-sm text-warn">{t('Unsaved mapping changes — Save mapping applies them.')}</div>
          ) : null}
            </div>
          </div>
        </Modal>
      ) : null}

      {deleteTarget ? (
        <DeleteModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(res) => {
            setDeleteTarget(null);
            if (selected?.id === res.id) setSelected(null);
            onReload();
            toast('ok', t('{ref} deleted', { ref: res.ref ?? t('Deliverable') }));
          }}
        />
      ) : null}
    </>
  );
}

// ───────────────────────── test cases tab ─────────────────────────

function TestCasesTab({
  engagementId,
  testCases,
  onReload,
}: {
  engagementId: number;
  testCases: TestCase[];
  onReload: () => void;
}) {
  const t = useT();
  const { tp } = useLocale();
  const toast = useToast();
  const [open, setOpen] = useState<TestCase | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const draftCount = testCases.filter((t) => t.status === 'draft').length;
  const readyCount = testCases.filter((t) => t.status === 'ready').length;
  const allSelected = testCases.length > 0 && selectedIds.length === testCases.length;

  const setStatus = async (tc: TestCase, status: string) => {
    try {
      await testCasesApi.setStatus(tc.id, status);
      if (open?.id === tc.id) setOpen({ ...open, status });
      onReload();
      toast('ok', t('{ref} is {status}', { ref: tc.ref, status: t(status) }));
    } catch (e) {
      toast('err', (e as Error).message);
    }
  };

  const bulkStatus = async (status: string) => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const res = await testCasesApi.bulkStatus(engagementId, selectedIds, status);
      setSelectedIds([]);
      onReload();
      if (res.skipped.length) toast('warn', t('{n} set to {status}, {skipped} skipped', { n: res.updated.length, status: t(status), skipped: res.skipped.length }));
      else toast('ok', tp(res.updated.length, '{n} test case now {status}', '{n} test cases now {status}', { status: t(status) }));
    } catch (e) {
      toast('err', (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="sheet sheet--clip reg-gridwrap">
      <div className="reg-toolbar">
        <span className="text-sm text-ink-3">
          <span className="voice-figure">{testCases.length}</span> {t('test cases')} ·{' '}
          <span className="voice-figure">{readyCount}</span> {t('ready for the client')} ·{' '}
          <span className="voice-figure">{draftCount}</span> {t('still draft')} ·{' '}
          <span className="voice-figure">{testCases.filter((tc) => !tc.acceptance_criteria.length).length}</span> {t('unlinked')}
        </span>
        {draftCount ? (
          <span className="badge badge-warn ml-8" title={t("Draft test cases are not listed in the client's UAT view")}>
            <AlertTriangle className="icon-sm" style={{ width: 11, height: 11 }} /> {t('{n} not visible to the client', { n: draftCount })}
          </span>
        ) : null}
      </div>

      {selectedIds.length ? (
        <div className="reg-toolbar" style={{ background: 'var(--accent-dim)' }}>
          <span className="text-sm font-medium">
            <span className="voice-figure">{selectedIds.length}</span> {t('selected')}
          </span>
          <div className="flex gap-8 items-center ml-auto">
            <button className="btn btn-sm btn-accent" disabled={busy} onClick={() => void bulkStatus('ready')}>
              <CheckCheck className="icon-sm" /> {t('Mark ready')}
            </button>
            <button className="btn btn-sm" disabled={busy} onClick={() => void bulkStatus('draft')}>
              {t('Back to draft')}
            </button>
            <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => void bulkStatus('dropped')}>
              {t('Drop')}
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setSelectedIds([])}>
              {t('Clear')}
            </button>
          </div>
        </div>
      ) : null}

      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 32 }}>
              <input
                type="checkbox"
                title={t('Select all')}
                checked={allSelected}
                onChange={(e) => setSelectedIds(e.target.checked ? testCases.map((tc) => tc.id) : [])}
              />
            </th>
            <th style={{ width: 80 }}>{t('Ref')}</th>
            <th style={{ width: '30%' }}>{t('Title')}</th>
            <th style={{ width: 60, textAlign: 'right' }}>{t('Steps')}</th>
            <th>{t('Covers')}</th>
            <th style={{ width: 190 }}>{t('Status')}</th>
            <th style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {testCases.map((tc) => (
            <tr key={tc.id} className="cursor-pointer" onClick={() => setOpen(tc)}>
              <td onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(tc.id)}
                  onChange={(e) =>
                    setSelectedIds(e.target.checked ? [...selectedIds, tc.id] : selectedIds.filter((x) => x !== tc.id))
                  }
                />
              </td>
              <td className="mono">{tc.ref}</td>
              <td className="font-medium text-ink-1">
                <Tx type="test_case" id={tc.id} field="title" original={tc.title} as="span" />
              </td>
              <td className="voice-figure text-right">{(tc.steps ?? []).length}</td>
              <td>
                <div className="flex flex-wrap gap-4">
                  {tc.acceptance_criteria.map((ac) => (
                    <span key={ac.id} className="badge mono" title={ac.statement}>
                      {ac.ref}
                    </span>
                  ))}
                  {!tc.acceptance_criteria.length ? <span className="badge badge-warn">{t('No AC linked')}</span> : null}
                </div>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                {/* draft → ready is the only thing that makes a test case reachable by the
                    client in the portal UAT view, so it is a control, not a read-out. */}
                <select
                  className="form-select"
                  style={{ width: '100%' }}
                  value={tc.status}
                  title={t("Only 'ready' test cases appear in the client's UAT list")}
                  onChange={(e) => void setStatus(tc, e.target.value)}
                >
                  {TC_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {t(s.label)}
                    </option>
                  ))}
                </select>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <TrashButton
                  label={t('Delete {ref}', { ref: tc.ref })}
                  onClick={() => setDeleteTarget({ entity: 'test_case', id: tc.id, ref: tc.ref })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!testCases.length ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FlaskConical className="icon-lg" />
          </div>
          <div className="empty-state-title">{t('No test cases')}</div>
          <div className="empty-state-desc">{t('Every acceptance criterion needs at least one test case before G3.')}</div>
        </div>
      ) : null}
      </div>

      {open ? (
        <Modal
          title={
            <>
              {open.ref} — <Tx type="test_case" id={open.id} field="title" original={open.title} as="span" />
            </>
          }
          onClose={() => setOpen(null)}
          wide
        >
          <div className="flex items-center gap-8 mb-16">
            <TcStatusBadge status={open.status} />
            <span className="text-sm text-ink-3">
              {open.status === 'ready'
                ? t('Listed in the client’s UAT view.')
                : t('Not visible to the client until it is ready.')}
            </span>
            <select
              className="form-select ml-auto"
              style={{ width: 'auto' }}
              value={open.status}
              onChange={(e) => void setStatus(open, e.target.value)}
            >
              {TC_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {t(s.label)}
                </option>
              ))}
            </select>
          </div>
          {/* the title is corrected here, beside the record it names, not up in the header */}
          <TxTools type="test_case" id={open.id} field="title" original={open.title} />
          <div className="flex gap-16 mb-16">
            <div className="flex-1">
              <div className="section-label mb-4">{t('Preconditions')}</div>
              {open.preconditions ? (
                <>
                  <Tx type="test_case" id={open.id} field="preconditions" original={open.preconditions}
                      className="text-sm text-ink-2" as="p" />
                  <TxTools type="test_case" id={open.id} field="preconditions" original={open.preconditions} />
                </>
              ) : (
                <p className="text-sm text-ink-2">—</p>
              )}
            </div>
            <div className="flex-1">
              <div className="section-label mb-4">{t('Test data')}</div>
              <p className="text-sm text-ink-2 mono">{open.test_data_ref || '—'}</p>
            </div>
          </div>
          <div className="section-label mb-8">{t('Steps')}</div>
          <table className="table table-compact mb-16">
            <thead>
              <tr>
                <th style={{ width: 32 }}>#</th>
                <th>{t('Action')}</th>
                <th>{t('Expected')}</th>
              </tr>
            </thead>
            <tbody>
              {(open.steps ?? []).map((s) => (
                <tr key={s.n}>
                  <td className="voice-figure">{s.n}</td>
                  <td>{s.action}</td>
                  <td className="text-ink-3">{s.expected}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {open.expected_result ? (
            <>
              <div className="section-label mb-4">{t('Overall expected result')}</div>
              <Tx type="test_case" id={open.id} field="expected_result" original={open.expected_result}
                  className="text-sm text-ink-2 mb-16" as="p" />
              <TxTools type="test_case" id={open.id} field="expected_result" original={open.expected_result} />
            </>
          ) : null}
          <div className="section-label mb-8">{t('Acceptance criteria covered')}</div>
          {open.acceptance_criteria.map((ac) => (
            <div key={ac.id} className="flex items-start gap-8 mb-4">
              <span className="src-ref">{ac.ref}</span>
              <Tx type="acceptance_criterion" id={ac.id} field="statement" original={ac.statement}
                  className="voice-record flex-1" as="span" />
              <span className="src-ref text-ink-3">{ac.requirement_ref}</span>
            </div>
          ))}
        </Modal>
      ) : null}

      {deleteTarget ? (
        <DeleteModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(res) => {
            setDeleteTarget(null);
            if (open?.id === res.id) setOpen(null);
            setSelectedIds((ids) => ids.filter((x) => x !== res.id));
            onReload();
            toast('ok', t('{ref} deleted', { ref: res.ref ?? t('Test case') }));
          }}
        />
      ) : null}
    </>
  );
}

// ───────────────────────── page ─────────────────────────
//
// THE REGISTER — the contract-bearing heart. Five zones, in this order:
//
//   masthead     the engagement, and ONE filled control: the binding act, recomputed per state
//   ledger       SCOPE · HELD · FUNDED · PROVEN · SOURCED — five cells, one sheet, invariant
//   obligation   the one clause the next gate still wants, then the rest on one line
//   contested    the questions the register cannot answer alone — and how each one is settled
//   the rows     tabs, a toolbar whose every chip carries its own count, and the grid
//
// FOUR RULES THIS PAGE IS BUILT ON:
//
//   1. THE LEDGER IS MADE OF DATA THE PAGE ALREADY FETCHED AND THREW AWAY. Not one request was
//      added for it. `coverage` is the only fetch pulled OUT of the Promise.all, because it is
//      the only one whose failure used to blank the entire page (F5.2).
//   2. A COVERAGE OUTAGE IS NOT A ZERO. SCOPE, HELD and SOURCED are derived from the grid rows
//      and survive it; FUNDED and PROVEN read `—` with the `unknown` tone and the ladder drops
//      the rungs it cannot judge. A missing check never renders as a passed check (B8).
//   3. THE PAGE NEVER PRINTS A GATE LETTER. `coverageReport` calls "every AC has a test case"
//      G3; `utils/gates.ts` calls "SOW signed" G3. Two different G3s must never appear on one
//      page, so this one names the OBLIGATION and lets Lifecycle own the letters.
//   4. EVERY FILTER PREDICTS ITS RESULT. The row list is unpaginated and entirely in memory, so
//      each chip counts itself against the OTHER active facets and a chip that would leave
//      nothing is disabled. A filter must never promise a view it cannot deliver.
//
// What search does NOT do: predict. `q` is a server-side trigram match, so the client cannot
// count it in advance — the search runs against the server and the empty state says as much
// rather than pretending it knew.

type Tab = 'requirements' | 'deliverables' | 'test-cases' | 'questions';

/** The five ledger cells, fixed set, fixed order — rendered in every state, including failure. */
type LedgerKey = 'SCOPE' | 'HELD' | 'FUNDED' | 'PROVEN' | 'SOURCED';

interface LedgerCell extends RailCell {
  key: LedgerKey;
  /** What the link under the cell says, and what it does. Null when the cell routes nowhere. */
  go: { label: string; act: () => void } | null;
}

/** The client-side facets. `q` and `document` are server filters and live outside this shape. */
interface Facets {
  priority: string;
  type: string;
  status: string;
  category: string;
  tags: string[];
  gap: string;
  /**
   * IN PLAY ONLY — the qualifier every gate predicate carries and the grid does not.
   *
   * The grid shows all 39 rows by default, descoped included, because "all statuses" must mean
   * all. The FUNDED cell counts the GATE's set, which is `status <> 'descoped'`. Without this
   * facet its route landed on 3 rows under a cell that said 2 — one page, two truths. Any route
   * that comes from a gate tally sets it, and the sheet says so in words.
   */
  live: boolean;
  /** Only rows named by an OPEN question — the client call's slice of the grid. */
  discussion: boolean;
}

const NO_FACETS: Facets = { priority: '', type: '', status: '', category: '', tags: [], gap: '', live: false, discussion: false };

/** The one predicate the grid and every chip count share — that is what makes a count honest. */
function passes(r: RegisterRow, f: Facets, inDiscussion?: Map<number, number>): boolean {
  if (f.live && r.status === 'descoped') return false;
  if (f.discussion && !inDiscussion?.has(r.id)) return false;
  if (f.status) {
    if (f.status === 'descoped' ? r.status !== 'descoped' : r.status !== f.status) return false;
  }
  if (f.priority && r.priority !== f.priority) return false;
  if (f.type && r.type !== f.type) return false;
  if (f.category && (r.category ?? '') !== f.category) return false;
  if (f.tags.length && !f.tags.every((t) => (r.tags ?? []).includes(t))) return false;
  if (f.gap === 'del' && !r.coverage.noDeliverable) return false;
  if (f.gap === 'ac' && !r.coverage.noAc) return false;
  if (f.gap === 'tc' && !r.coverage.acWithoutTc) return false;
  if (f.gap === 'ev' && r.evidence_count > 0) return false;
  return true;
}

const anyFacet = (f: Facets): boolean =>
  Boolean(f.priority || f.type || f.status || f.category || f.gap || f.tags.length || f.live || f.discussion);

/** Everything one load of this page produces. One poll, one payload, one refresh. */
interface RegisterPayload {
  rows: RegisterRow[];
  deliverables: Deliverable[];
  testCases: TestCase[];
  allAcs: AcceptanceCriterion[];
  milestones: Milestone[];
  /** null when GET /register/coverage did not answer — the rows are unaffected. */
  coverage: CoverageReport | null;
  coverageError: string | null;
  openQs: OpenQuestion[];
  openQsError: string | null;
  /** THE PENCIL LAYER: every assumption standing, so the call knows what it is about to settle. */
  assumptions: WorkingOverride[];
  scheme: CategorySchemeResponse | null;
  tagPool: string[] | null;
  tagPoolLoaded: boolean;
  documents: DocOption[];
  documentsError: string | null;
}

/** Statuses in which an operator stands behind the row — the gate engine's own set. */
const HELD_SET = new Set(['confirmed', 'baselined', 'delivered', 'accepted']);

export default function RegisterPage() {
  const t = useT();
  const { tp } = useLocale();
  const nav = useEngagement();
  const { current } = nav;
  const openRoute = useOpenRoute(nav);
  const routeParams = useRouteParams();
  const toast = useToast();
  const engagementId = current?.id ?? null;

  const [tab, setTab] = useState<Tab>('deliverables');
  /** The deliverables tab's two dresses: the dated roadmap (default) and the classic list. */
  const [delView, setDelView] = useState<'roadmap' | 'list'>('roadmap');
  /** Bumped when the planner held or filed proposals — the roadmap re-reads its ◇ marks. */
  const [plannerBump, setPlannerBump] = useState(0);
  const [busy, setBusy] = useState(false);

  // ── filters. All client-side except `q` and `document`, which the server owns. ──
  const [facets, setFacets] = useState<Facets>(NO_FACETS);
  const [query, setQuery] = useState('');
  const [documentFilter, setDocumentFilter] = useState<number | null>(null);
  /** The ids the server's trigram / document filter returned. null = no server filter active. */
  const [serverMatch, setServerMatch] = useState<Set<number> | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  // ── selection, panel, modals ──
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [detail, setDetail] = useState<RequirementDetail | null>(null);
  const [panelAt, setPanelAt] = useState<'evidence' | 'acs' | null>(null);
  const [panelAc, setPanelAc] = useState<string | null>(null);
  const [spanEvidence, setSpanEvidence] = useState<EvidenceRow | null>(null);
  const [tagPoolOpen, setTagPoolOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revising, setRevising] = useState(false);
  const [merging, setMerging] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [newTc, setNewTc] = useState(false);
  const [confirmDescope, setConfirmDescope] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [evidenceFailures, setEvidenceFailures] = useState<EvidenceFailure[]>([]);
  const [exportOpen, setExportOpen] = useState(false);

  // ── contested-zone working state ──
  const [resolvingQ, setResolvingQ] = useState<number | null>(null);
  const [resolutionText, setResolutionText] = useState('');
  /** The Contested zone's bucket in view; null = OPEN while anything is, SETTLED once nothing is. */
  const [qTab, setQTab] = useState<'open' | 'settled' | null>(null);
  /** The call stamp every decision below carries — name the call once, it rides every write. */
  const [callRef, setCallRef] = useState(() => t('Call on {date}', { date: new Date().toLocaleDateString('de-CH') }));
  /** The question standing in the call tab's viewer. */
  const [qCursor, setQCursor] = useState<number | null>(null);
  /** An adjudication in progress: which question, which side, and the statement that would land. */
  const [verdict, setVerdict] = useState<
    { qId: number; side: string; mode: 'revise' | 'create'; targetId: number | null; targetRef: string | null; statement: string } | null
  >(null);

  // ── one load, one poll (F5.3): numbers are never blanked because a refresh failed ──
  const load = useCallback(async (): Promise<RegisterPayload> => {
    if (!engagementId) throw new Error(t('No engagement selected.'));
    const softCoverage = registerApi
      .coverage(engagementId)
      .then((c) => ({ coverage: c as CoverageReport | null, coverageError: null as string | null }))
      // THE SPLIT (IMPLEMENTATION §1): coverage used to sit inside the Promise.all, so its
      // failure blanked the register itself. The rows come from GET /register and are unaffected.
      .catch((e: Error) => ({ coverage: null, coverageError: e.message }));

    const [reg, dels, tcs, acs, cov, ms, qs, sch, assumptions, pool, docs] = await Promise.all([
      registerApi.list(engagementId, { include_descoped: true }),
      deliverablesApi.list(engagementId),
      testCasesApi.list(engagementId),
      acsApi.list(engagementId),
      softCoverage,
      // Milestones belong to the contracting slice — a deliverable can still be edited without
      // them, so a failure degrades to "no milestones planned yet".
      contractingApi.milestones(engagementId).catch(() => ({ rows: [] as Milestone[] })),
      registerApi
        .openQuestions(engagementId)
        .then((r) => ({ openQs: r, openQsError: null as string | null }))
        .catch((e: Error) => ({ openQs: [] as OpenQuestion[], openQsError: e.message })),
      registerApi.categoryScheme(engagementId).catch(() => null),
      // The assumptions are a soft read: the client call works without them, it just cannot
      // offer to adopt what it cannot see.
      registerApi.overrides(engagementId).catch(() => [] as WorkingOverride[]),
      registerApi
        .tagPool(engagementId)
        .then((t) => ({ pool: t.pool, loaded: true }))
        .catch(() => ({ pool: null as string[] | null, loaded: false })),
      request<unknown>(`/documents?engagement_id=${engagementId}`)
        .then((res) => {
          const list = Array.isArray(res) ? res : ((res as { rows?: unknown[] })?.rows ?? []);
          return { documents: list as DocOption[], documentsError: null as string | null };
        })
        .catch((e: Error) => ({ documents: [] as DocOption[], documentsError: e.message })),
    ]);

    return {
      rows: reg.rows,
      deliverables: dels,
      testCases: tcs,
      allAcs: acs,
      milestones: ms.rows ?? [],
      coverage: cov.coverage,
      coverageError: cov.coverageError,
      openQs: qs.openQs,
      openQsError: qs.openQsError,
      assumptions,
      scheme: sch,
      tagPool: pool.pool,
      tagPoolLoaded: pool.loaded,
      documents: docs.documents,
      documentsError: docs.documentsError,
    };
  }, [engagementId, t]);

  const { data, loading, error, notice, refresh } = usePoll<RegisterPayload>(load, POLL_MS);

  const rows = data?.rows ?? [];
  /** What # can reference from the requirement forms — built once per data load. */
  const formRefOptions = useMemo<RefOption[]>(() => [
    ...(data?.deliverables ?? []).map((d) => ({ ref: d.ref, label: d.name })),
    ...(data?.rows ?? []).map((r) => ({ ref: r.ref, label: (r.statement ?? '').replace(/\s+/g, ' ').slice(0, 80) })),
  ], [data]);
  const coverage = data?.coverage ?? null;
  const coverageDown = Boolean(data && !data.coverage);
  const deliverables = data?.deliverables ?? [];
  const testCases = data?.testCases ?? [];
  const allAcs = data?.allAcs ?? [];
  const tagPool = data?.tagPool ?? null;
  const documents = useMemo(
    () => (data?.documents ?? []).filter((d) => ['ready', 'superseded'].includes(d.status)),
    [data],
  );

  // ── the server-side filters: trigram search and "cited by this document" ──
  useEffect(() => {
    if (!engagementId || (!query.trim() && documentFilter === null)) {
      setServerMatch(null);
      setSearchError(null);
      setSearching(false);
      return;
    }
    let alive = true;
    setSearching(true);
    const timer = setTimeout(() => {
      registerApi
        .list(engagementId, {
          q: query.trim() || undefined,
          document_id: documentFilter ?? undefined,
          include_descoped: true,
        })
        .then((res) => {
          if (!alive) return;
          setServerMatch(new Set(res.rows.map((r) => r.id)));
          setSearchError(null);
        })
        .catch((e: Error) => {
          if (!alive) return;
          // An empty result and a failed search are different facts: keep them different.
          setServerMatch(new Set());
          setSearchError(e.message);
        })
        .finally(() => alive && setSearching(false));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [engagementId, query, documentFilter]);

  // ── arrival intents (F1). One-shot; the page owns its state afterwards. ──
  const [pendingRef, setPendingRef] = useState<string | null>(null);
  // The facet intents all address the requirements grid, which is no longer the default tab
  // (the page opens on deliverables now) — so each of them pulls that tab forward, exactly as
  // an operator arriving on the old default experienced it.
  useDeepLink(routeParams, {
    status: (v) => { setFacets((f) => ({ ...f, status: v })); setTab('requirements'); },
    priority: (v) => { setFacets((f) => ({ ...f, priority: v })); setTab('requirements'); },
    // A `gap` intent only ever comes from a gate tally (lifecycle G2, or this page's own
    // ledger), and every gate predicate is in-play-only. Arriving without that qualifier would
    // land the operator on more rows than the number they clicked.
    gap: (v) => { setFacets((f) => ({ ...f, gap: v, live: true })); setTab('requirements'); },
    document: (v) => {
      const id = Number(v);
      if (Number.isInteger(id) && id > 0) { setDocumentFilter(id); setTab('requirements'); }
    },
    tab: (v) => {
      // `questions` is now a real tab — the call surface — not an anchor above the grid.
      if (v === 'deliverables' || v === 'test-cases' || v === 'questions') setTab(v as Tab);
      else if (v === 'requirements') setTab('requirements');
    },
    // `ref` and `ac` both open a row's panel; `ac` additionally names the criterion, which is
    // numbered inside its requirement (AC-014.2 → REQ-014, the schema's own rule).
    ref: (v) => {
      setPendingRef(v);
      setPanelAc(null);
    },
    ac: (v) => {
      setPendingRef(parentRef(v));
      setPanelAc(v);
    },
  });

  const openDetail = useCallback(
    async (id: number, at: 'evidence' | 'acs' | null = null) => {
      try {
        setPanelAt(at);
        setDetail(await registerApi.get(id));
      } catch (e) {
        toast('err', (e as Error).message);
      }
    },
    [toast],
  );

  // The rows may not have arrived when the link did; resolve the ref as soon as they do.
  useEffect(() => {
    if (!pendingRef || !rows.length) return;
    const row = rows.find((r) => r.ref === pendingRef);
    setPendingRef(null);
    if (!row) {
      toast('warn', t('{ref} is not in this register — it may have been merged away or deleted.', { ref: pendingRef }));
      return;
    }
    setFacets(NO_FACETS);
    setQuery('');
    void openDetail(row.id, panelAc ? 'acs' : 'evidence');
  }, [pendingRef, rows, panelAc, openDetail, toast, t]);

  // ── the ladder and its binding rung ──
  const ladder = useMemo(() => buildLadder({ rows, coverage }, { t, tp }), [rows, coverage, t, tp]);
  const binding = useMemo(() => bindingRung(ladder), [ladder]);
  const tal = useMemo(() => tally(rows), [rows]);

  /** Conditions the ladder could NOT compute. Named, never quietly dropped (B8). */
  const missingRungs = useMemo(
    () => ALL_RUNGS.filter((k) => !ladder.some((r) => r.key === k)),
    [ladder],
  );

  const scrollToRows = () =>
    requestAnimationFrame(() => document.getElementById('reg-rows')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));

  const applyFilter = (next: Partial<Facets>) => {
    setFacets({ ...NO_FACETS, ...next });
    setTab('requirements');
    scrollToRows();
  };

  const runRungAct = (rung: Rung) => {
    if (rung.act.kind === 'filter') applyFilter(rung.act.filter);
    else if (rung.act.kind === 'tab') setTab(rung.act.tab);
    else if (rung.act.kind === 'page') void openRoute(ROUTES.proposals({ engagement: engagementId }));
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast('err', (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** Submit path for the create/revise form: an evidence 422 lands on the offending rows. */
  const runForm = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      setEvidenceFailures([]);
    } catch (e) {
      if (e instanceof EvidenceInvalidError) {
        setEvidenceFailures(e.failures);
        toast('err', tp(e.failures.length, '{n} evidence span rejected — see the marked rows. Nothing was saved.', '{n} evidence spans rejected — see the marked rows. Nothing was saved.'));
      } else {
        toast('err', (e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const clearEvidenceFailure = (index: number) => setEvidenceFailures((f) => f.filter((x) => x.index !== index));

  const reloadDetail = useCallback(async () => {
    if (!detail) return;
    try {
      const fresh = await registerApi.get(detail.id);
      setDetail(fresh);
      await refresh();
    } catch (e) {
      toast('err', (e as Error).message);
    }
  }, [detail, refresh, toast]);

  /**
   * One row, one classification axis, through the same bulk endpoint the toolbar uses (one id).
   * The server refuses locked rows with the change-request message — surface it as the error it
   * is, never as a quiet success.
   */
  /** A Source chip's click: the span itself, fetched from the row's record. */
  const openSourceSpan = (rowId: number, docRef: string) =>
    run(async () => {
      const fresh = await registerApi.get(rowId);
      const ev = fresh.evidence.find((e) => e.document_ref === docRef) ?? fresh.evidence[0] ?? null;
      if (ev) setSpanEvidence(ev);
      else toast('warn', t('{ref} is cited without a stored span — open the row for the full record.', { ref: docRef }));
    });

  /** The way back from "out of scope": free before the first baseline, a change request after. */
  const restoreRow = (target: { id: number; ref: string }) =>
    run(async () => {
      const fresh = await registerApi.restore(target.id);
      if (detail?.id === target.id) setDetail(fresh);
      await refresh();
      toast('ok', t('{ref} is back in scope as proposed — confirm it once it is agreed', { ref: fresh.ref }));
    });

  const changeField = (
    target: { id: number; ref: string },
    field: 'type' | 'priority' | 'status' | 'category',
    value: string | null,
    currentValue: string | null,
  ) =>
    run(async () => {
      if (!engagementId || value === currentValue) return;
      const res = await registerApi.bulk(engagementId, [target.id], { [field]: value });
      if (res.skipped.length) toast('err', res.skipped[0].reason);
      else {
        const label =
          field === 'priority'
            ? t(PRIORITIES.find((p) => p.value === value)?.label ?? value ?? '')
            : field === 'type'
              ? t(typeLabel(value ?? ''))
              : field === 'status'
                ? t(REQUIREMENT_STATUS_LABEL[value ?? '']?.label ?? value ?? '')
                : (value ?? t('uncategorised'));
        toast('ok', t('{ref} {field} set to {label}', { ref: target.ref, field: t(field), label }));
      }
      if (detail?.id === target.id) setDetail(await registerApi.get(target.id));
      await refresh();
    });

  const bulkSet = (set: Record<string, string | null>, ids = selectedIds) =>
    run(async () => {
      if (!engagementId || !ids.length) return;
      const res = await registerApi.bulk(engagementId, ids, set);
      // The skipped list already carries a per-row reason: state the COUNT and the first reason,
      // not just one reason with no idea how many rows it stood for (F7.1).
      if (res.skipped.length)
        toast('warn', t('{updated} updated, {refused} refused — {ref} {reason}', { updated: res.updated.length, refused: res.skipped.length, ref: res.skipped[0].ref ?? '', reason: res.skipped[0].reason }).trim());
      else toast('ok', tp(res.updated.length, '{n} requirement updated', '{n} requirements updated'));
      setSelectedIds([]);
      await refresh();
    });

  /** ADD a tag to the selection — the axis 47 of the register's 115 mutations use. */
  const bulkAddTag = (tagName: string) =>
    run(async () => {
      if (!engagementId) return;
      const chosen = rows.filter((r) => selectedIds.includes(r.id));
      const res = await registerApi.addTag(engagementId, chosen, tagName);
      const cost = tp(res.calls, '{n} call — bulk takes tags as a replacement set, so one per resulting tag set', '{n} calls — bulk takes tags as a replacement set, so one per resulting tag set');
      // A row that already carries the tag is not written to at all — a no-op write is
      // still an audit row, and the operator should be told the difference.
      const already = res.already.length ? t(', {n} already carried it', { n: res.already.length }) : '';
      if (res.refused.length) toast('warn', t('{done} tagged, {refused} refused — {reason} · {cost}', { done: res.done.length, refused: res.refused.length, reason: res.refused[0].reason, cost }));
      else if (!res.done.length) toast('ok', t('Every selected row already carries {tag}. Nothing was written.', { tag: tagName }));
      else toast('ok', tp(res.done.length, '{n} row tagged {tag}{already} · {cost}', '{n} rows tagged {tag}{already} · {cost}', { tag: tagName, already, cost }));
      setSelectedIds([]);
      await refresh();
    });

  /** Bulk mapping: the register is where the operator SEES the unfunded rows. */
  const bulkMapToDeliverable = (deliverableId: number) =>
    run(async () => {
      const deliverable = deliverables.find((d) => d.id === deliverableId);
      const count = selectedIds.length;
      const fresh = await deliverablesApi.mapRequirements(deliverableId, selectedIds);
      setSelectedIds([]);
      await refresh();
      toast('ok', tp(count, '{n} requirement mapped to {ref}', '{n} requirements mapped to {ref}', { ref: deliverable?.ref ?? fresh.ref }));
    });

  const settleQuestion = async (id: number, qStatus: 'resolved' | 'dropped', resolution?: string) => {
    if (!engagementId) return;
    try {
      // Recording an answer or dropping a question both leave the record where it is, so the
      // assumptions that were waiting on it die with it — and the toast says how many.
      const out = await registerApi.updateOpenQuestion(id, engagementId, qStatus, resolution, {
        assumptions: 'revert', call_ref: stampText(),
      });
      setResolvingQ(null);
      setResolutionText('');
      await refresh();
      const base = qStatus === 'resolved'
        ? t('Resolution recorded — the agent reads it as a client decision on its next run')
        : t('Question dropped');
      toast('ok', base + revertedSuffix(out.assumptions_reverted));
    } catch (e) {
      toast('err', (e as Error).message);
    }
  };

  // ── the client call, point by point ────────────────────────────────────────
  // Each verb settles the question AND moves the register the way the call decided, in one
  // click, stamped with the call reference — the card leaves OPEN for SETTLED as you speak.

  /** The REQ a question names ("Refine REQ-012 with the client: …"), when it names exactly one. */
  const linkedRowOf = (q: OpenQuestion): RegisterRow | null => {
    const ref = refsIn(q.question, q.context).find((r) => r.startsWith('REQ-'));
    return ref ? rows.find((r) => r.ref === ref) ?? null : null;
  };
  const stampText = () => callRef.trim() || t('Call on {date}', { date: new Date().toLocaleDateString('de-CH') });
  const settleLast = () => { if (openQ.length <= 1) setQTab('settled'); };

  // ── THE PENCIL LAYER, on the call ──────────────────────────────────────────
  // Every verb below settles a question, and every question may have DATES pencilled against
  // it. Confirm and Refuse leave the record where it is, so those assumptions die (the server
  // reverts them and says how many). Amend moves the record, so the operator is offered the
  // one-click adoption — ticked by default, because having pencilled October and then agreed
  // October, retyping October is the kind of work this layer exists to remove.
  /** The assumptions tethered to one question. */
  const assumptionsOf = useCallback(
    (qId: number): WorkingOverride[] => (data?.assumptions ?? []).filter((o) => o.open_question_id === qId),
    [data],
  );
  /** What a toast may honestly add after a settlement. */
  const revertedSuffix = (n: number): string =>
    n ? ` · ${tp(n, '{n} assumption reverted', '{n} assumptions reverted')}` : '';

  /**
   * ADOPT — the assumptions become the record. The dates are written through the deliverable's
   * and the item's OWN PATCH routes, so the trail reads `deliverable.update` where a date
   * changed; only then does POST /overrides/adopt clear the pencil and record the adoption
   * under the call reference. Nothing here is a shortcut around the entity's own write path.
   */
  const adoptAssumptions = async (qId: number, list: WorkingOverride[]): Promise<number> => {
    if (!engagementId || !list.length) return 0;
    for (const o of list) {
      if (o.entity_type === 'deliverable') await deliverablesApi.update(o.entity_id, { [o.field]: o.value });
      else await deliverablesApi.updateItem(o.entity_id, { due_date: o.value });
    }
    const out = await registerApi.adoptOverrides(engagementId, qId, stampText());
    return out.adopted;
  };

  const callConfirm = (q: OpenQuestion) =>
    run(async () => {
      if (!engagementId) return;
      const row = linkedRowOf(q);
      if (row && row.status === 'draft' && !row.locked) {
        const res = await registerApi.bulk(engagementId, [row.id], { status: 'confirmed' });
        if (res.skipped.length) toast('warn', res.skipped[0].reason);
      }
      const out = await registerApi.updateOpenQuestion(q.id, engagementId, 'resolved',
        `Confirmed with the client — ${stampText()}${row ? ` · ${row.ref} stands as written` : ''}`,
        { assumptions: 'revert', call_ref: stampText() });
      await refresh();
      toast('ok', t('{ref} confirmed — {stamp}', { ref: q.ref ?? t('Question'), stamp: stampText() })
        + revertedSuffix(out.assumptions_reverted));
      settleLast();
    });

  const callRefuse = (q: OpenQuestion) =>
    run(async () => {
      if (!engagementId) return;
      const row = linkedRowOf(q);
      if (row && !row.locked && row.status !== 'descoped') {
        await registerApi.descope(row.id, `Refused by the client — ${stampText()}`);
      }
      // A refusal is a confirmation of the record by another route: nothing on it moves, so the
      // assumptions that were waiting on this question die exactly as they do on Confirm.
      const out = await registerApi.updateOpenQuestion(q.id, engagementId, 'resolved',
        `Refused by the client — ${stampText()}${row ? ` · ${row.ref} out of scope` : ''}`,
        { assumptions: 'revert', call_ref: stampText() });
      await refresh();
      toast('ok', (row
        ? t('{ref} refused — {row} out of scope', { ref: q.ref ?? t('Question'), row: row.ref })
        : t('{ref} refused', { ref: q.ref ?? t('Question') })) + revertedSuffix(out.assumptions_reverted));
      settleLast();
    });

  const callAmend = (q: OpenQuestion, statement: string, adopt: boolean) =>
    run(async () => {
      if (!engagementId) return;
      const row = linkedRowOf(q);
      if (!row) return;
      const fresh = await registerApi.revise(row.id, { statement, rationale: `Amended with the client — ${stampText()}` }, engagementId);
      if (fresh.status === 'draft' && !fresh.locked) {
        const res = await registerApi.bulk(engagementId, [row.id], { status: 'confirmed' });
        if (res.skipped.length) toast('warn', res.skipped[0].reason);
      }
      // The record CHANGED on this call, so the dates pencilled against the question are held
      // back rather than reverted — and adopted next, if the operator left the box ticked.
      const out = await registerApi.updateOpenQuestion(q.id, engagementId, 'resolved',
        `Amended and confirmed with the client — ${stampText()} · ${row.ref} rev ${fresh.revision_no}`,
        { assumptions: adopt ? 'hold' : 'revert', call_ref: stampText() });
      const adopted = adopt ? await adoptAssumptions(q.id, out.assumptions) : 0;
      await refresh();
      const tail = adopted
        ? ` · ${tp(adopted, '{n} assumption adopted', '{n} assumptions adopted')}`
        : revertedSuffix(out.assumptions_reverted);
      toast('ok', t('{ref} amended → rev {n} — {stamp}', { ref: row.ref, n: fresh.revision_no, stamp: stampText() }) + tail);
      settleLast();
    });

  /**
   * ADJUDICATION — the register mirror of the conflict-accept path in routes/agent.ts.
   *
   * A conflict is not a plain question: one side wins and the register CHANGES, or nobody wins
   * yet and it stays on the client-call agenda. With a target row the winner lands as a REVISION
   * of it (the losing side stays on the record, in the previous revision); with no target it
   * becomes a new DRAFT requirement. Both branches then resolve the question, naming the side.
   */
  const writeVerdict = () =>
    run(async () => {
      if (!engagementId || !verdict) return;
      const statement = verdict.statement.trim();
      if (!statement) throw new Error(t('The winning statement cannot be empty.'));
      // The rationale and the resolution are the RECORD, written in the register's own words;
      // only the toast speaks the operator's language.
      const rationale = `Conflict resolved by the operator in favour of position ${verdict.side}.`;
      let landedRef: string;
      let landedShown: string;
      if (verdict.mode === 'revise' && verdict.targetId) {
        const fresh = await registerApi.revise(verdict.targetId, { statement, rationale }, engagementId);
        landedRef = `${fresh.ref} rev ${fresh.revision_no}`;
        landedShown = t('{ref} rev {n}', { ref: fresh.ref, n: fresh.revision_no });
      } else {
        const created = await registerApi.create(engagementId, {
          statement,
          rationale: `${rationale} No row on the register carried this claim, so it enters as a draft.`,
          type: 'functional',
          priority: 'should',
        });
        landedRef = `${created.ref} (draft)`;
        landedShown = t('{ref} (draft)', { ref: created.ref });
      }
      await registerApi.updateOpenQuestion(
        verdict.qId,
        engagementId,
        'resolved',
        `Position ${verdict.side} wins — written to ${landedRef}.`,
      );
      setVerdict(null);
      await refresh();
      toast('ok', t('Position {side} wins — {ref}', { side: verdict.side, ref: landedShown }));
    });

  const runPass = () =>
    run(async () => {
      if (!engagementId) return;
      await agentApi.runPass(engagementId, 'test_cases');
      toast('ok', t('Test-case pass queued — the proposals land in the review queue'));
      await openRoute(ROUTES.agent({ engagement: engagementId }));
    });

  // ── the masthead's ONE filled control: the binding act, recomputed per state ──
  const primaryAct = () => {
    if (!binding?.primary) return;
    switch (binding.primary.kind) {
      case 'confirm-drafts':
        void bulkSet({ status: 'confirmed' }, tal.drafts.map((r) => r.id));
        break;
      case 'filter':
      case 'tab':
      case 'page':
        runRungAct(binding);
        break;
      case 'test-case-pass':
        void runPass();
        break;
    }
  };

  // ── the five ledger cells ──────────────────────────────────────────────────
  //
  // SCOPE, HELD and SOURCED are derived from the ROWS, so they stand while coverage is down.
  // FUNDED and PROVEN come from the roll-up and read `—` with the `unknown` tone when it did not
  // answer: a check that did not run is not a check that passed.
  /** Rows named by an OPEN question — "in discussion", each pointing at its question. */
  const discussionByRow = useMemo(() => {
    const m = new Map<number, number>();
    for (const q of data?.openQs ?? []) {
      if (q.status !== 'open') continue;
      const ref = refsIn(q.question, q.context).find((r) => r.startsWith('REQ-'));
      const row = ref ? rows.find((x) => x.ref === ref) : null;
      if (row && !m.has(row.id)) m.set(row.id, q.id);
    }
    return m;
  }, [data, rows]);

  const cells: LedgerCell[] = useMemo(() => {
    const unknown = (key: LedgerKey, label: string): LedgerCell => ({
      key,
      value: '—',
      unit: null,
      detail: t('the coverage roll-up did not answer'),
      tone: 'unknown',
      route: null,
      go: null,
    });
    const mustTotal = coverage ? Number(coverage.totals.must ?? 0) : 0;
    const unfunded = coverage?.must_requirements_without_deliverable ?? [];
    const acsTotal = coverage ? Number(coverage.totals.acceptance_criteria ?? 0) : 0;
    const unproven = coverage?.acs_without_test_case ?? [];
    const drafts = tal.drafts.length;
    const draftsTalking = tal.drafts.filter((r) => discussionByRow.has(r.id)).length;
    const unsourced = tal.unsourced.length;

    return [
      {
        key: 'SCOPE',
        value: String(tal.inPlay.length),
        unit: t('in play'),
        detail: t('{musts} must · {exclusions} · {descoped} descoped', {
          musts: tal.musts.length,
          exclusions: tp(tal.exclusions.length, '{n} exclusion', '{n} exclusions'),
          descoped: tal.descoped.length,
        }),
        tone: tal.inPlay.length ? 'ink' : 'nil',
        route: null,
        // The figure is what is IN PLAY; the link says plainly that it opens more than that.
        go: { label: t('Every row, descoped included'), act: () => applyFilter({}) },
      },
      {
        key: 'HELD',
        value: String(tal.held.length),
        unit: t('of {n}', { n: tal.inPlay.length }),
        detail: !tal.inPlay.length
          ? t('nothing on record yet')
          : drafts
            ? draftsTalking
              ? t('{drafts} still proposed ◇ · {talking} in discussion', { drafts, talking: draftsTalking })
              : t('{drafts} still proposed ◇', { drafts })
            : t('every row stands behind an operator'),
        tone: !tal.inPlay.length ? 'nil' : drafts ? 'warn' : 'ok',
        route: null,
        go: drafts
          ? { label: t('Show the proposed'), act: () => applyFilter({ status: 'draft' }) }
          : { label: t('All rows'), act: () => applyFilter({}) },
      },
      coverageDown
        ? unknown('FUNDED', 'Funded')
        : {
            key: 'FUNDED',
            value: String(Math.max(mustTotal - unfunded.length, 0)),
            unit: t('of {n} must', { n: mustTotal }),
            detail: !mustTotal
              ? t('no MUST requirement on record')
              : unfunded.length
                ? t('{n} unfunded — blocks the estimate', { n: unfunded.length })
                : t('every must maps to a deliverable'),
            tone: !mustTotal ? 'nil' : unfunded.length ? 'warn' : 'ok',
            route: null,
            go: unfunded.length
              ? { label: t('Show the unfunded'), act: () => applyFilter({ gap: 'del', priority: 'must', live: true }) }
              : { label: t('Deliverables'), act: () => setTab('deliverables') },
          },
      coverageDown
        ? unknown('PROVEN', 'Proven')
        : {
            key: 'PROVEN',
            value: String(Math.max(acsTotal - unproven.length, 0)),
            unit: t('of {n} criteria', { n: acsTotal }),
            detail: !acsTotal
              ? t('no acceptance criterion on record')
              : unproven.length
                ? tp(unproven.length, '{n} criterion with no test case', '{n} criteria with no test case')
                : t('every criterion is testable'),
            tone: !acsTotal ? 'nil' : unproven.length ? 'warn' : 'ok',
            route: null,
            go: { label: t('Test cases'), act: () => setTab('test-cases') },
          },
      {
        key: 'SOURCED',
        value: String(tal.sourced.length),
        unit: t('of {n}', { n: tal.inPlay.length }),
        detail: !tal.inPlay.length
          ? t('nothing on record yet')
          : unsourced
            ? t('{n} cannot say why they say this (P2)', { n: unsourced })
            : t('every row cites a document'),
        tone: !tal.inPlay.length ? 'nil' : unsourced ? 'warn' : 'ok',
        route: null,
        go: unsourced
          ? { label: t('Show the uncited'), act: () => applyFilter({ gap: 'ev', live: true }) }
          // The by-document view of the register is a real missing organ (IMPLEMENTATION §9):
          // the `document_id` filter exists and the view does not. Until it is built this cell
          // routes to the rows it is about, and says nothing it cannot show.
          : { label: t('All rows'), act: () => applyFilter({}) },
      },
    ];
  }, [tal, coverage, coverageDown, discussionByRow, t, tp]);

  // ── the grid's rows, and the counts every chip predicts with ────────────────
  const visibleRows = useMemo(
    () => rows.filter((r) => passes(r, facets, discussionByRow) && (serverMatch === null || serverMatch.has(r.id))),
    [rows, facets, serverMatch, discussionByRow],
  );

  const countIf = useCallback(
    (override: Partial<Facets>) =>
      rows.filter((r) => passes(r, { ...facets, ...override }, discussionByRow) && (serverMatch === null || serverMatch.has(r.id))).length,
    [rows, facets, serverMatch, discussionByRow],
  );

  /** Every tag in play: the agent's pool first, then ad-hoc tags already on rows. */
  const tagOptions = useMemo(() => {
    const out = [...(tagPool ?? [])];
    const seen = new Set(out);
    for (const r of rows) for (const tg of r.tags ?? []) if (!seen.has(tg)) { seen.add(tg); out.push(tg); }
    return out;
  }, [tagPool, rows]);

  const schemeSelectOptions = useMemo(
    () => (data?.scheme?.entries ?? []).map((e) => ({ value: e.name, label: e.name, title: e.description })),
    [data],
  );

  /** Scheme names first, then whatever ad-hoc categories the register already carries. */
  const categoryOptions = useMemo(() => {
    const names = (data?.scheme?.entries ?? []).map((e) => e.name);
    const seen = new Set(names.map((n) => n.toLowerCase()));
    for (const r of rows) {
      const c = (r.category ?? '').trim();
      if (c && !seen.has(c.toLowerCase())) { seen.add(c.toLowerCase()); names.push(c); }
    }
    return names;
  }, [data, rows]);

  const openQ = (data?.openQs ?? []).filter((q) => q.status === 'open');
  const settledQ = (data?.openQs ?? []).filter((q) => q.status !== 'open');
  const activeQTab: 'open' | 'settled' = qTab ?? (openQ.length ? 'open' : 'settled');
  /** The rail's list under the bucket in view, and the question standing in the viewer. */
  const bucketQs = activeQTab === 'open' ? openQ : settledQ;
  useEffect(() => {
    if (!bucketQs.length) { setQCursor(null); return; }
    if (qCursor === null || !bucketQs.some((q) => q.id === qCursor)) setQCursor(bucketQs[0].id);
  }, [bucketQs, qCursor]);
  const stoodQ = bucketQs.find((q) => q.id === qCursor) ?? null;
  const documentRef = documentFilter ? (documents.find((d) => d.id === documentFilter)?.ref ?? `#${documentFilter}`) : null;
  const readyDocuments = (data?.documents ?? []).filter((d) => d.status === 'ready').length;

  /** Open a ref the way the surface that owns it expects. Refs that are not here say so. */
  const openRef = (ref: string) => {
    const kind = refKind(ref);
    if (kind === 'DOC') {
      void openRoute(ROUTES.documents({ engagement: engagementId }));
      return;
    }
    if (kind === 'DEL') {
      setTab('deliverables');
      return;
    }
    if (kind === 'CR') {
      void openRoute(ROUTES.contracting({ engagement: engagementId, tab: 'changes' }));
      return;
    }
    const base = parentRef(ref);
    const row = rows.find((r) => r.ref === base || r.ref === ref);
    if (!row) {
      toast('warn', t('{ref} is not in this register — nothing on this page carries that ref.', { ref }));
      return;
    }
    setFacets(NO_FACETS);
    setQuery('');
    setPanelAc(kind === 'AC' ? ref : null);
    void openDetail(row.id, kind === 'AC' ? 'acs' : 'evidence');
  };

  const saveTagPool = (poolTags: string[] | null) =>
    run(async () => {
      if (!engagementId) return;
      await registerApi.saveTagPool(engagementId, poolTags);
      setTagPoolOpen(false);
      await refresh();
      toast('ok', poolTags ? t('Tag pool saved — the agent may tag from it on its next run') : t('Tag pool cleared — the agent proposes no tags'));
    });

  // ── no engagement: nothing on this page means anything ──
  if (!current || !engagementId) {
    return (
      <div className="page reg-page">
        <header className="mast"><div className="mast-top">
          <div className="mast-main"><h1>{t('Requirements register')}</h1></div>
        </div></header>
        <EmptyState
          kind="empty"
          title={t('No engagement selected')}
          detail={t('A register belongs to one engagement. Pick one from the switcher to open its requirements.')}
        />
      </div>
    );
  }

  // ── the rows themselves did not load: that is not an empty register ──
  if (!data && !loading) {
    return (
      <div className="page reg-page">
        <header className="mast"><div className="mast-top">
          <div className="mast-main">
            <h1>{t('Requirements register')}</h1>
            <div className="mast-sub"><span className="src-ref">{current.code}</span></div>
          </div>
        </div></header>
        <EmptyState
          kind="error"
          title={t('The register did not answer')}
          detail={<><span className="src-ref">GET /api/register</span> {t('answered: “{error}”.', { error: error ?? t('no response') })}</>}
          onRetry={() => void refresh()}
        />
      </div>
    );
  }

  const allSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedIds.includes(r.id));
  const filtered = anyFacet(facets) || Boolean(query.trim()) || documentFilter !== null;

  return (
    <div className="page reg-page">
      {/* ── masthead ─────────────────────────────────────────────────────── */}
      <header className="mast">
      <div className="mast-top">
        <div className="mast-main">
          <h1>{t('Requirements register')}</h1>
          <div className="mast-sub">
            <span className="src-ref">{current.code}</span>
            <span>{current.name}</span>
            {current.client_name ? (<><span className="sep">·</span><span>{current.client_name}</span></>) : null}
            {notice ? (<><span className="sep">·</span><span className="text-warn">{notice}</span></>) : null}
          </div>
        </div>
        <div className="mast-verbs">
          {/* THE ONE FILLED CONTROL is the binding act, not the once-a-quarter act:
              requirement.create fired 21 times in this product's life; status and tag
              updates fired 99. An empty register's call to action belongs to its empty
              state, not up here — two filled controls must never be on screen at once. */}
          {rows.length && binding?.primary ? (
            <button
              className="btn btn-accent"
              disabled={busy}
              title={t(binding.why)}
              onClick={primaryAct}
            >
              {t(binding.primary.label)}
            </button>
          ) : null}
          <button className="btn btn-ghost" disabled={busy} onClick={() => setCreating(true)}>
            <Plus className="icon-sm" /> {t('New requirement')}
          </button>
          <div className="reg-menu">
            <button className="btn btn-ghost" onClick={() => setExportOpen((v) => !v)} aria-expanded={exportOpen}>
              <Download className="icon-sm" /> {t('Export ▾')}
            </button>
            {exportOpen ? (
              <div className="reg-menu-list" onMouseLeave={() => setExportOpen(false)}>
                <a href={exportsApi.registerCsvUrl(engagementId)} onClick={() => setExportOpen(false)}>{t('Register CSV')}</a>
                <a href={exportsApi.traceabilityCsvUrl(engagementId)} onClick={() => setExportOpen(false)}>{t('Traceability CSV')}</a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      </header>

      {/* ── the ledger: five cells, one sheet, riding the masthead's 2px rule ── */}
      <div className="instr">
        <div className="rail">
          {cells.map((cell) => (
            <div key={cell.key} className={`fld reg-lcell${binding && bindingCellKey(binding.key) === cell.key ? ' reg-lcell--bind' : ''}`}>
              <span className="fl u-label">{t(cell.key)}</span>
              <span className={`fv voice-figure reg-lfig--${cell.tone}`} title={cell.tone === 'unknown' ? t('The coverage roll-up did not answer — this is not a zero.') : undefined}>
                {cell.value}
                {cell.unit ? <span className="u">{cell.unit}</span> : null}
              </span>
              <span className={`fd${cell.tone === 'warn' ? ' reg-ldet--short' : ''}`}>{cell.detail}</span>
              <span className="reg-lroute">
                {cell.go ? (
                  <button type="button" className="reg-link" onClick={cell.go.act}>{cell.go.label} →</button>
                ) : (
                  <span className="reg-lnone">—</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Coverage did not answer. Say it once, name what is unaffected, and do not guess. */}
      {coverageDown ? (
        <div className="reg-degraded">
          <b>{t('Coverage did not answer.')}</b> {t('The rows below are the register itself and are unaffected — they come from')}{' '}
          <span className="src-ref">GET /api/register</span>. {t('The ledger reports what it can count from those rows and marks the rest')}{' '}
          <span className="n-unknown">{t('unknown')}</span> {t('rather than printing a confident zero.')}{' '}
          {t('The roll-up said: “{error}”.', { error: data?.coverageError ?? t('no response') })}
          <button type="button" className="reg-link ml-8" onClick={() => void refresh()}>{t('Try again')}</button>
        </div>
      ) : null}

      {/* ── ZONE · the obligation ────────────────────────────────────────── */}
      <section className="zone" id="reg-obligation">
        <div className="zone-head">
          <span className="u-label">{t('The obligation')}</span>
          <span className="zone-note zone-note--end">{t('what the next gate still wants from this register — one clause, then the rest')}</span>
        </div>
        {binding ? (
          <div className="reg-oblig">
            <div className="reg-oblig-line">
              <span className="reg-oblig-cond">{t(binding.title)}</span>
              {/* the coverage endpoint's own finished sentence, rendered unedited (B7) */}
              <span className="reg-oblig-det">{t(binding.why)}</span>
              <button type="button" className="reg-link reg-oblig-go" onClick={() => runRungAct(binding)}>
                {t(binding.routeLabel)} →
              </button>
            </div>
            {ladder.filter((r) => !r.met).length > 1 ? (
              <div className="reg-oblig-next">
                {t('then')} ·{' '}
                {ladder
                  .filter((r) => !r.met && r.key !== binding.key)
                  .map((r) => {
                    const title = t(r.title);
                    return `${title.charAt(0).toLowerCase()}${title.slice(1)} — ${t(r.why)}`;
                  })
                  .join(' · ')}
              </div>
            ) : null}
            {missingRungs.length ? (
              // Say WHICH conditions are absent. A ladder that silently loses rungs reads as a
              // shorter list of obligations, which is the one thing it must never do.
              <div className="reg-oblig-next">
                {tp(
                  missingRungs.length,
                  '{n} condition is not on this ladder at all — {list} needs the coverage roll-up, and it did not answer.',
                  '{n} conditions are not on this ladder at all — {list} need the coverage roll-up, and it did not answer.',
                  { list: missingRungs.map((k) => t(RUNG_NOUN[k])).join(', ') },
                )}
              </div>
            ) : null}
          </div>
        ) : (
          <div className={`reg-oblig${missingRungs.length ? '' : ' reg-oblig--clear'}`}>
            <div className="reg-oblig-line">
              <span className="reg-oblig-cond">
                {missingRungs.length ? t('Nothing that could be checked is outstanding') : t('Nothing is outstanding')}
              </span>
              <span className="reg-oblig-det">
                {missingRungs.length
                  ? t('every condition this page could compute is met — {list} could not be computed', { list: missingRungs.map((k) => t(RUNG_NOUN[k])).join(', ') })
                  : t('every requirement held, funded, proven and sourced')}
              </span>
            </div>
            {missingRungs.length ? (
              <div className="reg-oblig-next">
                {t('The coverage roll-up did not answer, so this is not a clear verdict — it is the verdict on what answered.')}
              </div>
            ) : null}
          </div>
        )}
      </section>


      {/* ── ZONE · the rows ──────────────────────────────────────────────── */}
      <section className="zone" id="reg-rows">
        <div className="zone-head">
          <span className="u-label">{t('The rows')}</span>
          <span className="zone-note zone-note--end">
            {tp(rows.length, '{n} requirement', '{n} requirements')} · {tp(deliverables.length, '{n} deliverable', '{n} deliverables')} ·{' '}
            {tp(allAcs.length, '{n} criterion', '{n} criteria')} · {tp(testCases.length, '{n} test case', '{n} test cases')} {t('— the register itself')}
          </span>
        </div>

        <div className="tabs reg-tabs">
          {([
            ['requirements', 'Requirements', rows.length],
            ['deliverables', 'Deliverables', deliverables.length],
            ['test-cases', 'Test cases', testCases.length],
            ['questions', 'Client call', openQ.length],
          ] as [Tab, string, number][]).map(([key, label, n]) => (
            <button
              key={key}
              className={`tab ${tab === key ? 'active' : ''}`}
              aria-selected={tab === key}
              onClick={() => setTab(key)}
            >
              {t(label)} <span className="reg-tabn voice-figure">{n}</span>
            </button>
          ))}
        </div>

        {tab === 'requirements' ? (
          <div className="sheet sheet--clip reg-gridwrap">
            {/* ── the toolbar: the controls of THIS sheet, inside it ── */}
            <div className="reg-toolbar">
              <button
                className={`chip${!facets.priority && !facets.status && !facets.gap && !facets.type ? ' active' : ''}`}
                onClick={() => setFacets({ ...NO_FACETS, category: facets.category, tags: facets.tags })}
              >
                {t('All')}<span className="reg-n voice-figure">{countIf({ priority: '', status: '', gap: '', type: '' })}</span>
              </button>
              <span className="reg-sep" />
              {PRIORITIES.map((p) => (
                <FacetChip
                  key={p.value}
                  label={t(p.label)}
                  // The chip counts every ROW in the grid, descoped included, because that is
                  // what clicking it would show. SCOPE above counts only what is IN PLAY. Two
                  // different questions, so they are allowed to differ — and they say which.
                  title={t('Rows weighted {weight}, including any that are out of scope. The SCOPE cell counts only rows in play.', { weight: t(p.value === 'wont' ? "won't" : p.value) })}
                  n={countIf({ priority: p.value })}
                  on={facets.priority === p.value}
                  onClick={() => setFacets((f) => ({ ...f, priority: f.priority === p.value ? '' : p.value }))}
                />
              ))}
              <span className="reg-sep" />
              <FacetChip
                label={t('Proposed ◇')}
                title={t('Rows nobody has stood behind yet')}
                n={countIf({ status: 'draft' })}
                on={facets.status === 'draft'}
                onClick={() => setFacets((f) => ({ ...f, status: f.status === 'draft' ? '' : 'draft' }))}
              />
              <FacetChip
                label={t('In discussion')}
                title={t('Rows named by an OPEN question on the client call — decide them on the call, then confirm')}
                n={countIf({ discussion: true })}
                on={facets.discussion}
                onClick={() => setFacets((f) => ({ ...f, discussion: !f.discussion }))}
              />
              <FacetChip
                label={t('Exclusions')}
                title={t('What the contract says is NOT included — first-class scope, not an absence')}
                n={countIf({ type: 'out_of_scope' })}
                on={facets.type === 'out_of_scope'}
                onClick={() => setFacets((f) => ({ ...f, type: f.type === 'out_of_scope' ? '' : 'out_of_scope' }))}
              />
              <FacetChip
                label={t('Out of scope')}
                title={t('Descoped rows — kept with every revision and every piece of evidence (P4)')}
                n={countIf({ status: 'descoped' })}
                on={facets.status === 'descoped'}
                onClick={() => setFacets((f) => ({ ...f, status: f.status === 'descoped' ? '' : 'descoped' }))}
              />
              <span className="reg-sep" />
              <FacetChip
                label={t('No deliverable')}
                title={t("Every row with no deliverable, at any weight. FUNDED above counts only the MUSTs, because that is the gate's own predicate.")}
                n={countIf({ gap: 'del' })}
                on={facets.gap === 'del'}
                onClick={() => setFacets((f) => ({ ...f, gap: f.gap === 'del' ? '' : 'del' }))}
              />
              <FacetChip
                label={t('No criterion')}
                title={t('Rows with no acceptance criterion — nothing to test them against')}
                n={countIf({ gap: 'ac' })}
                on={facets.gap === 'ac'}
                onClick={() => setFacets((f) => ({ ...f, gap: f.gap === 'ac' ? '' : 'ac' }))}
              />
              <FacetChip
                label={t('No test case')}
                title={t('Rows carrying at least one criterion with no test case. PROVEN above counts CRITERIA, not rows — different units, both labelled.')}
                n={countIf({ gap: 'tc' })}
                on={facets.gap === 'tc'}
                onClick={() => setFacets((f) => ({ ...f, gap: f.gap === 'tc' ? '' : 'tc' }))}
              />
              <FacetChip
                label={t('No evidence')}
                title={t("Rows that cannot answer 'why does it say this?' (P2)")}
                n={countIf({ gap: 'ev' })}
                on={facets.gap === 'ev'}
                onClick={() => setFacets((f) => ({ ...f, gap: f.gap === 'ev' ? '' : 'ev' }))}
              />
              <div className="reg-search">
                <input
                  type="text"
                  placeholder={t('Search statements…')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query ? <button className="clr" onClick={() => setQuery('')}>×</button> : null}
              </div>
            </div>

            {/* the second control row: the axes that left the ROWS and became filters */}
            <div className="reg-toolbar reg-toolbar--2">
              <span className="u-label">{t('Tags')}</span>
              {tagOptions.length ? (
                tagOptions.map((tg) => (
                  <FacetChip
                    key={tg}
                    mono
                    label={tg}
                    title={tagPool?.includes(tg) ? t('Only rows tagged {tag}', { tag: tg }) : t("Only rows tagged {tag} (not in the agent's pool)", { tag: tg })}
                    n={countIf({ tags: [...new Set([...facets.tags, tg])] })}
                    on={facets.tags.includes(tg)}
                    onClick={() =>
                      setFacets((f) => ({ ...f, tags: f.tags.includes(tg) ? f.tags.filter((x) => x !== tg) : [...f.tags, tg] }))
                    }
                  />
                ))
              ) : (
                <span className="n-none text-xs">{t('— no tag on any row')}</span>
              )}
              <span className="reg-sep" />
              <select
                className="form-select"
                style={{ width: 'auto' }}
                value={facets.category}
                title={data?.scheme?.entries.find((e) => e.name === facets.category)?.description ?? t('Filter by (universal) category')}
                onChange={(e) => setFacets((f) => ({ ...f, category: e.target.value }))}
              >
                <option value="">{t('All categories ({n})', { n: countIf({ category: '' }) })}</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c} title={data?.scheme?.entries.find((e) => e.name === c)?.description}>
                    {c} ({countIf({ category: c })})
                  </option>
                ))}
              </select>
              <button
                className="btn btn-sm btn-ghost ml-auto"
                title={t("Edit this engagement's tag pool — the only tags the agent may attach to proposals")}
                disabled={!data?.tagPoolLoaded}
                onClick={() => setTagPoolOpen(true)}
              >
                <Tags className="icon-sm" /> {t('Tag pool')}
              </button>
            </div>

            {/* a server-side filter is a fact about the view: state it, and offer the exit */}
            {facets.live ? (
              <div className="reg-note">
                {t('Rows in play only — the descoped are hidden, because that is the qualifier the gate tally you arrived from carries.')}{' '}
                <button className="reg-link" onClick={() => setFacets((f) => ({ ...f, live: false }))}>
                  {t('Include the descoped')}
                </button>
              </div>
            ) : null}
            {documentRef || query.trim() ? (
              <div className="reg-note">
                {documentRef ? (
                  <>
                    {t('Only rows citing')} <span className="src-ref">{documentRef}</span>.{' '}
                    <button className="reg-link" onClick={() => setDocumentFilter(null)}>{t('Show every document')}</button>
                  </>
                ) : null}
                {query.trim() ? (
                  <>
                    {documentRef ? ' · ' : ''}
                    {searching
                      ? t('searching the statements…')
                      : searchError
                        ? t('the search failed: “{error}”', { error: searchError })
                        : tp(serverMatch?.size ?? 0, '{n} statement matched “{q}” on the server (trigram + ILIKE over current revisions)', '{n} statements matched “{q}” on the server (trigram + ILIKE over current revisions)', { q: query.trim() })}
                  </>
                ) : null}
              </div>
            ) : null}

            {/* the selection bar — the bulk axes, including the one it never had */}
            {selectedIds.length ? (
              <div className="reg-selbar">
                <span className="reg-selcnt"><span className="voice-figure">{selectedIds.length}</span> {t('selected')}</span>
                <div className="reg-selverbs">
                  <select
                    className="form-select" style={{ width: 'auto' }} value=""
                    disabled={busy || !tagOptions.length}
                    title={tagOptions.length ? t('Add one tag to every selected row') : t('No tag exists yet — set the pool first')}
                    onChange={(e) => { if (e.target.value) void bulkAddTag(e.target.value); }}
                  >
                    <option value="">{t('Add tag…')}</option>
                    {tagOptions.map((tg) => <option key={tg} value={tg}>{tg}</option>)}
                  </select>
                  <select
                    className="form-select" style={{ width: 'auto' }} value=""
                    disabled={busy || !deliverables.length}
                    title={deliverables.length ? t('Map the selected requirements to a deliverable') : t('No deliverable to map to yet')}
                    onChange={(e) => { if (e.target.value) void bulkMapToDeliverable(Number(e.target.value)); }}
                  >
                    <option value="">{t('Map to deliverable…')}</option>
                    {deliverables.map((d) => <option key={d.id} value={d.id}>{d.ref} — {d.name}</option>)}
                  </select>
                  <select
                    className="form-select" style={{ width: 'auto' }} value="" disabled={busy}
                    onChange={(e) => { if (e.target.value) void bulkSet({ priority: e.target.value }); }}
                  >
                    <option value="">{t('Set weight…')}</option>
                    {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{t(p.label)}</option>)}
                  </select>
                  <button className="btn btn-sm btn-accent" disabled={busy} onClick={() => void bulkSet({ status: 'confirmed' })}>
                    <Check className="icon-sm" /> {t('Confirm')}
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setSelectedIds([])}>{t('Clear')}</button>
                  {/* one loud thing per zone: Delete leaves the bar for the overflow */}
                  <details className="reg-menu reg-menu--inline">
                    <summary className="btn btn-sm btn-ghost">{t('More ▾')}</summary>
                    <div className="reg-menu-list">
                      {/* the other direction of the bar's Confirm — locked rows are reported back, not touched */}
                      <button disabled={busy} onClick={() => void bulkSet({ status: 'draft' })}>
                        {tp(selectedIds.length, 'Back to proposed ◇ — {n} row', 'Back to proposed ◇ — {n} rows')}
                      </button>
                      <button className="danger" disabled={busy} onClick={() => setBulkDeleting(true)}>
                        {tp(selectedIds.length, 'Delete {n} row permanently…', 'Delete {n} rows permanently…')}
                      </button>
                    </div>
                  </details>
                </div>
              </div>
            ) : null}

            {/* ── the grid ── */}
            {!rows.length ? (
              <EmptyState
                kind="empty"
                title={t('Nothing is on record for {code}', { code: current.code })}
                detail={t('A register is built two ways: the agent proposes from the documents already ingested, or you write the first requirement by hand. Neither is undone — a rejected proposal keeps its reason.')}
              >
                {readyDocuments ? (
                  <button className="btn btn-accent btn-sm" onClick={() => void openRoute(ROUTES.agent({ engagement: engagementId }))}>
                    {tp(readyDocuments, 'Run extraction on {n} ready document', 'Run extraction on {n} ready documents')}
                  </button>
                ) : null}
                <button className="btn btn-sm" onClick={() => setCreating(true)}>{t('Write one by hand')}</button>
              </EmptyState>
            ) : !visibleRows.length ? (
              <EmptyState
                kind="no-match"
                title={t('No row matches')}
                detail={
                  query.trim()
                    ? t('Every chip above carries the count it would leave you with, so this state is unreachable by clicking. It is reachable by typing: the search runs on the server and cannot be predicted here.')
                    : t('Every chip carries its own count, so this should not have been reachable — the rows may have changed under the filter since it was set.')
                }
                onClear={() => { setFacets(NO_FACETS); setQuery(''); setDocumentFilter(null); }}
                clearLabel={t('Clear every filter')}
              />
            ) : (
              <>
                <table className="tbl reg-grid">
                  <thead>
                    <tr>
                      <th className="reg-c-sel">
                        <input
                          type="checkbox"
                          title={t('Select every visible row')}
                          checked={allSelected}
                          onChange={(e) => setSelectedIds(e.target.checked ? visibleRows.map((r) => r.id) : [])}
                        />
                      </th>
                      <th className="reg-c-ref">{t('Ref')}</th>
                      <th>{t('Statement')}</th>
                      <th className="reg-c-src">{t('Source')}</th>
                      <th className="reg-c-w">{t('Weight')}</th>
                      <th className="reg-c-cov">{t('Cover')} <span className="reg-covkey">{t('D A T')}</span></th>
                      <th className="reg-c-ev">{t('Evid')}</th>
                      <th className="reg-c-act">{t('Status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <RegisterGridRow
                        key={row.id}
                        row={row}
                        selected={selectedIds.includes(row.id)}
                        open={detail?.id === row.id}
                        busy={busy}
                        onToggle={(on) => setSelectedIds((ids) => (on ? [...ids, row.id] : ids.filter((x) => x !== row.id)))}
                        onOpen={(at) => void openDetail(row.id, at)}
                        onConfirm={() => void changeField(row, 'status', 'confirmed', row.status)}
                        onUnconfirm={() => void changeField(row, 'status', 'draft', row.status)}
                        // the descope dialogue is built from the loaded detail — load THIS row first,
                        // then open it, so it can never show (or act on) the previously open row
                        onDescope={() => void openDetail(row.id, null).then(() => setConfirmDescope(true))}
                        onRestore={() => void restoreRow(row)}
                        discussionQId={discussionByRow.get(row.id) ?? null}
                        onOpenDiscussion={() => {
                          const qid = discussionByRow.get(row.id);
                          if (qid) { setQTab('open'); setQCursor(qid); setTab('questions'); }
                        }}
                        onOpenSource={(d) => void openSourceSpan(row.id, d)}
                      />
                    ))}
                  </tbody>
                </table>
                <div className="reg-gridfoot">
                  <span className="voice-figure">{visibleRows.length}</span> {t('of')} <span className="voice-figure">{rows.length}</span> {t('rows shown')}
                  {filtered ? t(' · filtered') : ''}
                  {filtered ? (
                    <button className="reg-link ml-8" onClick={() => { setFacets(NO_FACETS); setQuery(''); setDocumentFilter(null); }}>
                      {t('Clear')}
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </div>
        ) : null}

        {tab === 'questions' ? (
          <div className="sheet sheet--strong pq-panes">
            {/* ── the agenda rail: the buckets, then every question, mini ── */}
            <div className="pq-qrail">
              <div className="pq-qwrap">
                <div className="pq-qtabs" role="tablist" aria-label={t('The client-call agenda')}>
                  {(['open', 'settled'] as const).map((b) => (
                    <button
                      key={b}
                      type="button"
                      role="tab"
                      aria-selected={activeQTab === b}
                      className={`pq-qtab${activeQTab === b ? ' on' : ''}`}
                      onClick={() => setQTab(b)}
                    >
                      {t(b)}<span className="n voice-figure">({b === 'open' ? openQ.length : settledQ.length})</span>
                    </button>
                  ))}
                </div>
                <div className="pq-qscroll">
                  {!bucketQs.length ? (
                    <div className="pq-qempty">{activeQTab === 'open' ? t('nothing open — the agenda is clear') : t('nothing settled yet')}</div>
                  ) : null}
                  {bucketQs.map((q) => {
                    const linked = linkedRowOf(q);
                    const word = q.status === 'open'
                      ? (q.raised_by_kind === 'agent' ? 'agent' : q.raised_by_kind)
                      : q.status === 'dropped' ? 'dropped'
                        : /^Refused by the client/i.test(q.resolution ?? '') ? 'refused'
                          : /^(Confirmed|Amended)/i.test(q.resolution ?? '') ? 'confirmed' : 'answered';
                    return (
                      <button key={q.id} className={`pq-qrow${q.id === qCursor ? ' on' : ''}`} onClick={() => setQCursor(q.id)}>
                        <span
                          className={`att ${q.status === 'open' ? 'att--proposed' : (word === 'refused' || word === 'dropped' ? '' : 'att--held')}`}
                          aria-hidden="true"
                        />
                        <span>
                          <span className="qk">{q.ref ?? t('question')}</span>
                          <span className="qs"><QuestionTitle q={q} linked={linked} /></span>
                        </span>
                        <span className="pq-qattr">{linked ? <span>{linked.ref}</span> : null}</span>
                        <span className="pq-qflag mute">{t(word)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* ── the viewer: ONE question at full dress, and the stamp every decision carries ── */}
            <div className="pq-stand">
              <div className="pq-sthd">
                <span className="pq-stkind">{t('the client call')}</span>
                <span>
                  {stoodQ
                    ? t('{i} of {n} {bucket}', { i: bucketQs.findIndex((q) => q.id === stoodQ.id) + 1, n: bucketQs.length, bucket: t(activeQTab) })
                    : t('nothing {bucket}', { bucket: t(activeQTab) })}
                </span>
                <span className="reg-callref ml-auto">
                  <label className="u-label" htmlFor="reg-callref-in">{t('stamped as')}</label>
                  <input
                    id="reg-callref-in"
                    className="form-input"
                    value={callRef}
                    onChange={(e) => setCallRef(e.target.value)}
                    title={t('Every decision is recorded with this reference — name the call once, e.g. “Call with Mrs Meier · 23.08.2026”')}
                  />
                </span>
              </div>
              <div className="pq-stbody">
                {data?.openQsError ? (
                  <EmptyState
                    kind="error"
                    title={t('The open questions did not load')}
                    detail={<><span className="src-ref">GET /api/register/open-questions</span> {t('answered: “{error}”.', { error: data.openQsError })}</>}
                    onRetry={() => void refresh()}
                  />
                ) : stoodQ && stoodQ.status === 'open' ? (
                  <ContestedRow
                    q={stoodQ}
                    rows={rows}
                    busy={busy}
                    resolving={resolvingQ === stoodQ.id}
                    resolutionText={resolutionText}
                    verdict={verdict && verdict.qId === stoodQ.id ? verdict : null}
                    callRef={callRef}
                    linkedRow={linkedRowOf(stoodQ)}
                    onOpenSource={(d) => {
                      const linked = linkedRowOf(stoodQ);
                      if (linked) void openSourceSpan(linked.id, d);
                      else openRef(d);
                    }}
                    assumptions={assumptionsOf(stoodQ.id)}
                    onCallConfirm={() => void callConfirm(stoodQ)}
                    onCallRefuse={() => void callRefuse(stoodQ)}
                    onCallAmend={(st, adopt) => void callAmend(stoodQ, st, adopt)}
                    onOpenRef={openRef}
                    onStartResolve={() => { setResolvingQ(stoodQ.id); setResolutionText(''); }}
                    onCancelResolve={() => { setResolvingQ(null); setResolutionText(''); }}
                    onResolutionText={setResolutionText}
                    onRecord={() => void settleQuestion(stoodQ.id, 'resolved', `${resolutionText.trim()} — ${callRef.trim() || 'call'}`)}
                    onDrop={() => void settleQuestion(stoodQ.id, 'dropped', `Dropped — ${callRef.trim() || 'call'}`)}
                    onProposeVerdict={setVerdict}
                    onVerdictText={(s) => setVerdict((v) => (v ? { ...v, statement: s } : v))}
                    onCancelVerdict={() => setVerdict(null)}
                    onWriteVerdict={() => void writeVerdict()}
                  />
                ) : stoodQ ? (
                  (() => {
                    const word = stoodQ.status === 'dropped' ? 'dropped'
                      : /^Refused by the client/i.test(stoodQ.resolution ?? '') ? 'refused'
                        : /^(Confirmed|Amended)/i.test(stoodQ.resolution ?? '') ? 'confirmed' : 'answered';
                    return (
                      <div className="reg-q reg-q--done">
                        <div className="reg-q-head">
                          <span className={`att ${word === 'refused' || word === 'dropped' ? '' : 'att--held'}`} aria-hidden="true" />
                          {stoodQ.ref ? <span className="src-ref text-ink-3">{stoodQ.ref}</span> : null}
                          <span className="reg-q-title" title={stoodQ.question}><QuestionTitle q={stoodQ} linked={linkedRowOf(stoodQ)} tools /></span>
                          <span className={`reg-q-bucket${word === 'confirmed' ? '' : ' quiet'}`}>{t(word)}</span>
                        </div>
                        {stoodQ.context ? (
                          <div className="mt-8">
                            <Tx type="open_question" id={stoodQ.id} field="context" original={stoodQ.context}
                                className="text-sm text-ink-2" style={{ lineHeight: 1.55, whiteSpace: 'pre-wrap' }} as="div" />
                            <TxTools type="open_question" id={stoodQ.id} field="context" original={stoodQ.context} />
                          </div>
                        ) : null}
                        {stoodQ.resolution ? (
                          <div className="mt-8">
                            <Tx type="open_question" id={stoodQ.id} field="resolution" original={stoodQ.resolution}
                                className="voice-record" style={{ fontSize: 'var(--t-scan)' }} as="div" />
                            <TxTools type="open_question" id={stoodQ.id} field="resolution" original={stoodQ.resolution} />
                          </div>
                        ) : null}
                        <div className="text-xs text-ink-4 mt-8">{t('The agent reads settled questions as client decisions on its next run.')}</div>
                      </div>
                    );
                  })()
                ) : (
                  <EmptyState
                    kind="empty"
                    title={activeQTab === 'open' ? t('The agenda is clear.') : t('Nothing settled yet.')}
                    detail={activeQTab === 'open'
                      ? t('Hold a claim “· and put to the client”, or let the agent flag a question, and it lands here for the next call.')
                      : t('Decide an open question on the call and it lands here with its stamp.')}
                  />
                )}
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'deliverables' ? (
          <div className="tabs reg-tabs rw-subtabs">
            {(['roadmap', 'list'] as const).map((v) => (
              <button
                key={v}
                className={`tab ${delView === v ? 'active' : ''}`}
                aria-selected={delView === v}
                onClick={() => setDelView(v)}
              >
                {v === 'roadmap' ? t('Roadmap') : t('List')}{' '}
                <span className="reg-tabn voice-figure">
                  {v === 'roadmap' ? deliverables.filter((d) => d.due_date).length : deliverables.length}
                </span>
              </button>
            ))}
          </div>
        ) : null}
        {tab === 'deliverables' && delView === 'roadmap' ? (
          <RoadmapView
            engagementId={engagementId}
            deliverables={deliverables}
            rows={rows}
            onOpenSpan={(rid, d) => void openSourceSpan(rid, d)}
            onReload={() => void refresh()}
            refreshSignal={plannerBump}
          />
        ) : null}
        {tab === 'deliverables' && delView === 'list' ? (
          <DeliverablesTab
            engagementId={engagementId}
            deliverables={deliverables}
            milestones={data?.milestones ?? []}
            rows={rows}
            onReload={() => void refresh()}
          />
        ) : null}
        {/* ── the planner: the deliverables SECTION's flap-out, not the roadmap's — it judges
            the plan as a whole (coverage, overlap, sequencing, forgotten work) and proposes
            into the queue, so it stands on both sub-views, benches open or not. ────── */}
        {tab === 'deliverables' ? (
          <PlannerChat
            key={engagementId}   /* an engagement switch must remount — thread, runId and busy state are per-engagement */
            engagementId={engagementId}
            dels={deliverables}
            rows={rows}
            openQuestions={openQ.map((q) => ({ id: q.id, ref: q.ref, question: q.question }))}
            onChanged={() => { void refresh(); setPlannerBump((n) => n + 1); }}
            onProposalsFiled={() => setPlannerBump((n) => n + 1)}
          />
        ) : null}

        {tab === 'test-cases' ? (
          testCases.length ? (
            <TestCasesTab engagementId={engagementId} testCases={testCases} onReload={() => void refresh()} />
          ) : (
            <EmptyState
              kind="empty"
              title={t('No test case exists in {code}', { code: current.code })}
              detail={
                <>
                  {t('All')} <span className="voice-figure">{allAcs.length}</span>{' '}
                  {t('acceptance criteria are unproven. A test case is what turns a criterion into something the client can pass or fail at UAT.')}
                </>
              }
            >
              {allAcs.length ? (
                <button className="btn btn-accent btn-sm" disabled={busy} onClick={() => void runPass()}>
                  {tp(allAcs.length, 'Propose test cases for {n} criterion', 'Propose test cases for {n} criteria')}
                </button>
              ) : (
                <span className="text-xs text-ink-3">{t('A test case attaches to a criterion, and there is none yet.')}</span>
              )}
            </EmptyState>
          )
        ) : null}
      </section>

      {/* ── detail panel ── */}
      {detail ? (
        <RequirementPanel
          detail={detail}
          rows={rows}
          deliverables={deliverables}
          busy={busy}
          tagPool={tagPool}
          categories={schemeSelectOptions}
          initialSection={panelAt}
          highlightAc={panelAc}
          onOpenRef={openRef}
          onSaveTags={(tags) =>
            void run(async () => {
              // The bulk endpoint with one id IS the tag editor — full replacement array.
              const res = await registerApi.bulk(engagementId, [detail.id], { tags });
              if (res.skipped.length) {
                // The server's refusal, verbatim — a locked row must never read as saved.
                toast('err', res.skipped[0].reason);
                return;
              }
              setDetail(await registerApi.get(detail.id));
              await refresh();
              toast('ok', t('{ref} tags updated', { ref: detail.ref }));
            })
          }
          onSetField={(field, value) =>
            void changeField(
              detail,
              field,
              value,
              field === 'priority' ? detail.priority : field === 'type' ? detail.type : field === 'status' ? detail.status : detail.category,
            )
          }
          onClose={() => { setDetail(null); setPanelAt(null); setPanelAc(null); }}
          onReload={reloadDetail}
          onViewSpan={setSpanEvidence}
          onAction={(action) => {
            if (action === 'revise') setRevising(true);
            else if (action === 'merge') setMerging(true);
            else if (action === 'split') setSplitting(true);
            else if (action === 'descope') setConfirmDescope(true);
            else if (action === 'new-tc') setNewTc(true);
            else if (action === 'change-request')
              void openRoute(ROUTES.contracting({ engagement: engagementId, tab: 'changes' }));
            else if (action === 'delete')
              setDeleteTarget({ entity: 'requirement', id: detail.id, ref: detail.ref });
            else if (action === 'confirm')
              void run(async () => {
                const fresh = await registerApi.setStatus(detail.id, 'confirmed');
                setDetail(fresh);
                await refresh();
                toast('ok', t('{ref} confirmed', { ref: fresh.ref }));
              });
            else if (action === 'unconfirm')
              void run(async () => {
                const fresh = await registerApi.setStatus(detail.id, 'draft');
                setDetail(fresh);
                await refresh();
                toast('ok', t('{ref} is proposed again — confirm it once the client has agreed', { ref: fresh.ref }));
              });
            else if (action === 'restore')
              void run(async () => {
                const fresh = await registerApi.restore(detail.id);
                setDetail(fresh);
                await refresh();
                toast('ok', t('{ref} is back in scope as proposed — confirm it once it is agreed', { ref: fresh.ref }));
              });
          }}
        />
      ) : null}

      {spanEvidence ? <SpanModal evidence={spanEvidence} onClose={() => setSpanEvidence(null)} /> : null}

      {tagPoolOpen ? (
        <TagPoolModal
          pool={tagPool}
          busy={busy}
          onSave={(poolTags) => void saveTagPool(poolTags)}
          onClear={() => void saveTagPool(null)}
          onClose={() => setTagPoolOpen(false)}
        />
      ) : null}

      {creating ? (
        <RequirementFormModal
          title={t('New requirement')}
          submitLabel={t('Create requirement')}
          engagementId={engagementId}
          refOptions={formRefOptions}
          busy={busy}
          documents={documents}
          documentsError={data?.documentsError ?? null}
          evidenceFailures={evidenceFailures}
          categoryOptions={categoryOptions}
          onEvidenceEdited={clearEvidenceFailure}
          initial={{ statement: '', rationale: '', type: 'functional', priority: 'should', category: '', evidence: [] }}
          onClose={() => { setCreating(false); setEvidenceFailures([]); }}
          onSubmit={(values) =>
            void runForm(async () => {
              const created = await registerApi.create(engagementId, {
                statement: values.statement,
                rationale: values.rationale || undefined,
                type: values.type,
                priority: values.priority,
                category: values.category || undefined,
                evidence: values.evidence.filter((e) => e.document_id && e.quoted_span.trim()),
              });
              setCreating(false);
              await refresh();
              toast('ok', t('{ref} created', { ref: created.ref }));
            })
          }
        />
      ) : null}

      {revising && detail ? (
        <RequirementFormModal
          title={t('Revise {ref} → rev {n}', { ref: detail.ref, n: (detail.revision_no ?? 0) + 1 })}
          submitLabel={t('Save revision')}
          engagementId={engagementId}
          refOptions={formRefOptions}
          busy={busy}
          documents={documents}
          documentsError={data?.documentsError ?? null}
          evidenceFailures={evidenceFailures}
          categoryOptions={categoryOptions}
          onEvidenceEdited={clearEvidenceFailure}
          initial={{
            statement: detail.statement ?? '',
            rationale: detail.rationale ?? '',
            type: detail.type,
            priority: detail.priority,
            category: detail.category ?? '',
            evidence: [],
          }}
          onClose={() => { setRevising(false); setEvidenceFailures([]); }}
          onSubmit={(values) =>
            void runForm(async () => {
              const fresh = await registerApi.revise(
                detail.id,
                {
                  statement: values.statement,
                  rationale: values.rationale,
                  type: values.type,
                  priority: values.priority,
                  category: values.category,
                  evidence: values.evidence.filter((e) => e.document_id && e.quoted_span.trim()),
                },
                engagementId,
              );
              setDetail(fresh);
              setRevising(false);
              await refresh();
              toast('ok', t('{ref} now at rev {n}', { ref: fresh.ref, n: fresh.revision_no }));
            })
          }
        />
      ) : null}

      {merging && detail ? (
        <MergeModal
          source={detail}
          candidates={rows}
          busy={busy}
          onClose={() => setMerging(false)}
          onSubmit={(targetId, mergedStatement) =>
            void run(async () => {
              const res = await registerApi.merge(engagementId, detail.id, targetId, mergedStatement);
              setMerging(false);
              setDetail(res.target);
              await refresh();
              toast('ok', t('{source} merged into {target}', { source: res.source_ref, target: res.target_ref }));
            })
          }
        />
      ) : null}

      {splitting && detail ? (
        <SplitModal
          source={detail}
          busy={busy}
          onClose={() => setSplitting(false)}
          onSubmit={(parts) =>
            void run(async () => {
              const res = await registerApi.split(detail.id, parts);
              setSplitting(false);
              setDetail(null);
              await refresh();
              toast('ok', t('{ref} split into {parts}', { ref: res.original_ref, parts: res.parts.map((p) => p.ref).join(', ') }));
            })
          }
        />
      ) : null}

      {newTc && detail ? (
        <TestCaseModal
          acOptions={detail.acceptance_criteria.map((ac) => ({ id: ac.id, ref: ac.ref, statement: ac.statement }))}
          preselectedAcId={detail.acceptance_criteria[0]?.id}
          busy={busy}
          onClose={() => setNewTc(false)}
          onSubmit={(body) =>
            void run(async () => {
              const tc = await testCasesApi.create(engagementId, body);
              setNewTc(false);
              await reloadDetail();
              toast('ok', t('{ref} created', { ref: tc.ref }));
            })
          }
        />
      ) : null}

      <ConfirmModal
        open={confirmDescope && !!detail}
        title={t('Mark {ref} out of scope?', { ref: detail?.ref ?? '' })}
        confirmLabel={t('Mark out of scope')}
        danger
        onCancel={() => setConfirmDescope(false)}
        onConfirm={() =>
          void run(async () => {
            if (!detail) return;
            const fresh = await registerApi.descope(detail.id);
            setConfirmDescope(false);
            setDetail(fresh);
            await refresh();
            toast('ok', t('{ref} is out of scope', { ref: fresh.ref }));
          })
        }
      >
        <p className="text-sm text-ink-2">
          {t('The requirement is kept with every revision and every piece of evidence — its status becomes')}{' '}
          <em>{t('out of scope')}</em> {t('and it drops out of the default register view. Nothing is deleted (P4).')}
        </p>
      </ConfirmModal>

      {/* Hard delete. Deleting the requirement whose panel is open closes the panel — there is
          nothing left behind it to look at. */}
      {deleteTarget ? (
        <DeleteModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(res) => {
            setDeleteTarget(null);
            setSelectedIds((ids) => ids.filter((x) => x !== res.id));
            if (detail?.id === res.id) {
              setDetail(null);
              setRevising(false);
              setMerging(false);
              setSplitting(false);
              setNewTc(false);
              setConfirmDescope(false);
            }
            void refresh();
            toast('ok', t('{ref} deleted', { ref: res.ref ?? t('Requirement') }));
          }}
        />
      ) : null}

      {bulkDeleting ? (
        <BulkDeleteModal
          engagementId={engagementId}
          ids={selectedIds}
          onClose={() => setBulkDeleting(false)}
          onDone={(res) => {
            const goneIds = new Set(res.deleted.map((d) => d.id));
            // Keep the refused rows selected — they are the ones still needing a decision.
            setSelectedIds((ids) => ids.filter((id) => !goneIds.has(id)));
            if (detail && goneIds.has(detail.id)) setDetail(null);
            void refresh();
            if (res.refused.length) {
              toast('warn', t('{deleted} deleted, {refused} refused — see the list', { deleted: res.deleted.length, refused: res.refused.length }));
            } else {
              toast('ok', tp(res.deleted.length, '{n} requirement deleted', '{n} requirements deleted'));
            }
          }}
        />
      ) : null}
    </div>
  );
}

/** The ledger cell the binding rung caps. A condition maps to the figure it is about. */
function bindingCellKey(rung: RungKey): LedgerKey | null {
  if (rung === 'held') return 'HELD';
  if (rung === 'funded' || rung === 'justified') return 'FUNDED';
  if (rung === 'proven') return 'PROVEN';
  return null; // 'queue' is about the review queue, which is not a cell on this ledger
}

/** A filter chip that predicts its result. Zero and not selected means it cannot be clicked. */
function FacetChip({
  label, n, on, onClick, title, mono,
}: { label: string; n: number; on: boolean; onClick: () => void; title?: string; mono?: boolean }) {
  return (
    <button
      type="button"
      className={`chip${mono ? ' mono' : ''}${on ? ' active' : ''}`}
      aria-pressed={on}
      title={title}
      disabled={n === 0 && !on}
      onClick={onClick}
    >
      {label}
      <span className="reg-n voice-figure">{n}</span>
    </button>
  );
}

/**
 * One grid row. Seven columns, and not one of them is a control that fires on the wrong click:
 * the 78 per-row selects the old grid carried served axes the audit log says were edited four
 * and seven times in the register's whole life. Every one of them survives as a toolbar filter
 * and a panel field.
 */
function RegisterGridRow({
  row, selected, open, busy, onToggle, onOpen, onConfirm, onUnconfirm, onDescope, onRestore,
  discussionQId, onOpenDiscussion, onOpenSource,
}: {
  row: RegisterRow;
  selected: boolean;
  open: boolean;
  busy: boolean;
  onToggle: (on: boolean) => void;
  onOpen: (at: 'evidence' | 'acs' | null) => void;
  onConfirm: () => void;
  onUnconfirm: () => void;
  onDescope: () => void;
  onRestore: () => void;
  /** The OPEN question that names this row, when one does — the row is "in discussion". */
  discussionQId: number | null;
  onOpenDiscussion: () => void;
  onOpenSource: (docRef: string) => void;
}) {
  const t = useT();
  const { tp } = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);
  const held = HELD_SET.has(row.status);
  const na = row.type === 'out_of_scope' || row.status === 'descoped';
  // The status word is printed ONLY when it is abnormal: 36 rows saying "confirmed" is 36
  // repetitions of the default, and the claim mark already says who stands behind the statement.
  const word =
    row.merged_into_ref ? t('merged → {ref}', { ref: row.merged_into_ref })
      : row.status === 'draft' ? t('proposed')
        : row.status === 'descoped' ? t('descoped')
          : row.status === 'baselined' ? t('baselined · signed')
            : row.type === 'out_of_scope' ? t('exclusion')
              : '';
  const docs = row.source_document_refs ?? [];
  const hasD = row.deliverables.length > 0;
  const hasA = !row.coverage.noAc;
  const hasT = hasA && !row.coverage.acWithoutTc;

  return (
    <tr className={`${open ? 'reg-row--open ' : ''}${selected ? 'reg-row--sel' : ''}`} onClick={() => onOpen(null)}>
      <td className="reg-c-sel" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={(e) => onToggle(e.target.checked)} />
      </td>
      <td className="reg-c-ref">
        <div className="reg-refcell">{row.ref}</div>
        {word ? <div className={`reg-refstat${row.status === 'draft' ? ' reg-refstat--draft' : ''}`}>{word}</div> : null}
      </td>
      <td>
        <div className="flex items-center gap-8">
          <ClaimMark status={row.status} mergedInto={row.merged_into_ref} subject={row.ref} />
          <Tx type="requirement_revision" id={row.revision_id} field="statement"
              original={row.statement} className="voice-record" style={clamp2} as="div" />
        </div>
        {(row.tags ?? []).length || row.deliverables.length ? (
          <div className="reg-srcline">
            <TagChips tags={row.tags ?? []} small />
            {row.deliverables.length ? (
              <span className="src-ref reg-dels">→ {row.deliverables.map((d) => d.ref).join(' ')}</span>
            ) : null}
          </div>
        ) : null}
      </td>
      {/* SOURCE — the documents the row cites; the click shows the span itself */}
      <td className="reg-c-src" onClick={(e) => e.stopPropagation()}>
        {docs.length ? docs.map((d) => (
          <button key={d} type="button" className="reg-srcbtn src-ref" title={t('Show the span {ref} cites in {doc}', { ref: row.ref, doc: d })} onClick={() => onOpenSource(d)}>
            {d}
          </button>
        )) : <span className="n-none">—</span>}
      </td>
      {/* WEIGHT is weight: ink and case, never a hue (ATTEST LAW 2). */}
      <td className="reg-c-w">
        <span className={`reg-w reg-w--${row.priority}`}>{t(row.priority === 'wont' ? "won't" : row.priority)}</span>
      </td>
      {/* COVER: three FIXED slots — Deliverable, Acceptance criterion, Test case. An exclusion
          needs none of the three, so its pips are dotted: not-applicable is a third state. */}
      <td className="reg-c-cov">
        <span className="reg-cov">
          <span
            className={`reg-pip${na ? ' reg-pip--na' : hasD ? ' reg-pip--on' : ''}`}
            title={na ? t('not applicable to an exclusion') : hasD ? t('funded by {list}', { list: row.deliverables.map((d) => d.ref).join(', ') }) : t('no deliverable — unfunded work')}
          />
          <span
            className={`reg-pip${na ? ' reg-pip--na' : hasA ? ' reg-pip--on' : ''}`}
            title={na ? t('not applicable to an exclusion') : hasA ? tp(row.ac_count, '{n} acceptance criterion', '{n} acceptance criteria') : t('no acceptance criterion — nothing to test against')}
          />
          <span
            className={`reg-pip${na ? ' reg-pip--na' : hasT ? ' reg-pip--on' : ''}`}
            title={na ? t('not applicable to an exclusion') : hasT ? t('every criterion has a test case') : tp(row.ac_without_tc_count, '{n} criterion with no test case', '{n} criteria with no test case')}
          />
        </span>
      </td>
      {/* a count is arithmetic, not a claim — and here it is also the way in to the evidence */}
      <td className="reg-c-ev" onClick={(e) => e.stopPropagation()}>
        <button
          className={`reg-evfig voice-figure${row.evidence_count ? '' : ' reg-evfig--zero'}`}
          title={row.evidence_count ? t('Open {ref} at its evidence', { ref: row.ref }) : t('{ref} cites nothing — it cannot answer "why does it say this?"', { ref: row.ref })}
          onClick={() => onOpen('evidence')}
        >
          {row.evidence_count}
        </button>
      </td>
      <td className="reg-c-act" onClick={(e) => e.stopPropagation()}>
        {discussionQId !== null ? (
          <button type="button" className="reg-disc" title={t('On the client-call agenda — open the call at this question')} onClick={onOpenDiscussion}>
            {t('in discussion')}
          </button>
        ) : null}
        {row.status === 'draft' ? (
          /* still to confirm: the same round check, greyed — the click confirms */
          <button
            className="reg-ck"
            disabled={busy}
            title={discussionQId !== null
              ? t('{ref} is waiting on the call — decide it there, or confirm it here regardless', { ref: row.ref })
              : t('Confirm {ref} — stand behind it as real scope', { ref: row.ref })}
            onClick={onConfirm}
          >
            <span className="reg-ck-c" aria-hidden="true"><Check /></span>
            <span className="reg-ck-t">{t('confirm')}</span>
          </button>
        ) : row.status === 'confirmed' ? (
          /* Where Confirm was, the mark that it happened — and the way to change it. A quiet ✓
             (the default state across most rows must not shout); the click opens the two
             changes that are actually available pre-baseline, plus the way into the row. */
          <RowMenu
            label={t('{ref} is held — click to change that', { ref: row.ref })}
            trigger={<><span className="reg-ck-c reg-ck-c--on" aria-hidden="true"><Check /></span><span className="reg-ck-t">{t(REQUIREMENT_STATUS_LABEL.confirmed.word)}</span></>}
            triggerClassName="reg-ck"
            open={menuOpen}
            onOpenChange={setMenuOpen}
          >
            <button type="button" disabled={busy} onClick={() => { setMenuOpen(false); onUnconfirm(); }}>
              {t('Back to proposed ◇ — ask the client first')}
            </button>
            <button type="button" disabled={busy} onClick={() => { setMenuOpen(false); onDescope(); }}>
              {t('Mark out of scope…')}
            </button>
            <button type="button" onClick={() => { setMenuOpen(false); onOpen(null); }}>
              {t('Open the row — revise, merge, split')}
            </button>
          </RowMenu>
        ) : row.status === 'descoped' ? (
          row.locked ? (
            <span className="reg-rowlock" title={lockTitle(t, row.ref, row.status)}>{t('out of scope — CR only')}</span>
          ) : (
            /* descoped before any baseline: not signed scope, so the way back is one click */
            <RowMenu
              label={t('{ref} is out of scope — click to change that', { ref: row.ref })}
              trigger={t('out of scope')}
              triggerClassName="btn btn-sm btn-ghost reg-rowheld"
              open={menuOpen}
              onOpenChange={setMenuOpen}
            >
              <button type="button" disabled={busy} onClick={() => { setMenuOpen(false); onRestore(); }}>
                {t('Restore to scope — proposed ◇')}
              </button>
              <button type="button" onClick={() => { setMenuOpen(false); onOpen(null); }}>
                {t('Open the row')}
              </button>
            </RowMenu>
          )
        ) : row.baselined ? (
          <span className="reg-rowlock" title={lockTitle(t, row.ref, row.status)}>{t('signed — CR only')}</span>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * ONE CONTESTED QUESTION.
 *
 * A plain question is answered in free text. A CONFLICT is adjudicated: the agent's own
 * accept path (`routes/agent.ts`, `case 'conflict'`) lets an operator pick a side, and the
 * winner either REVISES the row it opposes or BECOMES a new draft requirement — and choosing
 * no side at all is the deliberate "log as open question" branch, which is what this row
 * already is. This renders all three, and each act names exactly what it writes.
 */
/**
 * A discuss-question's text carries the statement the card ALREADY shows in its REQ block —
 * one fact, said once (the full wording stays on hover and on the record).
 *
 * The question's headline, as a rendered node: the record's own words — translated, marked, the
 * original one click away — unless this is the synthesized "Refine ⟨ref⟩…" prompt, whose short
 * label is the console's own phrasing, not the record, and so carries no translation claim.
 * `tools` adds the correction affordance — only where this sits in a one-question detail view.
 */
function QuestionTitle({ q, linked, tools = false }: { q: OpenQuestion; linked: RegisterRow | null; tools?: boolean }) {
  const t = useT();
  if (linked && q.question.startsWith(`Refine ${linked.ref} with the client:`)) {
    return <>{t('Refine this requirement with the client')}</>;
  }
  return (
    <>
      <Tx type="open_question" id={q.id} field="question" original={q.question} as="span" />
      {tools ? <TxTools type="open_question" id={q.id} field="question" original={q.question} /> : null}
    </>
  );
}

function ContestedRow({
  q, rows, busy, resolving, resolutionText, verdict, assumptions,
  callRef, linkedRow, onOpenSource, onCallConfirm, onCallRefuse, onCallAmend,
  onOpenRef, onStartResolve, onCancelResolve, onResolutionText, onRecord, onDrop,
  onProposeVerdict, onVerdictText, onCancelVerdict, onWriteVerdict,
}: {
  q: OpenQuestion;
  rows: RegisterRow[];
  busy: boolean;
  resolving: boolean;
  resolutionText: string;
  verdict: { qId: number; side: string; mode: 'revise' | 'create'; targetId: number | null; targetRef: string | null; statement: string } | null;
  onOpenRef: (ref: string) => void;
  onStartResolve: () => void;
  onCancelResolve: () => void;
  onResolutionText: (s: string) => void;
  onRecord: () => void;
  onDrop: () => void;
  onProposeVerdict: (v: { qId: number; side: string; mode: 'revise' | 'create'; targetId: number | null; targetRef: string | null; statement: string }) => void;
  onVerdictText: (s: string) => void;
  onCancelVerdict: () => void;
  onWriteVerdict: () => void;
  callRef: string;
  linkedRow: RegisterRow | null;
  /** The source ref's click — the span overlay via the linked REQ's evidence, or the documents page. */
  onOpenSource: (docRef: string) => void;
  onCallConfirm: () => void;
  onCallRefuse: () => void;
  onCallAmend: (statement: string, adopt: boolean) => void;
  /** THE PENCIL LAYER: the dates pencilled against THIS question, waiting for it to settle. */
  assumptions: WorkingOverride[];
}) {
  const t = useT();
  const { fmtDate, tp } = useLocale();
  const [amending, setAmending] = useState(false);
  const [amendText, setAmendText] = useState('');
  /** Amend moves the record, so adopting what was pencilled is the expected answer — ticked. */
  const [adopt, setAdopt] = useState(true);
  const pos = positions(q.context);
  const isConflict = pos.length === 2;
  const anchor = isConflict ? anchorRow(pos, rows) : null;
  const hinges = hinge(q.context);

  // Refs the BODY already states — the ❝ source line under each quote, and the anchored row.
  // Printing them again at the right edge said one fact twice.
  const said = new Set<string>(pos.map((p) => p.doc));
  if (q.document_ref) said.add(q.document_ref);
  if (anchor) said.add(anchor.row.ref);
  if (linkedRow) said.add(linkedRow.ref);
  const chips = refsIn(q.question, q.context).filter((r) => !said.has(r));
  if (q.document_ref && !said.has(q.document_ref)) chips.push(q.document_ref);

  return (
    <div className="reg-q">
      {/* 1 · the identity line — who asks, under which ref, since when */}
      <div className="cq-head">
        {/* LAW 1 — who wrote it decides the face. A question the AGENT raised is the system
            speaking and is still only proposed (◇); one a human wrote is the record. */}
        {q.raised_by_kind === 'agent' ? (
          <span className="att att--proposed" aria-hidden="true" title={t('Raised by the agent — nobody has answered it')} />
        ) : null}
        {q.ref ? <span className="src-ref">{q.ref}</span> : null}
        <span className="cq-meta">
          {q.raised_by_kind === 'agent' ? t('the agent asks') : q.raised_by_kind === 'portal' ? t('asked by the client') : t('raised by the operator')}
          {' · '}{fmtDate(q.created_at)}
        </span>
        {chips.length ? (
          <span className="reg-q-chips">
            {chips.map((ref) => (
              <button key={ref} type="button" className="reg-refchip" title={t('Open {ref}', { ref })} onClick={() => onOpenRef(ref)}>
                {ref}
              </button>
            ))}
          </span>
        ) : null}
      </div>

      {/* 2 · the question itself — the thing being decided, at reading size, said ONCE:
          the statement lives in the REQ block below, not here (full wording on hover) */}
      <p className="cq-question" title={q.question}><QuestionTitle q={q} linked={linkedRow} tools /></p>

      <div className="reg-q-body">
        {/* 3 · the row the call decides — the REQ, in its own block, statement in the record voice */}
        {!isConflict && linkedRow ? (
          <div className="cq-req">
            <div className="cq-req-head">
              <span className="src-ref">{linkedRow.ref}</span>
              <span className="cq-meta">
                {t(linkedRow.priority === 'wont' ? "won't" : linkedRow.priority)} · {t(typeLabel(linkedRow.type).toLowerCase())} ·{' '}
                {t(REQUIREMENT_STATUS_LABEL[linkedRow.status]?.word ?? linkedRow.status)}
              </span>
              <button type="button" className="lnk ml-auto" onClick={() => onOpenRef(linkedRow.ref)}>
                {t('Open {ref} →', { ref: linkedRow.ref })}
              </button>
            </div>
            <p className="voice-record cq-req-s">{linkedRow.statement}</p>
          </div>
        ) : null}
        {isConflict ? (
          <>
            {anchor ? (
              <div className="reg-q-anchor">
                <div className="reg-q-anchor-k">
                  {t('Opposes')} <span className="src-ref">{anchor.row.ref}</span> {t('— the row on the register today')}
                </div>
                <div className="reg-q-anchor-s">{anchor.row.statement}</div>
                <div className="reg-q-anchor-m">
                  {t(anchor.row.priority === 'wont' ? "won't" : anchor.row.priority)} ·{' '}
                  {t(typeLabel(anchor.row.type).toLowerCase())} ·{' '}
                  {t(REQUIREMENT_STATUS_LABEL[anchor.row.status]?.word ?? anchor.row.status)} · {t('rev {n}', { n: anchor.row.revision_no })}{' '}
                  {t('— position {side} is what it already says, so the other side is the one that would change it.', { side: anchor.side })}{' '}
                  <span
                    className="n-unknown"
                    title={t('The live app stores this link as proposal.target_requirement_id; a question logged FROM a conflict does not carry it, so the row is matched on the statement.')}
                  >
                    {t('derived, not stored')}
                  </span>
                </div>
                <button type="button" className="reg-link mt-4" onClick={() => onOpenRef(anchor.row.ref)}>
                  {t('Open {ref} →', { ref: anchor.row.ref })}
                </button>
              </div>
            ) : null}

            {pos.map((p) => (
              <div key={p.side} className="reg-q-pos">
                <div>
                  <span className="u-label">{t('Position {side}', { side: p.side })}</span>{' '}
                  <span className="text-xs text-ink-3">{p.claim}</span>
                </div>
                <blockquote className="ev mt-8">{p.quote}</blockquote>
                <div className="ev-src">
                  <span className="att att--quoted" aria-hidden="true" />
                  <span className="src-ref">{p.doc}</span>
                  <span className="sep">·</span>
                  <span className="voice-figure">{p.date}</span>
                </div>
                {verdict && verdict.side === p.side ? (
                  <div className="reg-verdict">
                    <div className="text-xs text-ink-3 mb-4">
                      {verdict.mode === 'revise'
                        ? <>{t('This writes')} <span className="src-ref">{verdict.targetRef}</span> {t('a new revision. The losing side stays on the record, in the revision it replaces.')}</>
                        : <>{t('No row on the register carries this claim, so it enters as a new DRAFT requirement (functional · should). It will cite no evidence, so confirming it is refused until you revise it with a span.')}</>}
                    </div>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={verdict.statement}
                      onChange={(e) => onVerdictText(e.target.value)}
                    />
                    <div className="flex gap-8 mt-8">
                      <button className="btn btn-sm btn-accent" disabled={busy || !verdict.statement.trim()} onClick={onWriteVerdict}>
                        {verdict.mode === 'revise' ? t('Write it to {ref}', { ref: verdict.targetRef }) : t('Create the draft row')}
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={onCancelVerdict}>{t('Cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <div className="reg-q-win">
                    <button
                      type="button"
                      className="reg-act"
                      disabled={busy}
                      onClick={() =>
                        onProposeVerdict({
                          qId: q.id,
                          side: p.side,
                          mode: anchor ? 'revise' : 'create',
                          targetId: anchor?.row.id ?? null,
                          targetRef: anchor?.row.ref ?? null,
                          statement: p.claim,
                        })
                      }
                    >
                      {t('Position {side} wins →', { side: p.side })}
                    </button>
                    <span className="reg-q-what">
                      {anchor
                        ? t('revises {ref} with this side · the losing side stays on the record', { ref: anchor.row.ref })
                        : t('no row fits — this side becomes a new draft requirement (functional · should)')}
                    </span>
                    {anchor ? (
                      <button
                        type="button"
                        className="reg-act reg-act--quiet"
                        title={t("The accept path's other branch: with no target requirement the winning position becomes a new DRAFT requirement.")}
                        disabled={busy}
                        onClick={() =>
                          onProposeVerdict({
                            qId: q.id, side: p.side, mode: 'create',
                            targetId: null, targetRef: null, statement: p.claim,
                          })
                        }
                      >
                        {t('…or none fits — make it a new row')}
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
            {hinges ? <div className="reg-q-hinge">{t('Hinges on')} · {hinges}</div> : null}
          </>
        ) : q.context ? (
          // It did not parse as two positions, so it is NOT dressed as evidence. Prose is prose —
          // labelled as the background it is, and set apart from the question being decided.
          <div className="cq-why">
            <span className="u-label">{t('background')}</span>
            <Tx type="open_question" id={q.id} field="context" original={q.context} as="p" />
            <TxTools type="open_question" id={q.id} field="context" original={q.context} />
            {q.document_ref ? (
              <div className="ev-src">
                <span className="voice-system">{t('source')}</span>
                <button type="button" className="reg-srcbtn src-ref" title={t('Show the span behind this question in {ref}', { ref: q.document_ref })} onClick={() => onOpenSource(q.document_ref!)}>
                  {q.document_ref}
                </button>
              </div>
            ) : null}
          </div>
        ) : q.document_ref ? (
          <div className="ev-src">
            <span className="voice-system">{t('source')}</span>
            <button type="button" className="reg-srcbtn src-ref" title={t('Show the span behind this question in {ref}', { ref: q.document_ref })} onClick={() => onOpenSource(q.document_ref!)}>
              {q.document_ref}
            </button>
          </div>
        ) : null}
      </div>

      {amending && linkedRow ? (
        <div className="reg-q-resolve">
          <div className="text-xs text-ink-3 mb-4">
            {t('Writes')} <span className="src-ref">{linkedRow.ref}</span>{' '}
            {t('a new revision, confirms it, and settles this question — stamped “{stamp}”.', { stamp: callRef })}
          </div>
          <textarea
            className="form-input"
            rows={3}
            autoFocus
            value={amendText}
            onChange={(e) => setAmendText(e.target.value)}
          />
          {/* The call moved the record, so the dates pencilled against this question can move
              with it — written through each row's own PATCH, audited as itself, then the
              pencil is cleared. Unticked, they are rubbed out instead: nothing lingers. */}
          {assumptions.length ? (
            <label className="reg-q-adopt mt-8">
              <input type="checkbox" checked={adopt} onChange={(e) => setAdopt(e.target.checked)} />
              <span>
                {tp(assumptions.length, 'Adopt the {n} assumption pencilled against this question', 'Adopt the {n} assumptions pencilled against this question')}
                {' — '}
                {assumptions.map((o, i) => (
                  <span key={o.id}>
                    {i ? ' · ' : ''}
                    <span className="src-ref">{o.entity_ref ?? '—'}</span>{' '}
                    <span className="voice-figure">{fmtDate(o.value)}</span>
                  </span>
                ))}
              </span>
            </label>
          ) : null}
          <div className="flex gap-8 mt-8">
            <button
              className="btn btn-sm btn-accent"
              disabled={busy || !amendText.trim()}
              onClick={() => { onCallAmend(amendText.trim(), adopt); setAmending(false); }}
            >
              {t('Amend, then confirm')}
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setAmending(false)}>{t('Cancel')}</button>
          </div>
        </div>
      ) : resolving ? (
        <div className="reg-q-resolve">
          <textarea
            className="form-input"
            rows={2}
            autoFocus
            placeholder={t("What was agreed? e.g. 'Asbjørn confirmed 3–5 use cases, mail of 4 Aug.'")}
            value={resolutionText}
            onChange={(e) => onResolutionText(e.target.value)}
          />
          <div className="flex gap-8 mt-8">
            <button className="btn btn-sm btn-accent" disabled={busy || !resolutionText.trim()} onClick={onRecord}>
              {t('Record resolution')}
            </button>
            <button className="btn btn-sm btn-ghost" onClick={onCancelResolve}>{t('Cancel')}</button>
          </div>
        </div>
      ) : (
        <div className="reg-q-acts">
          {assumptions.length ? (
            <span className="reg-q-assume" title={assumptions.map((o) => `${o.entity_ref ?? '—'} → ${o.value}`).join(' · ')}>
              <span className="att att--proposed" aria-hidden="true" />
              <span className="tag-unheld">
                {tp(assumptions.length,
                  '{n} date is pencilled against this question — confirming or refusing rubs it out',
                  '{n} dates are pencilled against this question — confirming or refusing rubs them out')}
              </span>
            </span>
          ) : null}
          {isConflict ? (
            // The no-winner-yet path — the app's "log as open question", which is what this row
            // IS. Nothing is written; both positions travel to the client as they stand.
            <span className="reg-q-what">{t('Neither side yet — it stays on the client-call agenda, and nothing is written.')}</span>
          ) : (
            <>
              {/* The verbs fit the kind. A question that names a REQ is decided — confirm,
                  amend or refuse the row. One that names none is an ASK — the answer is
                  recorded, nothing is signed. Every path is stamped with the call reference. */}
              {linkedRow ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-accent"
                    disabled={busy}
                    title={t('{ref} stands as written — confirms it and settles the question, stamped “{stamp}”', { ref: linkedRow.ref, stamp: callRef })}
                    onClick={onCallConfirm}
                  >
                    {t('Confirmed — as it stands')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy}
                    onClick={() => { setAmendText(String(linkedRow.statement ?? '')); setAmending(true); }}
                  >
                    {t('Amend, then confirm')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy}
                    title={t('{ref} goes out of scope and the refusal is recorded, stamped “{stamp}”', { ref: linkedRow.ref, stamp: callRef })}
                    onClick={onCallRefuse}
                  >
                    {t('Refused — out of scope')}
                  </button>
                  <button type="button" className="reg-act reg-act--quiet" disabled={busy} onClick={onStartResolve} style={{ marginLeft: 'auto' }}>
                    {t('Record something else →')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-accent"
                  disabled={busy}
                  title={t('Record what was decided or done — stamped “{stamp}”', { stamp: callRef })}
                  onClick={onStartResolve}
                >
                  {t('Record the answer →')}
                </button>
              )}
            </>
          )}
          <button type="button" className="reg-act reg-act--quiet" disabled={busy} onClick={onDrop}>
            {t('Drop')}
          </button>
        </div>
      )}
    </div>
  );
}
